import { trigger, transition, style, animate } from '@angular/animations';
import { __awaiter } from 'tslib';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, Optional, TemplateRef, Component, ContentChild, Input, NgModule, Pipe } from '@angular/core';
import { Subject, of, from, BehaviorSubject, identity, EMPTY, forkJoin, Observable, combineLatest } from 'rxjs';
import { parseScript } from 'esprima';
import evaluate from 'static-eval';
import * as i8 from '@angular/common';
import { DOCUMENT, CommonModule, HashLocationStrategy, APP_BASE_HREF } from '@angular/common';
import jsonpath from 'jsonpath';
import { tap, shareReplay, first, map, catchError, filter, distinctUntilChanged, concatMap, takeUntil } from 'rxjs/operators';
import * as i3 from '@allianz/ngx-logger';
import { NgxLoggerService } from '@allianz/ngx-logger';
import deepmerge from 'deepmerge';
import * as i1 from '@angular/common/http';
import { HttpErrorResponse, HttpParams, HttpRequest, HttpEventType } from '@angular/common/http';
import equal from 'fast-deep-equal/es6';
import * as i1$1 from '@angular/router';
import { ChildActivationEnd, RouterModule } from '@angular/router';
import { HttpClientTestingModule } from '@angular/common/http/testing';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

const fadeIn = trigger('routeAnimations', [
    // fade in when created. this could also be written as transition('void => *')
    transition('* <=> *', [style({ opacity: 0 }), animate(300)]),
]);

/**
 * Enum of meta keys that can be used to do specific navigations to other sections of navigation config
 */
var META_KEYS;
(function (META_KEYS) {
    META_KEYS["FIRST_PAGE"] = "FIRST_PAGE";
})(META_KEYS || (META_KEYS = {}));
/**
 * State operation through different variables
 */
var StateOperations;
(function (StateOperations) {
    StateOperations["REMOVE"] = "remove";
    StateOperations["PUSH"] = "push";
    StateOperations["DECREASE"] = "decrease";
    StateOperations["INCREASE"] = "increase";
    /** Joins the "key" using the "value" as a join operator */
    StateOperations["JOIN"] = "join";
})(StateOperations || (StateOperations = {}));
var OPERATORS;
(function (OPERATORS) {
    OPERATORS["AND"] = "AND";
    OPERATORS["OR"] = "OR";
})(OPERATORS || (OPERATORS = {}));
/**
 * Defines the serviceActivators to be triggered while doing so manually.
 */
var ServiceActivatorHook;
(function (ServiceActivatorHook) {
    ServiceActivatorHook[ServiceActivatorHook["ENTER"] = 0] = "ENTER";
    ServiceActivatorHook[ServiceActivatorHook["LEAVE"] = 1] = "LEAVE";
})(ServiceActivatorHook || (ServiceActivatorHook = {}));

const NGX_PFE_CONFIGURATION = new InjectionToken('NGX_PFE_CONFIGURATION');
var PFE_DEFAULT_REQUEST_BODY;
(function (PFE_DEFAULT_REQUEST_BODY) {
    PFE_DEFAULT_REQUEST_BODY["EMPTY"] = "EMPTY";
    PFE_DEFAULT_REQUEST_BODY["FULL_STATE"] = "FULL_STATE";
})(PFE_DEFAULT_REQUEST_BODY || (PFE_DEFAULT_REQUEST_BODY = {}));
var NGX_PFE_CONFIG_API_FORMAT;
(function (NGX_PFE_CONFIG_API_FORMAT) {
    NGX_PFE_CONFIG_API_FORMAT[NGX_PFE_CONFIG_API_FORMAT["CONFIG_SERVICE"] = 0] = "CONFIG_SERVICE";
    NGX_PFE_CONFIG_API_FORMAT[NGX_PFE_CONFIG_API_FORMAT["STATIC"] = 1] = "STATIC";
})(NGX_PFE_CONFIG_API_FORMAT || (NGX_PFE_CONFIG_API_FORMAT = {}));

function clone(data) {
    if (data) {
        return JSON.parse(JSON.stringify(data));
    }
    else {
        return data;
    }
}

/**
 * The RegExp to find JSONPath Expressions in URL
 * Math {$.anything} or {$['anything']}
 */
const URL_STATE_REGEX = /{\$[^}]*}/gi;
const STATE_EXPRESSION = '$';
const ARRAY_STATE_EXPRESSION = '[]$';
/**
 * The following parameters can be used in the URL to control
 * certain functionalities of the application.
 *
 * @export
 */
class PfeUrlParameters {
}
/**
 * The applicationId is used by the config service to determine the configuration to load.
 * Usually, this corresponds to the branch in the configuration repository.
 */
PfeUrlParameters.APPLICATION_ID = 'applicationId';
/**
 * The tenant determines the sbp-plugin, where the gateway will forward the calls to.
 */
PfeUrlParameters.TENANT = 'tenant';
/**
 * Restore a previously saved state with a specific ID.
 * Usually, the state service will handle this: https://github.developer.allianz.io/gcj/state-server
 */
PfeUrlParameters.STATE_ID = 'stateId';
/**
 * This parameter allows it, to set values in the state from URL parameters.
 *
 * For example:
 *
 * The URL parameter
 *
 * https://example.com/?pfe-somekey=somevalue
 *
 * will be written to the state like this:
 *
 * key: somekey
 *
 * value: somevalue
 *
 * That way, it is possible to use some data from the URL in a serviceActivator Call.
 */
PfeUrlParameters.PFE_STATE_URL_PARAM = 'pfe-';
/**
 * The older, legacy parameter. As this parameter is in use in various applications/links it is
 * marked as deprecated, but realistically won't be removed for a long time...
 *  @deprecated true
 * @deprecationMessage Use the PFE_STATE_URL_PARAM instead
 */
PfeUrlParameters.PFE_STATE_URL_PARAM_LEGACY = 'pfestate-';
class PfeUtilService {
    constructor(ngxPfeModuleConfiguration, 
    // @Inject(WINDOW_INJECTION) private window: any
    _document, ngxLoggerService) {
        this.ngxPfeModuleConfiguration = ngxPfeModuleConfiguration;
        this._document = _document;
        this.ngxLoggerService = ngxLoggerService;
        /**
         * This subject is used as a bridge to the tracking integration in @allianz/ngx-pfe/tracking.
         * Events that are triggered here, are forwarded by the PfeTrackingService.
         *
         * As the @allianz/ngx-pfe/tracking integration is optional, a custom integration can also attach to this.
         * (This might be moved to it's own service, if more events are triggered)
         */
        this.trackingEvents$ = new Subject();
        this.trackingStateRestoredSubject$ = new Subject();
        /**
         * Triggers when a state is restored, with the restored state as value.
         */
        this.trackingStateRestored$ = this.trackingStateRestoredSubject$.asObservable().pipe(tap((value) => clone(value)));
        this.readApplicationParameters();
    }
    /**
     * @see getValueOrList
     * @deprecated instead use `getValueOrList` and set the optional third parameter ("returnUndefinedForEmptyArray") to true
     */
    static getValueOrListFromJsonExpression(obj, pathExpression) {
        return PfeUtilService.getValueOrList(obj, pathExpression, true);
    }
    /**
     * Retrieves elements in `obj` that match the jsonpath expression `pathExpression`.
     *
     * Returns the element value if only a single element is found at the path, otherwise
     * a list of matching elements is returned.
     * If you want to receive the result as an array, even if it contains only a single element
     * prepend your pathExpression with '[]' (e.g. '[]$.arrayWithOneElement[0:]')
     *
     * If you want to receive `undefined` in case the result is an empty array, set the optional
     * third parameter to true.
     *
     */
    static getValueOrList(obj, pathExpression, returnUndefinedForEmptyArray = false) {
        let shouldReturnArray = false;
        if (PfeUtilService.isJsonArrayPathExpression(pathExpression)) {
            shouldReturnArray = true;
            pathExpression = pathExpression.replace(ARRAY_STATE_EXPRESSION, STATE_EXPRESSION);
        }
        const notAJsonPathExpression = !PfeUtilService.isJsonPathExpression(pathExpression);
        if (notAJsonPathExpression) {
            return pathExpression;
        }
        const result = jsonpath.query(obj, pathExpression);
        if (result.length === 0) {
            return undefined;
        }
        if (result.length > 1) {
            return result;
        }
        const onlyResult = result[0];
        const resultIsEmptyArray = Array.isArray(onlyResult) && onlyResult.length === 0;
        if (resultIsEmptyArray && returnUndefinedForEmptyArray) {
            return undefined;
        }
        return shouldReturnArray ? result : onlyResult;
    }
    static setValueFromJsonExpression(obj, pathExpression, value) {
        if (PfeUtilService.isJsonPathExpression(pathExpression)) {
            jsonpath.value(obj, pathExpression, value);
        }
    }
    /**
     * Check if the pathExpression is a JsonPathExpression
     * Some other services send values that are not really path expressions (like `false` or `true` values)
     * Or some strings that don't start with "$"
     * @param maybeJsonPathExpression Maybe a JsonPathExpression
     * @return true if is a JsonPathExpression
     */
    static isJsonPathExpression(maybeJsonPathExpression) {
        return maybeJsonPathExpression && typeof maybeJsonPathExpression == 'string' && maybeJsonPathExpression.startsWith(STATE_EXPRESSION);
    }
    /**
     * Check if the pathExpression is a JsonArrayPathExpression, i.e. starts with '[]$'
     * @param maybeJsonArrayPathExpression Maybe a JsonArrayPathExpression
     * @return true if is a JsonArrayPathExpression
     */
    static isJsonArrayPathExpression(maybeJsonArrayPathExpression) {
        return (maybeJsonArrayPathExpression &&
            typeof maybeJsonArrayPathExpression == 'string' &&
            maybeJsonArrayPathExpression.startsWith(ARRAY_STATE_EXPRESSION));
    }
    readApplicationParameters() {
        this._applicationId = this.getURLParam(PfeUrlParameters.APPLICATION_ID);
        this._tenant = this.getURLParam(PfeUrlParameters.TENANT);
        if (!this._applicationId) {
            this._applicationId = this.ngxPfeModuleConfiguration.applicationID;
        }
        if (!this._tenant) {
            this._tenant = this.ngxPfeModuleConfiguration.tenant;
        }
        if (this._applicationId && this._applicationId !== '') {
            this.ngxLoggerService.debug('Using application: ' + this._applicationId);
        }
        if (this._tenant && this._tenant !== '') {
            this.ngxLoggerService.debug('Using tenant: ' + this._tenant);
        }
    }
    get applicationId() {
        if (!this._applicationId) {
            this.readApplicationParameters();
        }
        return this._applicationId;
    }
    get tenant() {
        if (!this._tenant) {
            this.readApplicationParameters();
        }
        return this._tenant;
    }
    /**
     * Returns a URL parameter, that is either set in the location.search
     * or location.hash.
     */
    getURLParam(paramName) {
        let paramResult = this.getUrlParams(this._document.location.search, '?')[paramName];
        if (!paramResult) {
            paramResult = this.getUrlParams(this._document.location.hash, '#')[paramName];
        }
        return paramResult;
    }
    getUrlParams(search, separator) {
        if (!search) {
            return {};
        }
        const hashes = search.slice(search.indexOf(separator) + 1).split('&');
        return hashes.reduce((params, hash) => {
            const [key, val] = hash.split('=');
            return Object.assign(params, { [key]: decodeURIComponent(val) });
        }, {});
    }
}
PfeUtilService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeUtilService, deps: [{ token: NGX_PFE_CONFIGURATION }, { token: DOCUMENT }, { token: i3.NgxLoggerService }], target: i0.ɵɵFactoryTarget.Injectable });
PfeUtilService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeUtilService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeUtilService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [NGX_PFE_CONFIGURATION]
                    }] }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [DOCUMENT]
                    }] }, { type: i3.NgxLoggerService }];
    } });

function isObject(value) {
    return value !== null && typeof value === 'object';
}
function isString(value) {
    return typeof value === 'string';
}

/**
 * This PfeRulesEvaluator is used to evaluate the rules used in the navigation
 * configuration.
 *
 * @export
 */
class PfeRulesEvaluator {
    /**
     * Evaluates all the conditions of a navigational rule.
     * Conditions are connected by AND.
     *
     * @param conditions The conditions to check.
     * @param state The current state.
     * @returns true if all conditions are true, false if at least one is false.
     */
    static evaluateConditions(conditions, state) {
        if (Array.isArray(conditions)) {
            let result = false;
            if (conditions) {
                for (const condition of conditions) {
                    result = PfeRulesEvaluator.evaluateCondition(condition, state);
                    if (result === false) {
                        return result;
                    }
                }
            }
            return result;
        }
        return PfeRulesEvaluator.evaluateAdvancedConditions(conditions, state);
    }
    /**
     * Evaluates all the conditions of a navigational rule.
     * Conditions are connected by OR.
     *
     * @param conditions The conditions to check.
     * @param state The current state.
     * @returns true if all conditions are true, false if at least one is false.
     */
    static evaluateORConditions(conditions, state) {
        let result = false;
        if (conditions) {
            for (const condition of conditions) {
                result = PfeRulesEvaluator.evaluateCondition(condition, state);
                if (result === true) {
                    return result;
                }
            }
        }
        return result;
    }
    /**
     * Evaluate Advanced Conditions
     *
     * @param conditionAdvanced object that describe the operator and conditions
     * @param state The current state.
     * @returns true if all conditions are true, false if at least one is false.
     */
    static evaluateAdvancedConditions(conditionAdvanced, state) {
        return conditionAdvanced.operator === OPERATORS.AND
            ? PfeRulesEvaluator.evaluateConditions(conditionAdvanced.conditions, state)
            : PfeRulesEvaluator.evaluateORConditions(conditionAdvanced.conditions, state);
    }
    /**
     * Evaluates a condition.
     * Possible comparator are:
     * =, !=, >, <
     * A condition always consists of three parts. For example:
     * a = b
     * Conditions can also contain strings with whitespace:
     * a = 'test with space'
     *
     * @param condition The condition to evaluate.
     * @param state The current state.
     * @returns boolean that shows, if the condition was true.
     */
    static evaluateCondition(condition, state) {
        try {
            let evalResult = false;
            if (condition.expression !== undefined) {
                const expression = condition.expression;
                if (isString(expression)) {
                    // used for iterating over all capture groups with content {.*}
                    const regex = /({.*?})/gm;
                    const replacer = (match) => {
                        // {$.aTestKey} -> $.aTestKey
                        const jsonPath = match.replace(/{|}/gm, '');
                        const value = PfeUtilService.getValueOrListFromJsonExpression(state, jsonPath);
                        if (isString(value)) {
                            // aTestValue -> "aTestValue"
                            return '"' + value + '"';
                        }
                        else if (isObject(value) && !Array.isArray(value)) {
                            // can't serialize and evaluate an object
                            return '"' + Math.random() + '"';
                        }
                        else if (Array.isArray(value)) {
                            // Return the Array as a string
                            return JSON.stringify(value);
                        }
                        return value;
                    };
                    let statement = expression.replace(regex, replacer);
                    // esprima can not handle undefined.
                    // So undefined == undefined evaluates to false and even !undefined evaluates to false
                    // To prevent this we convert any undefined to null.
                    statement = statement.replace(/undefined/gm, 'null');
                    const ast = parseScript(statement).body[0].expression;
                    evalResult = !!evaluate(ast, {});
                }
                else {
                    evalResult = !!expression;
                }
            }
            if (condition.value1Expression !== undefined) {
                const value1 = condition.value1Expression;
                let expression1Result = value1;
                // If the first expression is not a string, there it not have get the value
                if (isString(value1)) {
                    const jpe1 = value1;
                    expression1Result = PfeUtilService.getValueOrListFromJsonExpression(state, jpe1);
                    // Historically, if there is only one expression the result is not threated as a string
                    // Is threated as a real boolean
                    if (!condition.operator && condition.value2Expression === undefined && expression1Result) {
                        return true;
                    }
                }
                if (condition.operator && condition.value2Expression !== undefined) {
                    const value2 = condition.value2Expression;
                    let expression2Result = value2;
                    if (isString(value2)) {
                        const jpe2 = value2;
                        expression2Result = PfeUtilService.getValueOrListFromJsonExpression(state, jpe2);
                    }
                    // If the "value1AsReal" is not true, then the value is treated as string, that is because we add the
                    // '' to the expresion rseult
                    expression1Result = condition.value1AsReal ? expression1Result : `'${expression1Result}'`;
                    expression2Result = condition.value2AsReal ? expression2Result : `'${expression2Result}'`;
                    // We compare the two expressions
                    const expression = `${expression1Result} ${condition.operator} ${expression2Result}`;
                    const ast = parseScript(expression).body[0].expression;
                    evalResult = evaluate(ast, {});
                }
            }
            return evalResult;
        }
        catch (error) {
            console.error('Error evaluating rule', condition, error);
            return false;
        }
    }
    /**
     * Retrieve the state property names of the first part of the conditions.
     * propertyName in case of $state.propertyName or value unchanged
     *
     * @param conditions The conditions to parse.
     * @returns The state property names of the condition's first part.
     */
    static getStatePropertyName(conditions) {
        const propertyNames = [];
        const FIRST_KEY_REGEX = /\$\.(.*?)(?:\.|$)/g;
        if (conditions) {
            for (const condition of conditions) {
                const value1 = condition.value1Expression;
                const match = FIRST_KEY_REGEX.exec(value1);
                if (match && match.length > 0) {
                    propertyNames.push(match[1]);
                }
            }
        }
        return propertyNames;
    }
}

const VALIDATION_ERROR_STATE_KEY_FALLBACK = 'validationErrorData';
const NGX_PFE_FLOW_CONFIGURATION = new InjectionToken('NGX_PFE_FLOW_CONFIGURATION - supply the flow configuration as a token');
/**
 * Service that provides the pfe configuration.
 *
 * We need to tell the Angular compiler that the NGX_PFE_FLOW_CONFIGURATION generic Promise token is ok:
 * @dynamic
 * It doesn't like the generic there, as that could cause issues during runtime (Check runs due to strictMetadataEmit).
 * This will not be required anymore, once the library builds are also switched to ivy.
 *
 * @export
 * @abstract
 */
class PfeConfigurationService {
    constructor(ngxPfeModuleConfiguration, http, pfeUtilService, logger, flowConfigToken) {
        this.ngxPfeModuleConfiguration = ngxPfeModuleConfiguration;
        this.http = http;
        this.pfeUtilService = pfeUtilService;
        this.logger = logger;
        this.flowConfigToken = flowConfigToken;
        this.config$ = this.loadConfig().pipe(shareReplay(1));
        /**
         * Assign the AppConfiguration Object for quick and easy access
         */
        this.pfeApplicationConfiguration = undefined;
        this.overwriteMerge = (destinationArray, sourceArray, _) => sourceArray;
    }
    /**
     * Get the config
     */
    getConfig() {
        return this.config$.pipe(first()).toPromise();
    }
    getPageConfiguration(pageID) {
        return this.config$
            .pipe(map((config) => { var _a; return (_a = config === null || config === void 0 ? void 0 : config.pagesConfiguration) === null || _a === void 0 ? void 0 : _a.find((page) => page.pageId === pageID); }), catchError(() => of(undefined)), first())
            .toPromise();
    }
    getPageNavigationConfiguration(pageID) {
        if (!pageID) {
            return Promise.resolve(undefined);
        }
        return this.config$
            .pipe(map((config) => { var _a, _b; return (_b = (_a = config === null || config === void 0 ? void 0 : config.navConfiguration) === null || _a === void 0 ? void 0 : _a.pages) === null || _b === void 0 ? void 0 : _b.find((page) => page.pageId === pageID); }), catchError(() => of(undefined)), first())
            .toPromise();
    }
    isAppConfigurationAvailable() {
        return this.config$
            .pipe(map((config) => !!config && (config === null || config === void 0 ? void 0 : config.appConfiguration) !== null && (config === null || config === void 0 ? void 0 : config.appConfiguration) !== undefined), catchError(() => of(false)), first())
            .toPromise();
    }
    getAppConfiguration() {
        return this.config$
            .pipe(map((config) => config.appConfiguration), catchError(() => of(undefined)), first())
            .toPromise();
    }
    getErrorMessageKey() {
        return this.config$
            .pipe(map((config) => { var _a, _b, _c; return (_c = (_b = (_a = config === null || config === void 0 ? void 0 : config.appConfiguration) === null || _a === void 0 ? void 0 : _a.pfeConfig) === null || _b === void 0 ? void 0 : _b.validationErrorStateKey) !== null && _c !== void 0 ? _c : VALIDATION_ERROR_STATE_KEY_FALLBACK; }), catchError(() => of(VALIDATION_ERROR_STATE_KEY_FALLBACK)), first())
            .toPromise();
    }
    /**
     * It is possible, to reference other pageConfigs in a navigation config.
     * This method will copy the referenced configs and do a deep merge if there is
     * also a navigation pageConfig.
     */
    addReferencedPageConfigs(config) {
        if (config && config.navConfiguration && config.navConfiguration.pages && config.pagesConfiguration) {
            config.navConfiguration.pages.forEach((navConfig) => {
                if (navConfig.pageConfigIdReference) {
                    // If there is also a config for the pageId, a merge will be done
                    const navConfigPageConfig = config.pagesConfiguration.find((page) => page.pageId === navConfig.pageId);
                    // Get the pageConfig that is referenced here. It is copied.
                    const referencedPageConfig = config.pagesConfiguration.find((page) => page.pageId === navConfig.pageConfigIdReference);
                    if (!referencedPageConfig) {
                        this.logger.warnToServer(`No config for pageConfigIdReference: ${navConfig.pageConfigIdReference}`);
                        return;
                    }
                    const referencedPageConfigCopy = clone(referencedPageConfig);
                    if (!referencedPageConfigCopy) {
                        return;
                    }
                    referencedPageConfigCopy.pageId = navConfig.pageId;
                    if (navConfigPageConfig) {
                        // Merge the navConfig pageConfig on top of the reference config and replace the navConfig pageConfig:
                        const index = config.pagesConfiguration.indexOf(navConfigPageConfig);
                        config.pagesConfiguration[index] = deepmerge(referencedPageConfigCopy, navConfigPageConfig, navConfig.pageConfigIdReferenceDeepMergeArrays ? {} : { arrayMerge: this.overwriteMerge });
                    }
                    else {
                        // Add the copy of the reference pageConfig:
                        config.pagesConfiguration.push(referencedPageConfigCopy);
                    }
                }
            });
        }
        // store the pfeConfig Object
        if (config.appConfiguration) {
            this.pfeApplicationConfiguration = config.appConfiguration;
        }
        return config;
    }
    loadConfig() {
        let config$;
        if (this.flowConfigToken) {
            // The flowConfigToken can be a Promise, but can also be a value.
            // This neat little trick turns values into a promise and if it is already a promise, it keeps the Promise
            // (As in, it is not resolved immediately, but only when the original promise is resolved.
            // The alternative to this would be a type guard and to handle the types differently)
            const promisifiedValue = Promise.resolve(this.flowConfigToken);
            config$ = from(promisifiedValue);
        }
        else {
            config$ = this.loadConfigFromBackend();
        }
        return config$.pipe(map((config) => this.addReferencedPageConfigs(config)));
    }
    loadConfigFromBackend() {
        const tenant = this.pfeUtilService.tenant;
        const url = this.ngxPfeModuleConfiguration.config_api_endpoint +
            (tenant ? `/${tenant}` : '') +
            `/${this.pfeUtilService.applicationId}` +
            (this.ngxPfeModuleConfiguration.config_api_format === NGX_PFE_CONFIG_API_FORMAT.STATIC ? '/index.json' : '');
        return this.http.get(url);
    }
}
PfeConfigurationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeConfigurationService, deps: [{ token: NGX_PFE_CONFIGURATION }, { token: i1.HttpClient }, { token: PfeUtilService }, { token: i3.NgxLoggerService }, { token: NGX_PFE_FLOW_CONFIGURATION, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
PfeConfigurationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeConfigurationService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeConfigurationService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [NGX_PFE_CONFIGURATION]
                    }] }, { type: i1.HttpClient }, { type: PfeUtilService }, { type: i3.NgxLoggerService }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [NGX_PFE_FLOW_CONFIGURATION]
                    }] }];
    } });

const PFE_HISTORY_KEY = 'pfeHistory';
const PFE_VISITED_KEY = 'pfeVisitedPages';
/**
 * This key in the state is reserved for all internal pfe data.
 * In general, it is desirable to also migrate existing previous keys over to this.
 * For example pfeHistory, etc...
 * But as those are used in a lot of places, that is a bigger, breaking, effort
 */
const PFE_STATE_INTERNAL_STATE_KEY = '_pfe';
const PFE_STATE_NAVIGATING_FROM = `$.${PFE_STATE_INTERNAL_STATE_KEY}.navigationState.navigatingFrom`;
const PFE_STATE_NAVIGATING_TO = `$.${PFE_STATE_INTERNAL_STATE_KEY}.navigationState.navigatingTo`;

/**
 * Injection token to configure the key in which the state will be stored in the session storage from each app
 */
const NGX_PFE_STATE_SESSION_STORAGE_KEY_CONFIG = new InjectionToken('NGX_PFE_STATE_KEY_CONFIG');
/**
 * Optional injection token that can be used to restore the state during startup
 */
const NGX_PFE_INITIAL_STATE = new InjectionToken('NGX_PFE_INITIAL_STATE');

/**
 * The PfeRestService is the central service for all backend communication.
 * All backend calls should be issued only from this service.
 *
 * @export
 */
class PfeRestService {
    constructor(ngxPfeModuleConfiguration, http) {
        this.ngxPfeModuleConfiguration = ngxPfeModuleConfiguration;
        this.http = http;
    }
    /**
     * Stores the client state in the backend.
     *
     * @param stateID The ID that is used by the backend as a key.
     * @param state The current state.
     * @returns Observable to react to the result of the call.
     */
    createClientState(state) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.ngxPfeModuleConfiguration.state_service_endpoint) {
                return Promise.reject(undefined);
            }
            return this.http.post(this.ngxPfeModuleConfiguration.state_service_endpoint, state).toPromise();
        });
    }
    /**
     * Stores the client state in the backend.
     *
     * @param stateID The ID that is used by the backend as a key.
     * @param state The current state.
     * @returns Observable to react to the result of the call.
     */
    updateClientState(stateID, state) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.ngxPfeModuleConfiguration.state_service_endpoint) {
                return Promise.reject(undefined);
            }
            return this.http.put(this.ngxPfeModuleConfiguration.state_service_endpoint + `/${stateID}`, state).toPromise();
        });
    }
    /**
     * Retrieves a client state from the backend.
     *
     * @param stateID The ID of the state.
     * @returns Observable to react to the result of the call.
     */
    getClientState(stateID) {
        return this.http.get(this.ngxPfeModuleConfiguration.state_service_endpoint + `/${stateID}`);
    }
}
PfeRestService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeRestService, deps: [{ token: NGX_PFE_CONFIGURATION }, { token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
PfeRestService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeRestService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeRestService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [NGX_PFE_CONFIGURATION]
                    }] }, { type: i1.HttpClient }];
    } });

class Model {
    constructor(initialData, immutable, sharedSubscription, clone) {
        this.immutable = immutable;
        this.clone = clone;
        this._data = new BehaviorSubject(initialData);
        this.data$ = this._data.asObservable().pipe(map((data) => (this.immutable ? (clone ? clone(data) : JSON.parse(JSON.stringify(data))) : data)), sharedSubscription ? shareReplay({ bufferSize: 1, refCount: true }) : map((data) => data));
    }
    get() {
        const data = this._data.getValue();
        return this.immutable ? (this.clone ? this.clone(data) : JSON.parse(JSON.stringify(data))) : data;
    }
    set(data) {
        if (this.immutable) {
            const clone = this.clone ? this.clone(data) : JSON.parse(JSON.stringify(data));
            this._data.next(clone);
        }
        else {
            this._data.next(data);
        }
    }
}
class ModelFactory {
    create(initialData) {
        return new Model(initialData, true, false);
    }
    createMutable(initialData) {
        return new Model(initialData, false, false);
    }
    createMutableWithSharedSubscription(initialData) {
        return new Model(initialData, false, true);
    }
    createWithCustomClone(initialData, clone) {
        return new Model(initialData, true, false, clone);
    }
    createWithConfig(config) {
        const { initialData, immutable, sharedSubscription, clone } = config;
        return new Model(initialData, immutable, sharedSubscription, clone);
    }
}
ModelFactory.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: ModelFactory, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ModelFactory.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: ModelFactory, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: ModelFactory, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/**
 * The PfeStateService is the central location to hold the app state.
 *
 * We need to tell the Angular compiler that the NGX_PFE_INITIAL_STATE token with a Promise is ok:
 * @dynamic
 * It doesn't like the Promise there, as that could cause issues during runtime (Check runs due to strictMetadataEmit).
 * This will not be required anymore, once the library builds are also switched to ivy.
 *
 * @export
 */
class PfeStateService {
    constructor(ngxPfeModuleConfiguration, ngxPfeStateKeyConfig, restoreStateToken, pfeRestService, pfeAppStateFactory, pfeUtilService, appRef, pfeConfigService, _document, logger) {
        this.ngxPfeModuleConfiguration = ngxPfeModuleConfiguration;
        this.ngxPfeStateKeyConfig = ngxPfeStateKeyConfig;
        this.restoreStateToken = restoreStateToken;
        this.pfeRestService = pfeRestService;
        this.pfeAppStateFactory = pfeAppStateFactory;
        this.pfeUtilService = pfeUtilService;
        this.appRef = appRef;
        this.pfeConfigService = pfeConfigService;
        this._document = _document;
        this.logger = logger;
        /**
         * staleState
         */
        // Keeping the previous naming for now, as it would be potentially breaking (Even with the _)
        // eslint-disable-next-line @typescript-eslint/naming-convention
        this._staleState = {
            pages: {},
            currentPageId: '',
            removePageFromState: [],
        };
        this.stateKey = 'state';
        // Replace with a nicer solution, once
        // https://github.com/Microsoft/TypeScript/issues/10853 and
        // https://github.com/Microsoft/TypeScript/issues/11304
        // are resolved.
        const pfeUserInputState = {
            [PFE_STATE_INTERNAL_STATE_KEY]: {},
            staleState: this._staleState,
        };
        /**
         * The pfeUserInputState is created without the immutable behavior here.
         * That means, this needs to be done manually in every location where the pfeUserInputState or pfeUserInputState$
         * is returned to the outside world. Wether it be in full or only as a slice from within the data.
         *
         * The background for this is, that the immutable version of the Model always copies the whole state
         * when it is accessed. This results in a worsening performance the bigger the data in the state is.
         *
         * Copying the whole state is not necessary, when only a slice of data is accessed. For example via the getObservableFor* methods.
         * I those cases is is enough to only copy the slice of data that is actually returned.
         *
         * For this reason, the state itself is created as mutable and only the returned data to outside this service is
         * copied.
         *
         * Additionally, the performance when updating the state via the store* methods is improved as the state is now only copied once instead of twice.
         */
        this.pfeUserInputState = this.pfeAppStateFactory.createMutable(pfeUserInputState);
        this.pfeUserInputStateInternal$ = this.pfeUserInputState.data$;
        this.pfeUserInputState$ = this.pfeUserInputStateInternal$.pipe(map((data) => clone(data)));
        const stateIDFomURL = this.pfeUtilService.getURLParam(PfeUrlParameters.STATE_ID);
        this.stateID = stateIDFomURL;
        this.readURLStateParameters();
        this.setupHotkeys();
        this.setUpStateKey();
    }
    set stateID(stateID) {
        if (!stateID) {
            return;
        }
        this._stateID = stateID;
        (() => __awaiter(this, void 0, void 0, function* () {
            // FIXME: Fix app config loading in state service:
            // const appConfiguration: AppConfiguration = await this.pfeConfigurationService.getAppConfiguration();
            // if (appConfiguration) {
            //   const stateIDStateKey = appConfiguration.stateIDStateKey;
            //   if (stateIDStateKey) {
            const stateIDStateKey = 'pfeStateID';
            this.storeValue(stateIDStateKey, this._stateID);
            // }
            // }
        }))();
    }
    /**
     * Store a value under the key in the state. It can be retrieved with {@link getUserInput}
     * Existing values with the same key, are overwritten.
     *
     * @param key The key has to be a string value.
     * @param value The value can be of any type. Typecasting needs to be handled by the retrieving component.
     */
    storeValue(key, value) {
        this.addKeyToStale(key);
        const pfeAppState = this.pfeUserInputState.get();
        pfeAppState[key] = value;
        this.pfeUserInputState.set(clone(pfeAppState));
    }
    /**
     * Store a value under the expression in the state. It can be retrieved with {@link getUserInput}
     * Existing values with the same key, are overwritten.
     *
     * @param expression The expression has to be a valid JsonPathExpression.
     * @param value The value can be of any type. Typecasting needs to be handled by the retrieving component.
     */
    storeValueByExpression(expression, value) {
        const pfeAppState = this.pfeUserInputState.get();
        PfeUtilService.setValueFromJsonExpression(pfeAppState, expression, value);
        this.pfeUserInputState.set(clone(pfeAppState));
        // store the userInput state
        this.addKeyToStale(expression);
    }
    /**
     * Returns previously stored data by its key.
     *
     * @param key The key in the state
     */
    getValue(key) {
        const pfeAppState = this.pfeUserInputState.get();
        return clone(pfeAppState[key]);
    }
    getStateValueByExpression(jsonPathExpression) {
        const pfeAppState = this.pfeUserInputState.get();
        return clone(PfeUtilService.getValueOrList(pfeAppState, jsonPathExpression));
    }
    storeStateOnBackend() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.ngxPfeModuleConfiguration.state_service_endpoint) {
                return;
            }
            const currentState = clone(this.pfeUserInputState.get());
            const stateSize = Object.keys(currentState).length;
            if (stateSize > 0) {
                if (!this._stateID) {
                    yield this.createStateAndGetStateID(currentState);
                }
                else {
                    yield this.updateState(this._stateID, currentState);
                }
            }
        });
    }
    createStateAndGetStateID(currentState) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const generatedStateID = (yield this.pfeRestService.createClientState(currentState)).id;
                this.stateID = encodeURIComponent(generatedStateID);
            }
            catch (error) {
                // TODO: Error handling:
                this.logger.errorToServer('createStateAndGetStateID', error);
            }
        });
    }
    updateState(stateID, currentState) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.pfeRestService.updateClientState(stateID, currentState);
            }
            catch (error) {
                // TODO: Error handling:
                this.logger.errorToServer('updateState', error);
            }
        });
    }
    restoreState(state, resolve) {
        this.pfeUserInputState.set(clone(state));
        this.populateBrowserHistory(state);
        this.readURLStateParameters();
        this.appRef.tick();
        if (resolve) {
            resolve(state);
        }
        this.pfeUtilService.trackingStateRestoredSubject$.next(state);
    }
    restoreStateFromBackend() {
        if (!this.ngxPfeModuleConfiguration.state_service_endpoint || !this._stateID) {
            return Promise.reject(undefined);
        }
        return new Promise((resolve, reject) => {
            if (!this._stateID) {
                reject();
            }
            else {
                this.pfeRestService.getClientState(this._stateID).subscribe((data) => {
                    const state = Object.assign({}, data);
                    const stateSize = Object.keys(state).length;
                    if (stateSize > 0) {
                        this.restoreState(state, resolve);
                    }
                    else if (resolve) {
                        resolve(state);
                    }
                }, (error) => {
                    // TODO: Error handling
                    this.logger.errorToServer('restoreStateFromBackend', error);
                    reject();
                });
            }
        });
    }
    restoreStateFromToken(tokenPromise) {
        return tokenPromise.then((state) => {
            const validState = isObject(state) ? state : this.pfeUserInputState.get();
            return new Promise((resolve) => this.restoreState(validState, resolve));
        });
    }
    /**
     * Returns a copy of the current PfeAppState.
     * To change data within the state, the StateService methods need to be used.
     */
    getFullState() {
        return clone(this.pfeUserInputState.get());
    }
    setFullState(pfeUserInputState) {
        // TODO: track stale state key when using setFullState function
        this.pfeUserInputState.set(clone(pfeUserInputState));
    }
    resetState() {
        return new Promise((resolve) => this.restoreState({}, resolve));
    }
    /**
     * Returns an observable for a specific key in the state.
     * Use subscribe to react to changes.
     * The observable gets only triggered, if the actual value of the key changes.
     * If it is set again with the same value, the observable won't trigger.
     *
     * Do not forget to unsubscribe, once the subscription is not needed anymore.
     *
     * @param key The key of the value in the state.
     * @param triggerForUndefined Trigger the observable, also when the value is undefined.
     * @param disableDeepEqual By default the observable only triggers for a deepEqual change. This behavior can be disabled. Default: false
     */
    getObservableForKey(key, triggerForUndefined, disableDeepEqual) {
        return this.getObservableForAccessor((entry) => entry[key], triggerForUndefined, disableDeepEqual);
    }
    /**
     * Returns an observable for am expression in the state.
     * Use subscribe to react to changes.
     * The observable gets only triggered, if the actual value of the expression changes.
     * If it is set again with the same value, the observable won't trigger.
     *
     * Do not forget to unsubscribe, once the subscription is not needed anymore.
     *
     * @param expression JsonPathExpression to get the value in the state.
     * @param triggerForUndefined Trigger the observable, also when the value is undefined. Default: false
     * @param disableDeepEqual By default the observable only triggers for a deepEqual change. This behavior can be disabled. Default: false
     */
    getObservableForExpressionKey(expression, triggerForUndefined, disableDeepEqual) {
        return this.getObservableForAccessor((entry) => PfeUtilService.getValueOrList(entry, expression), triggerForUndefined, disableDeepEqual);
    }
    /**
     * Called by the public getObservableForExpressionKey and getObservableForKey
     * to get the same pipe with different accessors.
     */
    getObservableForAccessor(accessor, triggerForUndefined, disableDeepEqual) {
        return this.pfeUserInputStateInternal$.pipe(map(accessor), filter((entry) => {
            if (triggerForUndefined) {
                return true;
            }
            else {
                return entry != null;
            }
        }), map((data) => clone(data)), !disableDeepEqual ? distinctUntilChanged((prev, next) => equal(prev, next)) : identity);
    }
    readURLStateParameters() {
        const urlParams = this.pfeUtilService.getUrlParams(this._document.location.search, '?');
        this.copyValuesToState(urlParams);
        const hashParams = this.pfeUtilService.getUrlParams(this._document.location.hash, '#');
        this.copyValuesToState(hashParams);
    }
    copyValuesToState(parameters) {
        for (const key in parameters) {
            if (Object.prototype.hasOwnProperty.call(parameters, key)) {
                const value = parameters[key];
                this.savePFEStateParamsToState(key, value);
                this.saveUrlParamsToState(key, value);
            }
        }
    }
    savePFEStateParamsToState(key, value) {
        if (key.toLowerCase().startsWith(PfeUrlParameters.PFE_STATE_URL_PARAM_LEGACY)) {
            const actualKey = key.slice(PfeUrlParameters.PFE_STATE_URL_PARAM_LEGACY.length);
            this.storeValue(actualKey, value);
        }
        if (key.toLowerCase().startsWith(PfeUrlParameters.PFE_STATE_URL_PARAM)) {
            const actualKey = key.slice(PfeUrlParameters.PFE_STATE_URL_PARAM.length);
            this.storeValue(actualKey, value);
        }
    }
    saveUrlParamsToState(key, value) {
        this.pfeConfigService.getAppConfiguration().then((appConfiguration) => {
            var _a;
            if (((_a = appConfiguration === null || appConfiguration === void 0 ? void 0 : appConfiguration.pfeConfig) === null || _a === void 0 ? void 0 : _a.urlParametersInState) && appConfiguration.pfeConfig.urlParametersInState.length > 0) {
                appConfiguration.pfeConfig.urlParametersInState.forEach((urlParametersInState) => {
                    if (urlParametersInState.key === key) {
                        this.storeValueByExpression(urlParametersInState.stateKeyExpression, value);
                    }
                });
            }
        });
    }
    populateBrowserHistory(state) {
        // TODO: Implement me
    }
    setupHotkeys() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.ngxPfeModuleConfiguration.disableStateDebuggingHotkey) {
                this._document.addEventListener('keydown', (event) => {
                    if ((event.altKey || event.metaKey) && event.ctrlKey && event.key === 'l') {
                        const state = this.getFullState();
                        console.log(state);
                        return false; // Prevent bubbling
                    }
                });
            }
        });
    }
    setUpStateKey() {
        var _a;
        if ((_a = this.ngxPfeStateKeyConfig) === null || _a === void 0 ? void 0 : _a.keySuffix) {
            this.stateKey = `state_${this.ngxPfeStateKeyConfig.keySuffix}`;
        }
    }
    restoreStateFromSessionStorage() {
        const state = sessionStorage.getItem(this.stateKey);
        if (state) {
            return new Promise((resolve) => this.restoreState(JSON.parse(state), resolve));
        }
        else {
            return Promise.reject();
        }
    }
    storeStateOnSessionStorage() {
        const currentState = this.pfeUserInputState.get();
        const stateSize = Object.keys(currentState).length;
        if (stateSize > 0) {
            sessionStorage.setItem(this.stateKey, JSON.stringify(currentState));
        }
    }
    removeStateOnSessionStorage(stateKey) {
        sessionStorage.removeItem(stateKey);
    }
    retrieveStateFromSessionStorage() {
        return this.isSessionStorageEnabled().then((value) => {
            if (value) {
                return this.restoreStateFromSessionStorage().catch(() => { });
            }
        });
    }
    storeStateInSessionStorage() {
        return this.isSessionStorageEnabled().then((value) => {
            if (value) {
                return this.storeStateOnSessionStorage();
            }
        });
    }
    removeStateInSessionStorage(stateKey = this.stateKey) {
        return this.isSessionStorageEnabled().then((value) => {
            if (value) {
                return this.removeStateOnSessionStorage(stateKey);
            }
        });
    }
    isSessionStorageEnabled() {
        return this.pfeConfigService.getConfig().then((config) => { var _a, _b; return !!((_b = (_a = config === null || config === void 0 ? void 0 : config.appConfiguration) === null || _a === void 0 ? void 0 : _a.pfeConfig) === null || _b === void 0 ? void 0 : _b.enableSessionStorageState); });
    }
    /**
     * Tries to restore a state from these sources:
     *
     * 1. NGX_PFE_RESTORED_STATE injection token
     * 2. Backend
     * 3. SessionStorage
     *
     * Errors from the NGX_PFE_RESTORED_STATE injection token are forwarded.
     * Errors from the other sources are swallowed.
     */
    automaticallyRestoreState() {
        if (this.restoreStateToken) {
            return this.restoreStateFromToken(this.restoreStateToken);
        }
        else {
            return this.restoreStateFromBackend()
                .then((data) => {
                const state = Object.assign({}, data);
                const stateSize = Object.keys(state).length;
                if (!(stateSize > 0)) {
                    return this.retrieveStateFromSessionStorage();
                }
            })
                .catch(() => this.retrieveStateFromSessionStorage());
        }
    }
    /**
     * @deprecated Please switch to the PfeBusinessService restoreState() method.
     */
    restoreStateFromBackendOrSessionStorage() {
        return this.automaticallyRestoreState();
    }
    storeState() {
        return Promise.all([this.storeStateOnBackend(), this.storeStateInSessionStorage()]);
    }
    /**
     *
     * @param stateKeys
     * @param pfeStateObj
     */
    removeKeysFromState(stateKeys, pfeStateObj) {
        stateKeys.forEach((key) => this.getValueForKey(key, pfeStateObj, true));
    }
    /**
     *
     * @param key
     * @param pfeStateObj
     */
    getValueForKey(key, pfeStateObj, removeKey) {
        const props = key.split('.'), last = props.pop();
        let context = pfeStateObj;
        props.forEach((dKey) => {
            context = context && context[dKey] ? context[dKey] : null;
        });
        if (removeKey && context && last) {
            delete context[last];
        }
        return context;
    }
    /**
     * registers a page for staling
     * @param pageID
     */
    startStaleForPage(pageID) {
        this._staleState.currentPageId = pageID;
        if (!Object.prototype.hasOwnProperty.call(this._staleState.pages, pageID)) {
            this._staleState.pages[pageID] = {
                keys: [],
                staleKeys: [],
                revisit: false,
            };
        }
        else {
            const state = this._staleState.pages[pageID];
            state.revisit = true;
            // eslint-disable-next-line prefer-spread
            state.staleKeys.push.apply(state.staleKeys, state.keys.filter((key) => !this.contains(state.staleKeys, key)));
            state.keys.length = 0;
        }
    }
    /**
     *
     * @param pageId
     * @param pfeAppState
     */
    cleanUpRevisitedPage(pageId, pfeAppState) {
        const state = this._staleState.pages[pageId];
        if (state && state.revisit) {
            /**
             * remove the values from old state
             */
            this.removeKeysFromState(state.staleKeys.filter((key) => !this.contains(state.keys, key)), pfeAppState);
            state.staleKeys.length = 0;
        }
    }
    getAllPagesKeys(exclude) {
        const keys = [];
        this.staleForEach((key) => {
            const state = this._staleState.pages[key];
            if (!this.contains(exclude, key)) {
                // eslint-disable-next-line prefer-spread
                keys.push.apply(keys, state.keys.filter((id) => !this.contains(keys, id)));
            }
        });
        return keys;
    }
    /**
     *
     * @param list
     * @param key
     */
    contains(list, key) {
        return list.includes(key);
    }
    /**
     *
     * @param pageId
     * @param pfeAppState
     */
    removePageFromStaleState(pageId, pfeAppState, allPagesKeys) {
        if (Object.prototype.hasOwnProperty.call(this._staleState.pages, pageId)) {
            const state = this._staleState.pages[pageId];
            const keysToRemove = state.keys.concat(state.staleKeys).reduce((acc, key) => {
                if (!this.contains(acc, key) && !this.contains(allPagesKeys, key)) {
                    acc.push(key);
                }
                return acc;
            }, []);
            this.removeKeysFromState(keysToRemove, pfeAppState);
            delete this._staleState.pages[pageId];
            const pageIndexInVisitedPage = pfeAppState.pfeVisitedPages.indexOf(pageId);
            /**
             * remove pageId values from pfeStateObject
             */
            if (-1 < pageIndexInVisitedPage) {
                pfeAppState.pfeVisitedPages.splice(pageIndexInVisitedPage, 1);
                delete pfeAppState[pageId];
            }
        }
    }
    /**
     *
     * @param key
     */
    addKeyToStale(key) {
        if (this.pfeConfigService.pfeApplicationConfiguration &&
            this.pfeConfigService.pfeApplicationConfiguration.pfeConfig &&
            this.pfeConfigService.pfeApplicationConfiguration.pfeConfig.enableStateCleaning) {
            /**
             * remove expression from key
             */
            key = (key.split('$.')[1] || key).replace(/(\[)/g, '.').replace(/(])/g, '').replace(/['"]/g, '');
            const pfeInternalValues = ['pfeHistory', 'pfeVisitedPages', 'validationErrorData', 'lastVisitedPage'];
            if (this._staleState.currentPageId &&
                !this.contains(pfeInternalValues, key) &&
                !Object.prototype.hasOwnProperty.call(this._staleState.pages, key)) {
                const state = this._staleState.pages[this._staleState.currentPageId];
                if (!this.contains(state.keys, key)) {
                    state.keys.push(key);
                }
            }
        }
    }
    /**
     *
     * @param pageId
     * @param removeFromStateObj
     */
    removeStaleKeysFromState(pageId, removeFromStateObj = false) {
        const pfeAppState = this.pfeUserInputState.get();
        if (removeFromStateObj) {
            const allPagesKeys = this.getAllPagesKeys([pageId]);
            this.removePageFromStaleState(pageId, pfeAppState, allPagesKeys);
        }
        else {
            this.cleanUpRevisitedPage(pageId, pfeAppState);
        }
        this.pfeUserInputState.set(pfeAppState);
    }
    staleForEach(method) {
        Object.keys(this._staleState.pages).forEach(method);
    }
    /**
     *
     * @param page
     */
    analyzePageForStale(page) {
        var _a, _b, _c;
        if ((_c = (_b = (_a = this.pfeConfigService) === null || _a === void 0 ? void 0 : _a.pfeApplicationConfiguration) === null || _b === void 0 ? void 0 : _b.pfeConfig) === null || _c === void 0 ? void 0 : _c.enableStateCleaning) {
            /**
             * check for page excluded from stale analysis
             */
            const excludedState = this.pfeConfigService.pfeApplicationConfiguration.pfeConfig.excludeFromStaleState;
            if (excludedState && this.contains(excludedState, page.pageId)) {
                return;
            }
            this.startStaleForPage(page.pageId);
            /**
             * check if page configuration contains pages to remove from state
             */
            if (Object.prototype.hasOwnProperty.call(page, 'removePageFromState')) {
                if (page.removePageFromState) {
                    this.removePageFromState(page.removePageFromState, page.pageId);
                }
            }
        }
    }
    /**
     * @param pageIds
     * @param currentPageId
     */
    removePageFromState(pageIds, currentPageId) {
        pageIds.forEach((pageId) => {
            var _a, _b, _c;
            if ((_c = (_b = (_a = this.pfeConfigService) === null || _a === void 0 ? void 0 : _a.pfeApplicationConfiguration) === null || _b === void 0 ? void 0 : _b.pfeConfig) === null || _c === void 0 ? void 0 : _c.autoCleanStaleState) {
                this.removeStaleKeysFromState(pageId, true);
            }
            else if (!this.contains(this._staleState.removePageFromState, pageId)) {
                this._staleState.removePageFromState.push(pageId);
            }
        });
        const pageIndexInRemoveState = this._staleState.removePageFromState.indexOf(currentPageId);
        if (-1 < pageIndexInRemoveState) {
            this._staleState.removePageFromState.splice(pageIndexInRemoveState, 1);
        }
    }
    /**
     * Method is triggered by serviceActivator
     */
    cleanStaleStateByServiceActivator() {
        const pfeAppState = this.pfeUserInputState.get();
        const allPagesKeys = this.getAllPagesKeys(this._staleState.removePageFromState);
        // remove stalePages
        this._staleState.removePageFromState.forEach((pageId) => this.removePageFromStaleState(pageId, pfeAppState, allPagesKeys));
        this._staleState.removePageFromState.length = 0;
        // save state
        this.pfeUserInputState.set(pfeAppState);
    }
    /**
     * @deprecated
     */
    getAllUserInput(includeProps) {
        const pfeAppState = this.pfeUserInputState.get();
        const userInputs = {};
        this.getAllPagesKeys([]).forEach((key) => (userInputs[key] = this.getValueForKey(key, pfeAppState)));
        if (includeProps) {
            includeProps.forEach((key) => (userInputs[key] = this.getValueForKey(key, pfeAppState)));
        }
        return userInputs;
    }
    /**
     * @param formIds
     */
    setStateIds(formIds) {
        formIds.forEach((key) => this.addKeyToStale(key));
    }
    /**
     * This method analyze if a navConfig have define userInputState updates and apply them
     * @param stateUpdates navigation config to be analyze
     */
    updateStateValues(stateUpdates) {
        // Exit if no updates are define
        if (!stateUpdates || stateUpdates.length === 0) {
            return;
        }
        try {
            // Filter the updates that could be done
            stateUpdates
                .filter((update) => {
                if (!update.conditions) {
                    return true;
                }
                return PfeRulesEvaluator.evaluateConditions(update.conditions, this.getFullState());
            })
                .forEach((update) => {
                if (update.operation) {
                    this.executeOperationState(update);
                }
                else if (typeof update.value === 'string' &&
                    (update.value.startsWith(STATE_EXPRESSION) || update.value.startsWith(ARRAY_STATE_EXPRESSION))) {
                    const object = this.getStateValueByExpression(update.value);
                    this.storeValueByExpression(update.key, object);
                }
                else {
                    this.storeValueByExpression(update.key, update.value);
                }
            });
        }
        catch (error) {
            this.logger.error(`Error during stateUpdates "${error.message}, used in config : `, stateUpdates);
        }
    }
    /**
     * Execute state operations
     * @param state
     */
    executeOperationState(state) {
        switch (state.operation) {
            case StateOperations.REMOVE:
                // Only trigger the remove operation if the value actually exists in the state.
                // The background of this is, that parents of a non existing expression would be automatically created in that case.
                // As we do not want that to happen, we first check if the value exists.
                if (this.getStateValueByExpression(state.key) !== undefined) {
                    this.storeValueByExpression(state.key, undefined);
                }
                break;
            case StateOperations.PUSH: {
                // Create or retrieve list
                let object = this.getStateValueByExpression(state.key);
                object = !object ? [] : object;
                // Retrieve value if state one or use it
                let insertVal = state.value;
                if (typeof state.value === 'string' &&
                    (state.value.startsWith(STATE_EXPRESSION) || state.value.startsWith(ARRAY_STATE_EXPRESSION))) {
                    insertVal = this.getStateValueByExpression(state.value);
                }
                // If we want to push something using spread assignation, we should have an array
                if (!Array.isArray(insertVal)) {
                    insertVal = [insertVal];
                }
                // Store new list
                this.storeValueByExpression(state.key, [...object, ...insertVal]);
                break;
            }
            case StateOperations.INCREASE: {
                const object = this.getStateValueByExpression(state.key);
                if (typeof state.value === 'number' && typeof object === 'number') {
                    this.storeValueByExpression(state.key, object + state.value);
                }
                break;
            }
            case StateOperations.DECREASE: {
                const object = this.getStateValueByExpression(state.key);
                if (typeof state.value === 'number' && typeof object === 'number') {
                    this.storeValueByExpression(state.key, object - state.value);
                }
                break;
            }
            case StateOperations.JOIN: {
                const object = this.getStateValueByExpression(state.key);
                if (Array.isArray(object)) {
                    this.storeValueByExpression(state.key, object.join(state.value));
                }
                break;
            }
        }
    }
}
PfeStateService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeStateService, deps: [{ token: NGX_PFE_CONFIGURATION }, { token: NGX_PFE_STATE_SESSION_STORAGE_KEY_CONFIG, optional: true }, { token: NGX_PFE_INITIAL_STATE, optional: true }, { token: PfeRestService }, { token: ModelFactory }, { token: PfeUtilService }, { token: i0.ApplicationRef }, { token: PfeConfigurationService }, { token: DOCUMENT }, { token: i3.NgxLoggerService }], target: i0.ɵɵFactoryTarget.Injectable });
PfeStateService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeStateService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeStateService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [NGX_PFE_CONFIGURATION]
                    }] }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [NGX_PFE_STATE_SESSION_STORAGE_KEY_CONFIG]
                    }] }, { type: Promise, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [NGX_PFE_INITIAL_STATE]
                    }] }, { type: PfeRestService }, { type: ModelFactory }, { type: PfeUtilService }, { type: i0.ApplicationRef }, { type: PfeConfigurationService }, { type: Document, decorators: [{
                        type: Inject,
                        args: [DOCUMENT]
                    }] }, { type: i3.NgxLoggerService }];
    } });

/**
 * Get the pfe page ID from any ActivatedRouteSnapshot. This can be from the root
 * or anywhere lower in the tree.
 */
function getPageIDFromRouteSnapshot(snapshot) {
    var _a, _b, _c, _d;
    // Check if we found the pfe page in the routing config.
    // The pfePage boolean flag is created by the pfe during the routing setup
    // and can be used to identify a pfe page.
    // The path corresponds to the pageID and is also set by the pfe during the
    // routing setup.
    // Checking for the pfe flag, also ensures that other router outlets are ignored
    if ((((_a = snapshot === null || snapshot === void 0 ? void 0 : snapshot.data) === null || _a === void 0 ? void 0 : _a.pfePage) === true || //
        ((_c = (_b = snapshot === null || snapshot === void 0 ? void 0 : snapshot.routeConfig) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.pfePage) === true) &&
        ((_d = snapshot === null || snapshot === void 0 ? void 0 : snapshot.routeConfig) === null || _d === void 0 ? void 0 : _d.path)) {
        return snapshot.routeConfig.path;
    }
    else {
        for (const child of snapshot.children) {
            const pageID = getPageIDFromRouteSnapshot(child);
            if (pageID) {
                return pageID;
            }
        }
    }
}

/**
 * The headers of an HttpErrorResponse are stored as a map. This function converts them to an object
 * so that JSONPath expressions can be used against them.
 */
function convertHTTPHeaders(errorResponse) {
    var _a, _b;
    const headers = {};
    (_b = (_a = errorResponse === null || errorResponse === void 0 ? void 0 : errorResponse.headers) === null || _a === void 0 ? void 0 : _a.keys()) === null || _b === void 0 ? void 0 : _b.forEach((key) => {
        headers[key] = errorResponse.headers.getAll(key);
    });
    return headers;
}

const LAST_VISITED_PAGE_STATE_KEY = 'lastVisitedPage';
// This service is mostly there, to avoid a circular dependency.
/**
 * The PfeNavigationUtilService contains navigation util functions
 */
class PfeNavigationUtilService {
    constructor(router, pfeConfigService, pfeStateService, logger) {
        this.router = router;
        this.pfeConfigService = pfeConfigService;
        this.pfeStateService = pfeStateService;
        this.logger = logger;
        this.currentPageId$ = new BehaviorSubject(undefined);
    }
    navigateToGlobalErrorPage(errorResponse) {
        return __awaiter(this, void 0, void 0, function* () {
            const errorPageOption = yield this.getGlobalErrorPageOption(errorResponse);
            return this.navigateToErrorPage(errorPageOption);
        });
    }
    navigateToErrorPage(errorPageOption) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!errorPageOption) {
                this.logger.errorToServer('Tried to navigate to error page, without error page option configuration');
                return;
            }
            this.logger.warn(`Navigating to errorPage:`, errorPageOption);
            if (this.navigateExternal(errorPageOption)) {
                return;
            }
            if (errorPageOption === null || errorPageOption === void 0 ? void 0 : errorPageOption.nextPageId) {
                // The NavigationExtras are used to attach the information that this is a navigation to the error page.
                // This prevents the cleanup of the error message in the state that would usually happen.
                const navigationExtras = {
                    state: {
                        pfe: {
                            navigatingToErrorPage: true,
                        },
                    },
                };
                yield this.navigateRelative(errorPageOption.nextPageId, navigationExtras);
            }
            else {
                this.logger.errorToServer('A navigation to the error page was triggered. But there is no error page. Please update the configuration.');
            }
        });
    }
    navigateRelative(pageID, extras) {
        return __awaiter(this, void 0, void 0, function* () {
            // Currently, there is no way to trigger a relative navigation from within a service without
            // the current activatedRoute.
            // This workaround, works, but we should keep our eyes open for a better solution.
            // If it doesn't work anymore, we need to get the ActivatedRoute as a parameter for this method.
            const urlSegments = this.router.url.split('/');
            urlSegments.pop();
            urlSegments.push(pageID);
            let success;
            try {
                success = yield this.router.navigate(urlSegments, extras);
            }
            catch (error) {
                this.logger.errorToServer('Error during navigation', error);
                throw error;
            }
            // navigatePromise.catch((error) => {
            //     // Navigation failed hard.
            //     this.navigationError = true;
            // });
            return success;
        });
    }
    getFirstPage() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.pfeConfigService.getConfig()).navConfiguration.pages[0].pageId;
        });
    }
    getFirstPageOption() {
        return __awaiter(this, void 0, void 0, function* () {
            return {
                nextPageId: yield this.getFirstPage(),
            };
        });
    }
    /**
     * Evaluates the errorPageNavigation if it is available. The state and the errorResponse are available
     * in the conditions.
     */
    getErrorPage(errorResponse) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            return (_a = (yield this.getGlobalErrorPageOption(errorResponse))) === null || _a === void 0 ? void 0 : _a.nextPageId;
        });
    }
    getGlobalErrorPageOption(errorResponse) {
        return __awaiter(this, void 0, void 0, function* () {
            const navConfiguration = (yield this.pfeConfigService.getConfig()).navConfiguration;
            if (navConfiguration.errorPageNavigation) {
                return this.determineErrorPageOption(navConfiguration.errorPageNavigation, errorResponse);
            }
            else {
                return {
                    nextPageId: navConfiguration.errorPageID,
                };
            }
        });
    }
    determineErrorPageOption(errorPageNavigation, errorResponse) {
        const state = this.pfeStateService.getFullState();
        const nextPageOption = this.determineNextPage(errorPageNavigation, {
            state: state,
            response: errorResponse,
            responseHeaders: convertHTTPHeaders(errorResponse),
        });
        if (!nextPageOption) {
            this.logger.errorToServer('Could not determine error page');
        }
        return nextPageOption;
    }
    determineNextPage(pageNavConfig, state) {
        for (const page of pageNavConfig) {
            let ruleResult;
            if (!page.conditions) {
                ruleResult = true;
            }
            else {
                ruleResult = PfeRulesEvaluator.evaluateConditions(page.conditions, state);
            }
            if (ruleResult) {
                if (page.external) {
                    return page;
                }
                else {
                    if (page.nextPageId && page.nextPageId.startsWith(STATE_EXPRESSION)) {
                        return Object.assign(Object.assign({}, page), { nextPageId: this.pfeStateService.getStateValueByExpression(page.nextPageId) });
                    }
                    return page;
                }
            }
        }
        return undefined;
    }
    /**
     * Parse the URL and the params provided
     * @param nextPage The next page
     */
    getExternalURLParsed(nextPage) {
        let nextPageId = nextPage.nextPageId;
        if (nextPage.externalNavigationPathParams) {
            const params = [];
            const state = this.pfeStateService.getFullState();
            nextPage.externalNavigationPathParams
                .filter((pathParams) => (pathParams.conditions ? PfeRulesEvaluator.evaluateConditions(pathParams.conditions, state) : true))
                .forEach((pathParams) => {
                // Get the value
                const value = this.getStateValueOrLiteral(pathParams.value);
                // If name its provided, we just add as a param
                if (pathParams.name) {
                    params.push(`${pathParams.name}=${value}`);
                }
                else if (pathParams.id) {
                    // If id its provided, we replace the value
                    nextPageId = nextPageId === null || nextPageId === void 0 ? void 0 : nextPageId.replace(`{${pathParams.id}}`, value);
                }
            });
            // Join the params with "&"
            const stringParams = params.join('&');
            if (!stringParams) {
                return nextPageId;
            }
            else {
                // We can have 3 cases:
                // 1. Ends with '?' that means we should just add all the params
                // 2. Includes '?' but not end with that, that means we need to concat the custom param with the added ones
                // 3. No '?' that means we need to add the params
                if ((nextPageId === null || nextPageId === void 0 ? void 0 : nextPageId.includes('?')) && nextPageId.endsWith('?')) {
                    return nextPageId + stringParams;
                }
                else if (nextPageId === null || nextPageId === void 0 ? void 0 : nextPageId.includes('?')) {
                    return nextPageId + '&' + stringParams;
                }
                else {
                    return nextPageId + '?' + stringParams;
                }
            }
        }
        else {
            const toReplace = nextPageId === null || nextPageId === void 0 ? void 0 : nextPageId.match(URL_STATE_REGEX);
            // We iterate over the matched (if any) replacing the matched by their JSONPath Expression
            if (toReplace) {
                for (const matched of toReplace) {
                    // The matched is something like "{$.aaa.bbbb}" we transform to $.aaa.bbbb
                    // to be parsed by translate to JSON path expression
                    const jsonPathExpression = matched.replace(/[{}]/g, '');
                    nextPageId = nextPageId === null || nextPageId === void 0 ? void 0 : nextPageId.replace(matched, this.pfeStateService.getStateValueByExpression(jsonPathExpression));
                }
            }
            return nextPageId;
        }
    }
    getStateValueOrLiteral(value) {
        return value.startsWith(STATE_EXPRESSION) ? this.pfeStateService.getStateValueByExpression(value) : value;
    }
    /**
     * Check if a NavOptionConfig contains an external navigation.
     * If that is the case, navigate
     * @returns {boolean} true if the external navigation was triggered, false if not
     */
    navigateExternal(navOptionConfig) {
        if (navOptionConfig === null || navOptionConfig === void 0 ? void 0 : navOptionConfig.external) {
            // Navigate to the external URL:
            const externalURL = this.getExternalURLParsed(navOptionConfig);
            if (externalURL) {
                window.location.href = externalURL;
            }
            else {
                this.logger.errorToServer('Could not parse external URL from navigation config', navOptionConfig);
            }
            return true;
        }
        return false;
    }
}
PfeNavigationUtilService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeNavigationUtilService, deps: [{ token: i1$1.Router }, { token: PfeConfigurationService }, { token: PfeStateService }, { token: i3.NgxLoggerService }], target: i0.ɵɵFactoryTarget.Injectable });
PfeNavigationUtilService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeNavigationUtilService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeNavigationUtilService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.Router }, { type: PfeConfigurationService }, { type: PfeStateService }, { type: i3.NgxLoggerService }]; } });

// eslint-disable-next-line @typescript-eslint/naming-convention
const PfeTriggerServiceActivatorsType = 'TRIGGER_SERVICE_ACTIVATORS';

function reloadPageAction() {
    return __awaiter(this, void 0, void 0, function* () {
        window.location.reload();
        return false;
    });
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const ReloadPageActionType = 'PFE_RELOAD_PAGE';

// eslint-disable-next-line @typescript-eslint/naming-convention
const PfeResetStateActionType = 'PFE_RESET_STATE';

// eslint-disable-next-line @typescript-eslint/naming-convention
const PfeRewindHistory = 'PFE_REWIND_HISTORY';

// eslint-disable-next-line @typescript-eslint/naming-convention
const PfeUpdateStateOnBackend = 'PFE_UPDATE_STATE_ON_BACKEND';

// eslint-disable-next-line @typescript-eslint/naming-convention
const PfeUpdateStateValuesType = 'UPDATE_STATE_VALUES';

class PfeUpdateStateValuesService {
    constructor(pfeStateService) {
        this.pfeStateService = pfeStateService;
        this.executeActionUpdateStatesValues = (actionConfig) => __awaiter(this, void 0, void 0, function* () { return Promise.resolve(this.pfeStateService.updateStateValues(actionConfig.stateUpdates)); });
    }
}
PfeUpdateStateValuesService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeUpdateStateValuesService, deps: [{ token: PfeStateService }], target: i0.ɵɵFactoryTarget.Injectable });
PfeUpdateStateValuesService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeUpdateStateValuesService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeUpdateStateValuesService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: PfeStateService }]; } });

class PfeUpdateStateOnBackendService {
    constructor(pfeStateService) {
        this.pfeStateService = pfeStateService;
        this.executeActionUpdateStateOnBackend = (actionConfig) => __awaiter(this, void 0, void 0, function* () {
            if (actionConfig.pageToReturnTo) {
                this.pfeStateService.storeValue(LAST_VISITED_PAGE_STATE_KEY, actionConfig.pageToReturnTo);
            }
            return Promise.resolve(this.pfeStateService.storeState());
        });
    }
}
PfeUpdateStateOnBackendService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeUpdateStateOnBackendService, deps: [{ token: PfeStateService }], target: i0.ɵɵFactoryTarget.Injectable });
PfeUpdateStateOnBackendService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeUpdateStateOnBackendService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeUpdateStateOnBackendService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: PfeStateService }]; } });

class PfeRewindHistoryService {
    constructor(pfeStateService, pfeNavigationUtilService, logger) {
        this.pfeStateService = pfeStateService;
        this.pfeNavigationUtilService = pfeNavigationUtilService;
        this.logger = logger;
        this.rewindHistory = (actionConfig) => __awaiter(this, void 0, void 0, function* () {
            if (actionConfig === null || actionConfig === void 0 ? void 0 : actionConfig.clearHistory) {
                this.pfeStateService.storeValue(PFE_HISTORY_KEY, []);
                return;
            }
            const pageId = (actionConfig === null || actionConfig === void 0 ? void 0 : actionConfig.pageId)
                ? this.pfeStateService.getStateValueByExpression(actionConfig === null || actionConfig === void 0 ? void 0 : actionConfig.pageId)
                : this.pfeNavigationUtilService.currentPageId$.value;
            const pfeHistory = this.pfeStateService.getValue(PFE_HISTORY_KEY);
            if (!pfeHistory) {
                return;
            }
            let index = pfeHistory.findIndex((value) => value === pageId);
            if (index < 0) {
                this.logger.warnToServer(`${PfeRewindHistory}: Unable to find index of page "${pageId}", that was supposed to be used in the rewind.`);
                return;
            }
            if (actionConfig === null || actionConfig === void 0 ? void 0 : actionConfig.keepSelf) {
                index++;
            }
            const newPfeHistory = pfeHistory.splice(0, index);
            this.pfeStateService.storeValue(PFE_HISTORY_KEY, newPfeHistory);
        });
    }
}
PfeRewindHistoryService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeRewindHistoryService, deps: [{ token: PfeStateService }, { token: PfeNavigationUtilService }, { token: i3.NgxLoggerService }], target: i0.ɵɵFactoryTarget.Injectable });
PfeRewindHistoryService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeRewindHistoryService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeRewindHistoryService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: PfeStateService }, { type: PfeNavigationUtilService }, { type: i3.NgxLoggerService }]; } });

class PfeResetStateService {
    constructor(pfeStateService) {
        this.pfeStateService = pfeStateService;
        this.resetState = (actionConfig) => __awaiter(this, void 0, void 0, function* () {
            var _a;
            const stateToKeepMap = new Map();
            (_a = actionConfig.excludeFromResetExpressions) === null || _a === void 0 ? void 0 : _a.forEach((exceptionExpression) => {
                const stateValue = this.pfeStateService.getStateValueByExpression(exceptionExpression);
                stateToKeepMap.set(exceptionExpression, stateValue);
            });
            yield this.pfeStateService.resetState();
            for (const [key, value] of stateToKeepMap.entries()) {
                this.pfeStateService.storeValueByExpression(key, value);
            }
            return;
        });
    }
}
PfeResetStateService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeResetStateService, deps: [{ token: PfeStateService }], target: i0.ɵɵFactoryTarget.Injectable });
PfeResetStateService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeResetStateService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeResetStateService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: PfeStateService }]; } });

class PfeActionsService {
    constructor(pfeStateService, pfeUpdateStateValuesService, pfeUpdateStateOnBackendService, pfeConfigurationService, pfeRewindHistoryService, pfeResetStateService, logger) {
        this.pfeStateService = pfeStateService;
        this.pfeUpdateStateValuesService = pfeUpdateStateValuesService;
        this.pfeUpdateStateOnBackendService = pfeUpdateStateOnBackendService;
        this.pfeConfigurationService = pfeConfigurationService;
        this.pfeRewindHistoryService = pfeRewindHistoryService;
        this.pfeResetStateService = pfeResetStateService;
        this.logger = logger;
        this.registeredActions = new Map();
        this.registerDefaultActions();
    }
    /**
     * Execute an array of actions
     * @param actions The array of action
     * @param interruptBusyState Optional callback to interrupt PFE's busy state if an action is marked as `notBusyWhileRunning`
     * @returns The combined boolean response of the actions. True if all returned true. False if at least one returned false.
     */
    executeActions(actions, interruptBusyState) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!actions || (actions && !actions.length)) {
                return true;
            }
            const globalActions = yield this.getGlobalActions();
            for (let action of actions) {
                try {
                    action = this.getGlobalAction(action, globalActions);
                    const actionResult = yield this.executeSpreadAction(action, interruptBusyState);
                    if (!actionResult.actionResult || (actionResult.wasExecuted && action.break)) {
                        return actionResult.actionResult;
                    }
                }
                catch (error) {
                    this.logger.errorToServer(`Error on ACTION "${action.type || action.globalConfigId}"`, error.message);
                    throw error;
                }
            }
            return true;
        });
    }
    /**
     * Get the global actions. Then spread the action with the global action (if needed)
     * Then execute the action
     * @param actionCfg Action configuration
     * @param interruptBusyState Function to call to interrupt PFE's busy state if an action is waiting for user input
     */
    executeAction(actionCfg, interruptBusyState) {
        return __awaiter(this, void 0, void 0, function* () {
            const globalActions = yield this.getGlobalActions();
            const action = this.getGlobalAction(actionCfg, globalActions);
            return (yield this.executeSpreadAction(action, interruptBusyState)).actionResult;
        });
    }
    /**
     * Execute a spread action
     * @param action The action to execute
     * @returns The result that was returned from the action
     */
    executeSpreadAction(action, interruptBusyState) {
        return __awaiter(this, void 0, void 0, function* () {
            this.checkIfActionIsRegistered(action);
            const shouldExecuteAction = action.conditions
                ? PfeRulesEvaluator.evaluateConditions(action.conditions, this.pfeStateService.getFullState())
                : true;
            if (shouldExecuteAction) {
                // The get can never be undefined as checkIfActionIsRegistered() already ensures that.
                // See also: https://github.com/microsoft/TypeScript/issues/9619
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const { func, options } = this.registeredActions.get(action.type);
                if (options === null || options === void 0 ? void 0 : options.notBusyWhileRunning) {
                    interruptBusyState === null || interruptBusyState === void 0 ? void 0 : interruptBusyState(true);
                }
                const result = yield func(action).finally(() => {
                    if (options === null || options === void 0 ? void 0 : options.notBusyWhileRunning) {
                        interruptBusyState === null || interruptBusyState === void 0 ? void 0 : interruptBusyState(false);
                    }
                });
                // If the action returned something not boolean, we default to true:
                return {
                    wasExecuted: true,
                    actionResult: typeof result === 'boolean' ? result : true,
                };
            }
            else {
                return {
                    wasExecuted: false,
                    actionResult: true,
                };
            }
        });
    }
    /**
     * Check if an action its registered and if no it throws an error
     * @param action An action
     * @throws RangeError if the action its not registered
     */
    checkIfActionIsRegistered(action) {
        if (!(action === null || action === void 0 ? void 0 : action.type)) {
            throw new Error('The given action config does not have a type');
        }
        if (!this.registeredActions.has(action.type)) {
            throw new RangeError(`The action "${action.type}" is not registered.`);
        }
    }
    /**
     * Register an action that can be invoked later
     * @param type The name of the action
     * @param action The action itself, should be an arrow async function, see the default actions
     * to see examples
     * @param options Further options for the action.
     */
    registerAction(type, action, options) {
        if (this.registeredActions.has(type)) {
            throw new RangeError(`The action "${type}" its already registered`);
        }
        this.registeredActions.set(type, { func: action, options });
    }
    /**
     * Get the global actions from the config
     */
    getGlobalActions() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            return (_b = (_a = (yield this.pfeConfigurationService.getConfig())) === null || _a === void 0 ? void 0 : _a.globalConfiguration) === null || _b === void 0 ? void 0 : _b.actions;
        });
    }
    /**
     * Returns the action if there is no globalAction.
     * Else will return the action on the global action spread with the content of the action
     * @param actionCfg The action cfg
     * @param globalActionsCfg The global action config
     */
    getGlobalAction(actionCfg, globalActionsCfg) {
        if (!actionCfg.globalConfigId || !globalActionsCfg) {
            return actionCfg;
        }
        const id = actionCfg.globalConfigId;
        const globalAction = globalActionsCfg[id];
        if (!globalAction) {
            throw new RangeError(`The action with id "${id}" is not declared on the global actions`);
        }
        return Object.assign(Object.assign({}, globalAction), actionCfg);
    }
    /**
     * Register the default actions included in the Pfe
     */
    registerDefaultActions() {
        this.registerAction(PfeUpdateStateValuesType, this.pfeUpdateStateValuesService.executeActionUpdateStatesValues);
        this.registerAction(PfeUpdateStateOnBackend, this.pfeUpdateStateOnBackendService.executeActionUpdateStateOnBackend);
        this.registerAction(PfeRewindHistory, this.pfeRewindHistoryService.rewindHistory);
        this.registerAction(ReloadPageActionType, reloadPageAction);
        this.registerAction(PfeResetStateActionType, this.pfeResetStateService.resetState);
    }
}
PfeActionsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeActionsService, deps: [{ token: PfeStateService }, { token: PfeUpdateStateValuesService }, { token: PfeUpdateStateOnBackendService }, { token: PfeConfigurationService }, { token: PfeRewindHistoryService }, { token: PfeResetStateService }, { token: i3.NgxLoggerService }], target: i0.ɵɵFactoryTarget.Injectable });
PfeActionsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeActionsService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeActionsService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: PfeStateService }, { type: PfeUpdateStateValuesService }, { type: PfeUpdateStateOnBackendService }, { type: PfeConfigurationService }, { type: PfeRewindHistoryService }, { type: PfeResetStateService }, { type: i3.NgxLoggerService }]; } });

class PfeTriggerServiceActivatorsService {
    constructor() {
        this.executeActionTriggerServiceActivators = (actionConfig) => __awaiter(this, void 0, void 0, function* () {
            if (!this.pfeServiceActivatorsService) {
                throw new Error('serviceActivatorService not available');
            }
            else {
                yield this.pfeServiceActivatorsService.spreadWithGlobalServiceActivatorConfig(actionConfig.serviceActivators);
                yield this.pfeServiceActivatorsService.handleServiceActivators(actionConfig.serviceActivators, actionConfig.executeServiceActivatorsSequential);
                return Promise.resolve();
            }
        });
    }
    set serviceActivatorService(service) {
        this.pfeServiceActivatorsService = service;
    }
}
PfeTriggerServiceActivatorsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeTriggerServiceActivatorsService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
PfeTriggerServiceActivatorsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeTriggerServiceActivatorsService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeTriggerServiceActivatorsService, decorators: [{
            type: Injectable
        }] });

/**
 * This service is responsible to handle all configured service activator requests and responses.
 */
class PfeServiceActivatorService {
    constructor(ngxPfeModuleConfiguration, http, pfeStateService, pfeNavigationUtilService, pfeConfigurationService, logger, pfeActionsService, pfeTriggerServiceActivatorsService) {
        var _a;
        this.ngxPfeModuleConfiguration = ngxPfeModuleConfiguration;
        this.http = http;
        this.pfeStateService = pfeStateService;
        this.pfeNavigationUtilService = pfeNavigationUtilService;
        this.pfeConfigurationService = pfeConfigurationService;
        this.logger = logger;
        this.pfeActionsService = pfeActionsService;
        this.pfeTriggerServiceActivatorsService = pfeTriggerServiceActivatorsService;
        /**
         * Set to true, when a navigation or network call (service activator call) is currently running.
         */
        this.serviceActivatorCallInProgress$ = new BehaviorSubject(false);
        this._serviceActivatorCallInProgressDetails$ = new BehaviorSubject([]);
        this.serviceActivatorCallInProgressDetails$ = this._serviceActivatorCallInProgressDetails$.asObservable();
        // A list of active async calls per pageId:
        this.activeAsyncServiceActivatorCallsPerPage = new Map();
        /**
         * Setting up the pfeTriggerServiceActivatorsService here is a workaround to prevent a circular dependency
         * between the PfeServiceActivatorService and the PfeActionsService.
         *
         * - The PfeServiceActivatorService wants to use the PfeActionsService to trigger actions
         * - The PfeActionsService had the registration of TRIGGER_SERVICE_ACTIVATORS action directly
         * in its constructor. It requires the PfeServiceActivatorService
         * -> We're turning in circles here...
         *
         * Ways to resolve this:
         * - Moving the action registration out of the PfeActionsService into its own module did not really work.
         * It was fine within the pfe, but lead to unexpected effects in apps that use pfe services in locations where they
         * are instantiated sooner (e.g. interceptors). The background for this is, that a registration within a module leads to an immediate
         * instantiation of all the services as soon as the module is imported.
         *
         * - Moving it to another unrelated service is difficult, as that could lead to timing issues where an action is triggered
         * before it is registered.
         *
         * - Moving the registration here is safe as the action relies on the PfeServiceActivatorService. As soon as its gets
         * instantiated, it will handover its own instance to the action and register it with the PfeActionsService.
         * That way it is guaranteed that the PfeServiceActivatorService instance is available before the action can be triggered.
         *
         * How is it ensured that both of those services are instantiated before a service activator call is triggered?
         * -> Both are imported by the PFEPageRoutingEventsHandlerService, which gets active during the initial routing setup.
         * This happens before there is any chance that a call/action is triggered. It is therefore ensured that the registration
         * of the action happens before any action is triggered. (=This also works in the firstPage config which is the earliest possible trigger)
         *
         * How about the future? -> In general it seems like a refactoring of how the action registration works, might be a cleaner way to go.
         */
        this.pfeTriggerServiceActivatorsService.serviceActivatorService = this;
        this.pfeActionsService.registerAction(PfeTriggerServiceActivatorsType, this.pfeTriggerServiceActivatorsService.executeActionTriggerServiceActivators);
        this.baseURL = `${this.ngxPfeModuleConfiguration.service_activator_endpoint}`;
        const ignoreEmptyArrays = (_a = this.ngxPfeModuleConfiguration.ignoreEmptyArraysInServiceActivatorPayloads) !== null && _a !== void 0 ? _a : false;
        if (ignoreEmptyArrays) {
            this.getValueByExpression = (o, p) => PfeUtilService.getValueOrListFromJsonExpression(o, p);
        }
        else {
            this.getValueByExpression = (o, p) => PfeUtilService.getValueOrList(o, p);
        }
    }
    // Made static, so it can be easily accessed by the config editor:
    static spreadWithGlobalServiceActivatorConfig(serviceActivatorConfigsArray, globalServiceActivators) {
        if (serviceActivatorConfigsArray && serviceActivatorConfigsArray.length > 0 && globalServiceActivators) {
            serviceActivatorConfigsArray.forEach((serviceActivatorConfig, index, array) => {
                const newConfig = PfeServiceActivatorService.spreadSingleServiceActivatorConfig(serviceActivatorConfig, globalServiceActivators);
                if (newConfig) {
                    array[index] = newConfig;
                }
                // Only try it one time per config:
                array[index]._alreadySpread = true;
            });
        }
    }
    /**
     * Applies a global service activator config on to a service activator config.
     * Returns new config if there was a global config or undefined if there was none.
     */
    static spreadSingleServiceActivatorConfig(serviceActivatorConfig, globalServiceActivators) {
        if (!serviceActivatorConfig._alreadySpread && serviceActivatorConfig.globalConfigID) {
            const globalServiceActivatorConfig = globalServiceActivators[serviceActivatorConfig.globalConfigID];
            if (globalServiceActivatorConfig) {
                return Object.assign(Object.assign({}, globalServiceActivatorConfig), serviceActivatorConfig);
            }
        }
    }
    /**
     * Calls all the service activators of a configuration.
     * It either returns a forkJoin observable for all issued service calls (by default) or a concatMap observable if
     * executeServiceActivatorsSequential param is set to true, meaning to execute all the service activators
     * sequentially.
     *
     * The httpErrorHandler will be called in case of http errors.
     * For serviceActivators that do not use the async option an exception will also be thrown.
     * This exception should not be used for error handling, as it is not thrown for async calls.
     * If the exception is thrown, the navigation is not done.
     * We are aware of the ugliness of this solution and have the following
     * TODO: Improve: Remove error callback and replace with some common async solution
     *
     * To keep the promise handling of the service activators less complex, spreadWithGlobalServiceActivatorConfig has to be called
     * before handleServiceActivators is called. (with await)
     *
     * @param serviceActivators
     * @param executeServiceActivatorsSequential
     * @param httpErrorHandler will be called in case of http errors.
     */
    handleServiceActivators(serviceActivators, executeServiceActivatorsSequential, httpErrorHandler) {
        if (!serviceActivators || serviceActivators.length === 0) {
            return Promise.resolve(undefined);
        }
        /**
         * Check for updateValues
         */
        serviceActivators
            .filter((serviceActivator) => serviceActivator.updateStateValuesBefore)
            .forEach((service) => {
            this.pfeStateService.updateStateValues(service.updateStateValuesBefore);
        });
        // https://github.com/Microsoft/TypeScript/wiki/'this'-in-TypeScript#local-fat-arrow
        const errorHandler = httpErrorHandler
            ? (error, serviceActivatorConfig) => httpErrorHandler(error, serviceActivatorConfig)
            : (error, serviceActivatorConfig) => this.handleServiceActivatorError(error, serviceActivatorConfig);
        const hasCleanStaleState = serviceActivators.filter((serviceActivator) => serviceActivator.cleanStaleState).length;
        /**
         * clean state exists
         */
        if (hasCleanStaleState) {
            /**
             * check and perform stalestate cleaning
             */
            this.pfeStateService.cleanStaleStateByServiceActivator();
        }
        // We check if we need to emit the callInProgress
        let emitInProgress = this.shouldEmitInProgress(serviceActivators);
        try {
            let joinedPromise;
            if (executeServiceActivatorsSequential) {
                this.emitServiceActivatorCallInProgress(emitInProgress, true);
                joinedPromise = this.execServiceActivatorsSequential(serviceActivators, errorHandler, emitInProgress);
            }
            else {
                serviceActivators = this.filterServiceActivators(serviceActivators);
                emitInProgress = this.shouldEmitInProgress(serviceActivators);
                // If the call its not in the sequential, the emit of the event its done on "waitForAsyncServiceActivators"
                joinedPromise = this.execServiceActivators(serviceActivators, errorHandler, emitInProgress);
            }
            if (serviceActivators.length > 0) {
                joinedPromise.finally(() => {
                    this.emitServiceActivatorCallInProgress(emitInProgress, false);
                });
            }
            return joinedPromise;
        }
        catch (error) {
            this.logger.errorToServer('handleServiceActivators: Error', error.message);
            throw error;
        }
    }
    waitForAsyncServiceActivators(pageID) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.activeAsyncServiceActivatorCallsPerPage && this.activeAsyncServiceActivatorCallsPerPage.size > 0) {
                const activeAsyncCalls = this.activeAsyncServiceActivatorCallsPerPage.get(pageID);
                if (activeAsyncCalls && activeAsyncCalls.length > 0) {
                    const emitInProgress = this.shouldEmitInProgress(activeAsyncCalls);
                    this.addAsyncInProgressServiceActivatorDetails(activeAsyncCalls, emitInProgress);
                    this.emitServiceActivatorCallInProgress(emitInProgress, true);
                    const allPromise = Promise.all(activeAsyncCalls.map((call) => call.callPromise));
                    allPromise.finally(() => this.emitServiceActivatorCallInProgress(emitInProgress, false));
                    return allPromise;
                }
            }
            return [];
        });
    }
    addAsyncInProgressServiceActivatorDetails(activeAsyncCalls, emitInProgress) {
        if (!emitInProgress) {
            return;
        }
        const activeAsyncCallConfigs = activeAsyncCalls.map((call) => ({
            globalConfigID: call.id,
            displayMessage: call.displayMessage,
            path: call.id,
        }));
        activeAsyncCallConfigs.forEach((config) => {
            this.addInProgressServiceActivatorDetails(config, true);
        });
    }
    addInProgressServiceActivatorDetails(serviceActivator, emitInProgress) {
        if (!emitInProgress) {
            return;
        }
        this._serviceActivatorCallInProgressDetails$.next([...this._serviceActivatorCallInProgressDetails$.value, clone(serviceActivator)]);
    }
    removeInProgressServiceActivatorDetails(serviceActivator) {
        if (!serviceActivator) {
            return;
        }
        const inProgressServices = this._serviceActivatorCallInProgressDetails$.value.filter((inProgressService) => !this.areServiceActivatorsEqual(serviceActivator, inProgressService));
        this._serviceActivatorCallInProgressDetails$.next(inProgressServices);
    }
    /**
     * Handles the responses of service activator calls and writes data back to the state if a response mapping is configured.
     */
    handleServiceActivatorResult(response, serviceActivatorConfig, dataMapping) {
        this.removeInProgressServiceActivatorDetails(serviceActivatorConfig);
        if (dataMapping && dataMapping.length > 0) {
            this.handleResponseDataMapping(dataMapping, response, serviceActivatorConfig === null || serviceActivatorConfig === void 0 ? void 0 : serviceActivatorConfig.mapUndefinedResponses);
        }
        // Handles the configured error mappings
        if ((serviceActivatorConfig === null || serviceActivatorConfig === void 0 ? void 0 : serviceActivatorConfig.serviceActivatorResponseStatusHandlers) &&
            serviceActivatorConfig.serviceActivatorResponseStatusHandlers.length > 0) {
            serviceActivatorConfig.serviceActivatorResponseStatusHandlers.forEach((responseStatusHandler) => {
                if (response.status === responseStatusHandler.statusCode) {
                    try {
                        this.pfeStateService.storeValueByExpression(responseStatusHandler.stateKeyExpression, responseStatusHandler.stateValue);
                    }
                    catch (error) {
                        this.logger.errorToServer('handleServiceActivatorResult: Error in responseErrorMapping', error);
                        throw error;
                    }
                }
            });
        }
    }
    handleResponseDataMapping(dataMapping, response, mapUndefinedResponses) {
        dataMapping.forEach((responseDataMapping) => {
            const fullState = this.pfeStateService.getFullState();
            // If we have conditions and are not fulfilled, we don't execute the mapping for this object
            if (responseDataMapping.conditions && !PfeRulesEvaluator.evaluateConditions(responseDataMapping.conditions, fullState)) {
                return;
            }
            try {
                let value;
                let responseBody;
                if (response instanceof HttpErrorResponse) {
                    responseBody = response.error;
                }
                else {
                    responseBody = response.body;
                }
                const responseData = responseDataMapping.useHTTPResponseAsData ? response : responseBody;
                // Same issue as in the requestMapping: https://github.com/dchester/jsonpath/issues/72
                if (responseDataMapping.stateKeyExpression === '$') {
                    let state = this.pfeStateService.getFullState();
                    if (typeof responseData === 'object') {
                        state = Object.assign(Object.assign({}, state), responseData);
                    }
                    else {
                        this.logger.error('The responseDataExpression "$" can only be used with objects in the responseData');
                    }
                    this.pfeStateService.setFullState(state);
                }
                else {
                    if (responseData) {
                        value = this.getValueByExpression(responseData, responseDataMapping.responseDataExpression);
                    }
                    if (mapUndefinedResponses || (value !== undefined && value !== null)) {
                        this.pfeStateService.storeValueByExpression(responseDataMapping.stateKeyExpression, value);
                    }
                }
            }
            catch (error) {
                this.logger.errorToServer('handleServiceActivatorResult: Error in responseDataMapping', error);
                throw error;
            }
        });
    }
    /**
     * Called if one or multiple service activator calls failed.
     */
    handleServiceActivatorError(errorResponse, serviceActivatorConfig) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!(serviceActivatorConfig === null || serviceActivatorConfig === void 0 ? void 0 : serviceActivatorConfig.errorHandling)) {
                return this.handleServiceActivatorErrorLegacy(errorResponse, serviceActivatorConfig);
            }
            const state = this.pfeStateService.getFullState();
            const errorCase = this.determineErrorCase((_a = serviceActivatorConfig.errorHandling) === null || _a === void 0 ? void 0 : _a.errorCases, {
                state: state,
                response: errorResponse,
                responseHeaders: convertHTTPHeaders(errorResponse),
            });
            if (!errorCase) {
                this.logger.warn('Could not determine service activator error handling. Falling back to global error page.');
                this.pfeNavigationUtilService.navigateToGlobalErrorPage(errorResponse);
                return false;
            }
            // We do have an error case configuration, let's do the actual error handling:
            if (errorCase.errorResponseDataMapping) {
                this.handleResponseDataMapping(errorCase.errorResponseDataMapping, errorResponse, true);
            }
            if (errorCase.actions) {
                try {
                    const actionsResult = yield this.pfeActionsService.executeActions(errorCase.actions);
                    if (!errorCase.ignoreActionsResult) {
                        return actionsResult;
                    }
                }
                catch (error) {
                    // Something went wrong in the action. Simply continue to the errorPageNavigation.
                    this.logger.error('Error when executing action in service activator error handling. Continuing with error handling...');
                }
            }
            if (errorCase.ignoreError) {
                return true;
            }
            if (!errorCase.disableErrorPageNavigation) {
                let errorPageOption;
                if (errorCase.errorPageNavigation) {
                    errorPageOption = this.pfeNavigationUtilService.determineErrorPageOption(errorCase.errorPageNavigation, errorResponse);
                }
                if (errorPageOption) {
                    this.pfeNavigationUtilService.navigateToErrorPage(errorPageOption);
                }
                else {
                    this.pfeNavigationUtilService.navigateToGlobalErrorPage(errorResponse);
                }
            }
            return false;
        });
    }
    determineErrorCase(errorCases, state) {
        if (!errorCases) {
            return undefined;
        }
        for (const errorCase of errorCases) {
            let conditionsResult;
            if (!errorCase.conditions) {
                conditionsResult = true;
            }
            else {
                conditionsResult = PfeRulesEvaluator.evaluateConditions(errorCase.conditions, state);
            }
            if (conditionsResult) {
                return errorCase;
            }
        }
        return undefined;
    }
    /**
     * Legacy error handling that is still supported. Called by the new error handling, if there is no
     * new error handling config.
     */
    handleServiceActivatorErrorLegacy(errorResponse, serviceActivatorConfig) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isStatusExcluded(errorResponse, serviceActivatorConfig)) {
                try {
                    this.handleServiceActivatorResult(errorResponse, serviceActivatorConfig, serviceActivatorConfig === null || serviceActivatorConfig === void 0 ? void 0 : serviceActivatorConfig.responseDataMapping);
                }
                catch (error) {
                    this.logger.error('There was an error in the service activator error handling', error);
                    if (!(serviceActivatorConfig === null || serviceActivatorConfig === void 0 ? void 0 : serviceActivatorConfig._disableErrorPageNavigation)) {
                        this.pfeNavigationUtilService.navigateToGlobalErrorPage(error);
                    }
                }
            }
            else {
                if (serviceActivatorConfig === null || serviceActivatorConfig === void 0 ? void 0 : serviceActivatorConfig.errorResponseDataMapping) {
                    this.handleServiceActivatorResult(errorResponse, serviceActivatorConfig, serviceActivatorConfig === null || serviceActivatorConfig === void 0 ? void 0 : serviceActivatorConfig.errorResponseDataMapping);
                }
                if (!(serviceActivatorConfig === null || serviceActivatorConfig === void 0 ? void 0 : serviceActivatorConfig._disableErrorPageNavigation)) {
                    this.pfeNavigationUtilService.navigateToGlobalErrorPage(errorResponse);
                }
            }
        });
    }
    filterServiceActivators(serviceActivators) {
        return serviceActivators.filter((serviceActivator) => this.evaluateServiceActivatorCondition(serviceActivator));
    }
    evaluateServiceActivatorCondition(serviceActivator) {
        // The serviceActivatorID attribute was superseded by the path attribute everywhere (For example in the data models).
        // To ensure backwards compatibility it is still checked here, for now...
        if (serviceActivator.cleanStaleState &&
            !(serviceActivator.path || serviceActivator['serviceActivatorID'])) {
            return false;
        }
        if (!serviceActivator.conditions) {
            return true;
        }
        return PfeRulesEvaluator.evaluateConditions(serviceActivator.conditions, this.pfeStateService.getFullState());
    }
    execServiceActivatorsSequential(serviceActivators, errorHandler, emitInProgress) {
        return from(serviceActivators)
            .pipe(concatMap((serviceActivator) => {
            if (this.evaluateServiceActivatorCondition(serviceActivator)) {
                this.addInProgressServiceActivatorDetails(serviceActivator, emitInProgress);
                return this.callServiceActivator(serviceActivator, errorHandler);
            }
            return EMPTY;
        }))
            .toPromise();
    }
    execServiceActivators(serviceActivators, errorHandler, emitInProgress) {
        const serviceActivatorCalls = [];
        serviceActivators.forEach((serviceActivator) => {
            const serviceActivatorObservable = this.callServiceActivator(serviceActivator, errorHandler);
            if (!serviceActivator.async) {
                this.addInProgressServiceActivatorDetails(serviceActivator, emitInProgress);
                this.emitServiceActivatorCallInProgress(emitInProgress, true);
                serviceActivatorCalls.push(serviceActivatorObservable);
            }
            else {
                const promise = serviceActivatorObservable.toPromise();
                if (serviceActivator.asyncResolveBeforePageIds) {
                    this.addCallToAsyncPromiseList(serviceActivator, promise);
                }
            }
        });
        return forkJoin(serviceActivatorCalls).toPromise();
    }
    handleServiceActivatorRequest(serviceActivator) {
        var _a;
        const fullState = this.pfeStateService.getFullState();
        const defaultRequestBody = (_a = this.ngxPfeModuleConfiguration.defaultRequestBody) !== null && _a !== void 0 ? _a : PFE_DEFAULT_REQUEST_BODY.EMPTY;
        if (!serviceActivator.requestDataMapping && serviceActivator.serviceActivatorMethod === 'GET') {
            return undefined;
        }
        if (!serviceActivator.requestDataMapping || serviceActivator.requestDataMapping.length === 0) {
            return defaultRequestBody === PFE_DEFAULT_REQUEST_BODY.FULL_STATE ? fullState : undefined;
        }
        let payload = {};
        let uglyWorkaroundFirst = true;
        serviceActivator.requestDataMapping.forEach((requestDataMapping) => {
            // If we have conditions and are not fulfilled, we don't add this request param
            if (requestDataMapping.conditions && !PfeRulesEvaluator.evaluateConditions(requestDataMapping.conditions, fullState)) {
                return;
            }
            try {
                const storeValue = this.getValueByExpression(fullState, requestDataMapping.stateKeyExpression);
                const payloadValue = this.getValueByExpression(payload, requestDataMapping.requestDataExpression);
                /**
                 * BEGIN - Ugly workaround because JSONPath does not work when we are replacing the root object ("$")
                 *         Read more about the issue here: https://github.com/dchester/jsonpath/issues/72
                 */
                if (requestDataMapping.requestDataExpression === '$') {
                    // Clone stateValue locally
                    if (uglyWorkaroundFirst) {
                        payload = JSON.parse(JSON.stringify(storeValue));
                        uglyWorkaroundFirst = false;
                        return;
                    }
                    if (!uglyWorkaroundFirst && Array.isArray(payloadValue)) {
                        payload = [...payloadValue, ...storeValue];
                        return;
                    }
                    if (!uglyWorkaroundFirst && typeof payloadValue === 'object') {
                        payload = Object.assign(Object.assign({}, payloadValue), storeValue);
                        return;
                    }
                }
                /* END of Ugly workaround */
                // Remove value from from payload if stateKeyExpression is undefined
                if (storeValue === 'undefined') {
                    jsonpath.value(payload, requestDataMapping.requestDataExpression, undefined);
                    return;
                }
                // Extend the Array
                if (Array.isArray(payloadValue)) {
                    jsonpath.value(payload, requestDataMapping.requestDataExpression, [...payloadValue, ...storeValue]);
                    return;
                }
                // Extend the Object
                if (payloadValue && typeof payloadValue === 'object' && storeValue && typeof storeValue === 'object') {
                    jsonpath.value(payload, requestDataMapping.requestDataExpression, Object.assign(Object.assign({}, payloadValue), storeValue));
                    return;
                }
                // Replace string or integer into
                jsonpath.value(payload, requestDataMapping.requestDataExpression, storeValue);
            }
            catch (error) {
                this.logger.errorToServer('handleServiceActivatorRequest: Error in requestDataMapping ', error);
            }
        });
        return payload;
    }
    // Method that builds url replacing params in url
    addPathParamsToUrl(url, pathParams) {
        const fullState = this.pfeStateService.getFullState();
        const pathParamPlaceholderRegex = new RegExp(`{.+?}`, 'g');
        const changedUrl = pathParams
            .filter((param) => !param.conditions || PfeRulesEvaluator.evaluateConditions(param.conditions, fullState))
            .reduce((reducedUrl, param) => {
            var _a;
            const value = (_a = this.getValueByExpression(fullState, param.value)) !== null && _a !== void 0 ? _a : '';
            reducedUrl = reducedUrl.replace(new RegExp(`{${param.id}}`, 'g'), value);
            return reducedUrl;
        }, url)
            .replace(pathParamPlaceholderRegex, '');
        return changedUrl;
    }
    callServiceActivator(serviceActivator, httpErrorHandler) {
        // TODO: @deprecated Remove the trailing / with a future major version:
        let url = `${this.baseURL}/${this.getServiceActivatorPath(serviceActivator)}${this.ngxPfeModuleConfiguration.appendSlashToServiceActivators ? '/' : ''}`;
        // Check if the full URL is set in the config file.
        if (new RegExp(/^(http:|https:|\/\/)/, 'i').test(this.getServiceActivatorPath(serviceActivator))) {
            url = this.getServiceActivatorPath(serviceActivator);
        }
        if (serviceActivator.pathParams && serviceActivator.pathParams.length > 0) {
            url = this.addPathParamsToUrl(url, serviceActivator.pathParams);
        }
        // TODO Change here how to construct url using pathparams
        const method = serviceActivator.serviceActivatorMethod || 'POST';
        const payload = this.handleServiceActivatorRequest(serviceActivator);
        const params = method === 'GET' ? new HttpParams({ fromObject: payload }) : undefined;
        const req = new HttpRequest(method, url, payload, {
            params,
        });
        return this.http.request(req).pipe(
        // The resulting observable is converted to a promise later.
        // It seems like, this causes typescript to get confused with the
        // type information in strict mode here. That's why the any shows up here.
        filter((eventType) => eventType.type === HttpEventType.Response), tap((result) => {
            this.handleServiceActivatorResult(result, serviceActivator, serviceActivator.responseDataMapping);
        }), catchError((error) => new Observable((subscriber) => {
            (() => __awaiter(this, void 0, void 0, function* () {
                try {
                    // Wait for the httpErrorHandler to be done.
                    const ignoreError = httpErrorHandler ? yield httpErrorHandler(error, serviceActivator) : false;
                    // If the error should be ignored, everything will continue. If not, everything is stopped
                    // The legacy error handling returns a dark void, the new one a shiny boolean:
                    if (ignoreError !== undefined && typeof ignoreError === 'boolean') {
                        if (ignoreError) {
                            // This will prevent an error to be thrown above the service activator handling.
                            // That means the navigation will run as if there was no error.
                            // It also means, that the error handling will be executed for all other service activators in the array
                            // (If there is another error).
                            subscriber.next(undefined);
                        }
                        else {
                            // This will abort the navigation when the service activator fails
                            // The error handling will not be called multiple times for multiple service activators (That arrive later)
                            subscriber.error(error);
                        }
                    }
                    else {
                        // Legacy error handling:
                        if (this.isStatusExcluded(error, serviceActivator) && !serviceActivator.preventNavigationOnError) {
                            // This will prevent an error to be thrown above the service activator handling.
                            // That means the navigation will run as if there was no error.
                            // It also means, that the error handling will be executed for all other service activators in the array
                            // (If there is another error).
                            subscriber.next(undefined);
                        }
                        else {
                            // This will abort the navigation when the service activator fails
                            //  The error handling will not be called multiple times for multiple service activators (That arrive later)
                            subscriber.error(error);
                        }
                    }
                    subscriber.complete();
                }
                catch (errorHandlerError) {
                    // The httpErrorHandler failed. This should never happen, but who knows, better abort
                    subscriber.error(errorHandlerError);
                }
            }))();
        })));
    }
    addCallToAsyncPromiseList(serviceActivator, callPromise) {
        (serviceActivator.asyncResolveBeforePageIds || []).forEach((pageID) => {
            const newCall = {
                id: (serviceActivator.globalConfigID || serviceActivator.path),
                callPromise,
                dontEmitInProgress: Boolean(serviceActivator.dontEmitInProgress),
                displayMessage: serviceActivator.displayMessage,
            };
            if (this.activeAsyncServiceActivatorCallsPerPage.has(pageID)) {
                // There's a has() directly above, get your &$§@$%§$ together typescript...
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const pageCallsWithoutPreviousServiceActivatorCall = this.activeAsyncServiceActivatorCallsPerPage
                    .get(pageID)
                    .filter((pageCall) => ![serviceActivator.globalConfigID, serviceActivator.path].includes(pageCall.id));
                this.activeAsyncServiceActivatorCallsPerPage.set(pageID, [...pageCallsWithoutPreviousServiceActivatorCall, newCall]);
            }
            else {
                this.activeAsyncServiceActivatorCallsPerPage.set(pageID, [newCall]);
            }
        });
    }
    /**
     * Takes in a ServiceActivatorConfigsArray and spreads it on top of a global
     * service activator config, if there is one.
     * The array is changed directly!
     */
    spreadWithGlobalServiceActivatorConfig(serviceActivatorConfigsArray) {
        return __awaiter(this, void 0, void 0, function* () {
            const config = yield this.pfeConfigurationService.getConfig();
            PfeServiceActivatorService.spreadWithGlobalServiceActivatorConfig(serviceActivatorConfigsArray, config.navConfiguration.serviceActivators);
        });
    }
    /**
     * The serviceActivatorID attribute was superseded by the path attribute everywhere (For example in the data models).
     *  To ensure backwards compatibility it is still checked here, for now...
     */
    getServiceActivatorPath(config) {
        let path = config.path;
        if (!path) {
            path = config['serviceActivatorID'];
            if (path) {
                this.logger.warnToServer('Found a serviceActivatorID in the configuration. Please update your configuration to use the path attribute instead.');
            }
        }
        if (!path) {
            this.logger.error('Was not able to find path in serviceActivator config', config);
            throw new Error('Was not able to determine serviceActivator path');
        }
        return path;
    }
    /**
     * Check if at least one element in the array is configured to emit a progress.
     * This means that the "dontEmitInProgress" flag has to be false or undefined for at least one element.
     * If that is the case, the overall result to emit the progress will be true.
     * If all elements do not allow to emit the in progress state, the overall result will be false.
     *
     * The default for an empty config array is false.
     *
     * @param dontEmitInProgressConfig An array of objects that have the dontEmitInProgress flag
     */
    shouldEmitInProgress(dontEmitInProgressConfig) {
        if (dontEmitInProgressConfig && dontEmitInProgressConfig.length > 0) {
            return dontEmitInProgressConfig.some((config) => !config.dontEmitInProgress);
        }
        else {
            return false;
        }
    }
    /**
     * Check if should emit the service activator call in progress
     * @param emit If should emit the value
     * @param value If the event its "true" or "false"
     */
    emitServiceActivatorCallInProgress(emit, value) {
        if (emit) {
            if (!value) {
                this._serviceActivatorCallInProgressDetails$.next([]);
            }
            this.serviceActivatorCallInProgress$.next(value);
        }
    }
    /**
     * Check if the status of the error its present on the "serviceActivatorErrorHandlingExcludeStatusCodes"
     * @param error An HttpError
     * @param serviceActivatorConfig The configuration of the service activator
     */
    isStatusExcluded(error, serviceActivatorConfig) {
        var _a;
        return Boolean((_a = serviceActivatorConfig === null || serviceActivatorConfig === void 0 ? void 0 : serviceActivatorConfig.serviceActivatorErrorHandlingExcludeStatusCodes) === null || _a === void 0 ? void 0 : _a.includes(error.status));
    }
    areServiceActivatorsEqual(serviceActivator1, serviceActivator2) {
        return serviceActivator1.globalConfigID === serviceActivator2.globalConfigID && serviceActivator1.path === serviceActivator2.path;
    }
}
PfeServiceActivatorService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeServiceActivatorService, deps: [{ token: NGX_PFE_CONFIGURATION }, { token: i1.HttpClient }, { token: PfeStateService }, { token: PfeNavigationUtilService }, { token: PfeConfigurationService }, { token: i3.NgxLoggerService }, { token: PfeActionsService }, { token: PfeTriggerServiceActivatorsService }], target: i0.ɵɵFactoryTarget.Injectable });
PfeServiceActivatorService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeServiceActivatorService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeServiceActivatorService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [NGX_PFE_CONFIGURATION]
                    }] }, { type: i1.HttpClient }, { type: PfeStateService }, { type: PfeNavigationUtilService }, { type: PfeConfigurationService }, { type: i3.NgxLoggerService }, { type: PfeActionsService }, { type: PfeTriggerServiceActivatorsService }];
    } });

/**
 * The PfeNavigationService is responsible to determine the next page to be displayed
 * depending on the configuration and the current values in the state.
 *
 * @export
 */
class PfeNavigationService {
    constructor(router, pfeStateService, pfeConfigService, pfeServiceActivatorService, pfeNavigationUtilService, pfeActionsService, logger) {
        this.router = router;
        this.pfeStateService = pfeStateService;
        this.pfeConfigService = pfeConfigService;
        this.pfeServiceActivatorService = pfeServiceActivatorService;
        this.pfeNavigationUtilService = pfeNavigationUtilService;
        this.pfeActionsService = pfeActionsService;
        this.logger = logger;
        this.PREVENT_REPEATED_NAVIGATION_DURATION = 300;
        /**
         * Set true, while throttling navigation events
         */
        this.throttledActive = false;
        /**
         * Set to true, when a navigation or network call (service activator call) is currently running.
         */
        this.navigationInProgress$ = new BehaviorSubject(false);
        this.currentPageId$ = this.pfeNavigationUtilService.currentPageId$;
        /**
         * Determines if the current page is considered valid.
         * If it is, navigation is allowed. This not only affects the next/back button
         * but also the browser navigation.
         */
        this.pageStatus$ = new BehaviorSubject(false);
        /**
         * Will be set to true, when the state handling has been shutdown within a flow.
         */
        // TODO: Move to state service:
        this.stateStorageIsShutdown = false;
        // Even though router events do not differentiate between primary and secondary outlets
        // it is possible to subscribe to the ChildActivationEnd events.
        // These contain the ActivatedRouteSnapshot, which allows to filter out all non-pfe routes.
        this.router.events
            .pipe(filter((event) => event instanceof ChildActivationEnd), map((event) => {
            const childEvent = event;
            return getPageIDFromRouteSnapshot(childEvent.snapshot);
        }), filter((pageID) => pageID != null), distinctUntilChanged())
            .subscribe((pageID) => {
            (() => __awaiter(this, void 0, void 0, function* () {
                const pageNavConfig = yield this.pfeConfigService.getPageNavigationConfiguration(pageID);
                if (pageNavConfig && !this.getSkipLocationChangeSetting(pageNavConfig) && pageID) {
                    this.pushPageToHistory(pageID);
                }
            }))();
            this.pfeNavigationUtilService.currentPageId$.next(pageID);
        });
    }
    /**
     * Navigate to the next page.
     * Also triggers all actions/service activators, etc...
     * {@link PfeNavigationService.navigate}
     */
    navigateNext(doNotActuallyNavigate) {
        if (this.throttledActive) {
            return Promise.resolve(undefined);
        }
        this.throttledActive = true;
        setTimeout(() => {
            this.throttledActive = false;
        }, this.PREVENT_REPEATED_NAVIGATION_DURATION);
        return this._navigateNext(doNotActuallyNavigate);
    }
    _navigateNext(doNotActuallyNavigate) {
        return __awaiter(this, void 0, void 0, function* () {
            const currentPageNavCfg = yield this.pfeConfigService.getPageNavigationConfiguration(this.currentPageId$.value);
            if (currentPageNavCfg && currentPageNavCfg.nextOptionList) {
                this.navigationInProgress$.next(true);
                try {
                    const actionsResult = yield this.navigateNextActions(currentPageNavCfg);
                    if (!actionsResult) {
                        this.navigationInProgress$.next(false);
                        return undefined;
                    }
                    let nextPageId;
                    const nextPageOption = yield this.evaluateNavOptions(currentPageNavCfg.nextOptionList);
                    if (this.pfeNavigationUtilService.navigateExternal(nextPageOption)) {
                        return undefined;
                    }
                    if (nextPageOption === null || nextPageOption === void 0 ? void 0 : nextPageOption.nextPageId) {
                        nextPageId = nextPageOption === null || nextPageOption === void 0 ? void 0 : nextPageOption.nextPageId;
                        const navToUpdate = this.determineUpdateStateConfig(nextPageId, currentPageNavCfg.nextOptionList);
                        /** @todo DEPRECATED, will be remove after the implementation of PFE Actions */
                        if (navToUpdate === null || navToUpdate === void 0 ? void 0 : navToUpdate.updateStateValues) {
                            this.pfeStateService.updateStateValues(navToUpdate.updateStateValues);
                        }
                    }
                    if (nextPageId) {
                        let pageIdsToRemove = [];
                        pageIdsToRemove = currentPageNavCfg.nextOptionList
                            .filter((page) => page.nextPageId &&
                            page.nextPageId !== nextPageId &&
                            Object.prototype.hasOwnProperty.call(this.pfeStateService._staleState.pages, page.nextPageId))
                            .map((page) => page.nextPageId);
                        this.pfeStateService.analyzePageForStale({
                            pageId: nextPageId,
                            removePageFromState: pageIdsToRemove,
                        });
                        return yield this.navigate(nextPageId, doNotActuallyNavigate);
                    }
                    else {
                        this.navigationInProgress$.next(false);
                        this.logger.warnToServer('Next navigation options config did not lead to a result: ', currentPageNavCfg);
                    }
                }
                catch (e) {
                    this.navigationInProgress$.next(false);
                    // Error handling is done in httpErrorHandler
                    return undefined;
                }
            }
        });
    }
    /**
     * Execute all the actions related with the "onPageLeave*":
     * - Execute the onPageLeaveActions
     * - Execute the onPageLeaveServiceActivators
     * - Check for a new error key.
     * @throws Error if an error happens on the service activators or the actions.
     * Also rejects if the "errorMessage" is present
     * @param currentPageNavCfg The page configuration.
     * @returns A promise that signals if all actions returned a positive status.
     */
    navigateNextActions(currentPageNavCfg) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!currentPageNavCfg) {
                return true;
            }
            // clean error message data on navigation so message is not still displayed
            this.pfeStateService.storeValue(yield this.pfeConfigService.getErrorMessageKey(), undefined);
            if (currentPageNavCfg.onPageLeaveActions) {
                const actionsResult = yield this.pfeActionsService.executeActions(currentPageNavCfg.onPageLeaveActions, (notBusy) => this.navigationInProgress$.next(!notBusy));
                if (!actionsResult) {
                    return false;
                }
            }
            /** @todo DEPRECATED, will be remove after the implementation of PFE Actions */
            if (currentPageNavCfg.onPageLeaveServiceActivators) {
                yield this.pfeServiceActivatorService.spreadWithGlobalServiceActivatorConfig(currentPageNavCfg.onPageLeaveServiceActivators);
                yield this.pfeServiceActivatorService.handleServiceActivators(currentPageNavCfg.onPageLeaveServiceActivators, currentPageNavCfg.executeServiceActivatorsSync);
            }
            const errorMessageKey = yield this.pfeConfigService.getErrorMessageKey();
            const errorMessage = this.pfeStateService.getValue(errorMessageKey);
            if (errorMessage) {
                return Promise.reject(undefined);
            }
            return true;
        });
    }
    /**
     * Navigate to the previous page in the flow.
     * {@link PfeNavigationService.navigate}
     */
    navigateBack(doNotActuallyNavigate) {
        if (this.throttledActive) {
            return Promise.resolve(undefined);
        }
        this.throttledActive = true;
        setTimeout(() => {
            this.throttledActive = false;
        }, this.PREVENT_REPEATED_NAVIGATION_DURATION);
        return this._navigateBack(doNotActuallyNavigate);
    }
    _navigateBack(doNotActuallyNavigate) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            const currentPage = this.currentPageId$.value;
            const currentPageNavCfg = yield this.pfeConfigService.getPageNavigationConfiguration(currentPage);
            let nextPageId;
            if (currentPageNavCfg) {
                this.navigationInProgress$.next(true);
                try {
                    const actionsResult = yield this.navigateBackActions(currentPageNavCfg);
                    if (!actionsResult) {
                        this.navigationInProgress$.next(false);
                        return undefined;
                    }
                }
                catch (error) {
                    this.navigationInProgress$.next(false);
                    // Error handling is done in httpErrorHandler for service activators and in the actions for actions.
                    return undefined;
                }
                if (currentPageNavCfg.backOptionList) {
                    const backPageOption = yield this.evaluateNavOptions(currentPageNavCfg.backOptionList);
                    if (this.pfeNavigationUtilService.navigateExternal(backPageOption)) {
                        return undefined;
                    }
                    if (backPageOption === null || backPageOption === void 0 ? void 0 : backPageOption.nextPageId) {
                        nextPageId = backPageOption === null || backPageOption === void 0 ? void 0 : backPageOption.nextPageId;
                    }
                    const navToUpdate = this.determineUpdateStateConfig(nextPageId, currentPageNavCfg.backOptionList);
                    if (navToUpdate === null || navToUpdate === void 0 ? void 0 : navToUpdate.updateStateValues) {
                        this.pfeStateService.updateStateValues(navToUpdate.updateStateValues);
                    }
                }
            }
            // There was no backOptionList or no result. What we're gonna do right here is go back, way back, back into time
            // Also: If there is an empty backOptionList configured, no backwards navigation is done and the fallback is
            // determined with the backOptionListFallBackToHistory flag
            if (!nextPageId) {
                if (!(currentPageNavCfg === null || currentPageNavCfg === void 0 ? void 0 : currentPageNavCfg.backOptionList) ||
                    (((_a = currentPageNavCfg === null || currentPageNavCfg === void 0 ? void 0 : currentPageNavCfg.backOptionList) === null || _a === void 0 ? void 0 : _a.length) !== 0 &&
                        ((_c = (_b = (yield this.pfeConfigService.getAppConfiguration())) === null || _b === void 0 ? void 0 : _b.pfeConfig) === null || _c === void 0 ? void 0 : _c.backOptionListFallBackToHistory))) {
                    const pageIdFromHistory = this.getPreviousPageOnHistory(currentPageNavCfg === null || currentPageNavCfg === void 0 ? void 0 : currentPageNavCfg.omitFromHistory);
                    if (pageIdFromHistory) {
                        nextPageId = pageIdFromHistory;
                    }
                }
            }
            if (nextPageId) {
                // start staleState analysis for back button trigger
                if (currentPage) {
                    this.pfeStateService.analyzePageForStale({
                        pageId: nextPageId,
                        removePageFromState: [currentPage],
                    });
                }
                return yield this.navigate(nextPageId, doNotActuallyNavigate);
            }
            else {
                this.navigationInProgress$.next(false);
                this.logger.warnToServer('Back navigation options config did not lead to a result: ', currentPageNavCfg);
            }
        });
    }
    /**
     * Execute all the actions related with the "onPageLeaveBackwardsActions":
     * - Execute the onPageLeaveBackwardsActions
     * @throws Error if an error happens on the actions.
     * @param currentPageNavCfg The page configuration.
     * @returns A promise that signals if all actions returned a positive status.
     */
    navigateBackActions(currentPageNavCfg) {
        return __awaiter(this, void 0, void 0, function* () {
            // clean error message data on navigation so message is not still displayed
            this.pfeStateService.storeValue(yield this.pfeConfigService.getErrorMessageKey(), undefined);
            if (currentPageNavCfg.onPageLeaveBackwardsActions) {
                const actionsResult = yield this.pfeActionsService.executeActions(currentPageNavCfg.onPageLeaveBackwardsActions);
                return actionsResult;
            }
            return true;
        });
    }
    /**
     * Navigates directly to a specific page.
     * Does not execute the onPageLeave service activators.
     * Does not execute state navigation updates
     *
     * @param nextPage The id of the page to navigate to
     * @param doNotActuallyNavigate Do everything that is related to a navigation,
     * but do not trigger the actual navigation. Usecase: The actual navigation is done by some other
     * mechanism. For example the browser back button
     */
    navigate(nextPage, doNotActuallyNavigate) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!nextPage) {
                this.logger.errorToServer('Navigation was triggered without a pageID. Navigating nowhere...');
                return;
            }
            if (nextPage !== this.currentPageId$.value) {
                const nextPageConfig = yield this.pfeConfigService.getPageConfiguration(nextPage);
                const nextPageConfigNavConfig = yield this.pfeConfigService.getPageNavigationConfiguration(nextPage);
                if (nextPageConfig) {
                    if (!doNotActuallyNavigate) {
                        yield this.navigateRelative(nextPage, {
                            skipLocationChange: this.getSkipLocationChangeSetting(nextPageConfigNavConfig),
                        });
                    }
                }
                else {
                    this.logger.errorToServer(`No configuration for page: ${nextPage}`);
                    yield this.navigateToErrorPage();
                }
                return nextPageConfig;
            }
        });
    }
    /**
     * Navigate to a given pageId
     * @param pageId The page id
     * @param triggerLeaveActions If should trigger the page leave actions
     */
    navigateToPageId(pageId, triggerLeaveActions, doNotActuallyNavigate) {
        return __awaiter(this, void 0, void 0, function* () {
            if (triggerLeaveActions) {
                const currentPageNavCfg = yield this.pfeConfigService.getPageNavigationConfiguration(this.currentPageId$.value);
                const actionsResult = yield this.navigateNextActions(currentPageNavCfg);
                if (!actionsResult) {
                    return undefined;
                }
            }
            return yield this.navigate(pageId, doNotActuallyNavigate);
        });
    }
    getSkipLocationChangeSetting(pageNavConfig) {
        let skipLocationChangeSetting = false;
        if (pageNavConfig && pageNavConfig.omitFromHistory === true) {
            skipLocationChangeSetting = true;
        }
        return skipLocationChangeSetting;
    }
    /**
     * Updates the state on a remote backend or the session storage.
     * Checks if the automatic state storage is still active and doesn't do anything if it is shutdown.
     */
    updateStoredState(navConfig) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.stateStorageIsShutdown || (navConfig && navConfig.shutdownStateStorage)) {
                this.stateStorageIsShutdown = true;
                return;
            }
            yield this.pfeStateService.storeState();
        });
    }
    /**
     * @deprecated Please switch to updateStoredState() and handle the storage of the last visited page on the calling side.
     */
    updateStateOnBackend(currentPageName, navConfig) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!navConfig || (navConfig && !navConfig.omitFromHistory)) {
                this.pfeStateService.storeValue(LAST_VISITED_PAGE_STATE_KEY, currentPageName);
            }
            this.updateStoredState(navConfig);
        });
    }
    pushPageToHistory(pageID) {
        let history = this.pfeStateService.getValue(PFE_HISTORY_KEY), visitedPages = this.pfeStateService.getValue(PFE_VISITED_KEY);
        if (!history) {
            history = new Array();
        }
        if (!visitedPages) {
            visitedPages = new Array();
        }
        // TODO: Check if the current top page is the same, only add if not.
        // the page is not added if it is already there
        if (visitedPages.indexOf(pageID) === -1) {
            const lastVisited = visitedPages[visitedPages.length - 1], historyLast = history[history.length - 1];
            // if it the previous page is the same adding it at the end
            // if not adding it to after history last one index
            if (lastVisited === historyLast) {
                visitedPages.push(pageID);
            }
            else {
                const lastPageIndex = visitedPages.indexOf(historyLast);
                visitedPages.splice(lastPageIndex + 1, 0, pageID);
            }
        }
        history.push(pageID);
        this.pfeStateService.storeValue(PFE_HISTORY_KEY, history);
        this.pfeStateService.storeValue(PFE_VISITED_KEY, visitedPages);
    }
    popPageFromHistory(omitFromHistory) {
        const history = this.pfeStateService.getValue(PFE_HISTORY_KEY);
        if (history && history.length >= 1) {
            // If the current page was not added to the history, we don't need to skip it when we're going back:
            const goBackNumber = omitFromHistory === true ? 1 : 2;
            const page = history.splice(history.length - goBackNumber, goBackNumber)[0];
            this.pfeStateService.storeValue(PFE_HISTORY_KEY, history);
            return page;
        }
    }
    getPreviousPageOnHistory(omitFromHistory = false) {
        const history = this.pfeStateService.getValue(PFE_HISTORY_KEY);
        if (!history || history.length === 0) {
            return;
        }
        // if the current page is the only one in the history, then there is no known previous page
        const currentPageIsOnlyHistoryEntry = history.length === 1 && !omitFromHistory;
        if (currentPageIsOnlyHistoryEntry) {
            return;
        }
        // If the current page was not added to the history, we don't need to skip it when we're going back:
        const goBackNumber = omitFromHistory ? 1 : 2;
        return history[history.length - goBackNumber];
    }
    /**
     * Determine the next page to be displayed.
     *
     * @param pageNavConfig It can be either a pageNavConfig or a first page configuration.
     * @param httpErrorHandler
     * @returns The next page.
     * @throws Exception, if a sync service activator call fails.
     */
    getNextPage(pageNavConfig) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            return (_a = (yield this.evaluateNavOptions(pageNavConfig.nextOptionList))) === null || _a === void 0 ? void 0 : _a.nextPageId;
        });
    }
    getNextPageOption(pageNavConfig) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.evaluateNavOptions(pageNavConfig.nextOptionList);
        });
    }
    getPreviousPage(pageNavConfig) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            return (_a = (yield this.evaluateNavOptions(pageNavConfig.backOptionList))) === null || _a === void 0 ? void 0 : _a.nextPageId;
        });
    }
    getPreviousPageOption(pageNavConfig) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.evaluateNavOptions(pageNavConfig.backOptionList);
        });
    }
    /**
     * Return the following page to be displayed. This is independent of next or back.
     *
     * @param pageNavConfig The page navigation configuration.
     * @returns The ID of the next page to be displayed. Undefined, if no next page could be determined.
     */
    evaluateNavOptions(pageNavConfig) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (pageNavConfig) {
                const state = this.pfeStateService.getFullState();
                const navOptionConfig = this.pfeNavigationUtilService.determineNextPage(pageNavConfig, state);
                if (navOptionConfig) {
                    if ((_a = navOptionConfig === null || navOptionConfig === void 0 ? void 0 : navOptionConfig.metaNavigation) === null || _a === void 0 ? void 0 : _a.metaNavId) {
                        return Object.assign(Object.assign({}, navOptionConfig), { nextPageId: yield this.getFirstPage() });
                    }
                    else {
                        return navOptionConfig;
                    }
                }
            }
        });
    }
    // Forward some calls to the util service. This is done to keep the public API consistent.
    navigateToErrorPage(errorResponse) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.pfeNavigationUtilService.navigateToGlobalErrorPage(errorResponse);
        });
    }
    /**
     * Tries to get the first page. If that fails, the error page is returned.
     * This is mostly useful during startup of the application. Instead of the first page,
     * the error page is simply displayed.
     */
    getFirstPageOrErrorPageOption() {
        return __awaiter(this, void 0, void 0, function* () {
            let firstPage;
            let error;
            try {
                firstPage = yield this.getFirstPageOption(true);
            }
            catch (e) {
                error = e;
            }
            if (!firstPage) {
                return this.isHttpErrorResponse(error)
                    ? yield this.pfeNavigationUtilService.getGlobalErrorPageOption(error)
                    : yield this.pfeNavigationUtilService.getGlobalErrorPageOption();
            }
            return firstPage;
        });
    }
    getFirstPage(disableErrorPageNavigation) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            return (_a = (yield this.getFirstPageOption(disableErrorPageNavigation))) === null || _a === void 0 ? void 0 : _a.nextPageId;
        });
    }
    getFirstPageOption(disableErrorPageNavigation) {
        return __awaiter(this, void 0, void 0, function* () {
            const navConfiguration = (yield this.pfeConfigService.getConfig()).navConfiguration;
            const firstPageConfig = navConfiguration.firstPage || undefined;
            let firstPage;
            if (firstPageConfig) {
                if (this.isFirstPageConfig(firstPageConfig)) {
                    if (firstPageConfig.onEnterActions) {
                        try {
                            const actionsResult = yield this.pfeActionsService.executeActions(firstPageConfig.onEnterActions);
                            if (!actionsResult) {
                                return undefined;
                            }
                        }
                        catch (error) {
                            // Error handling of the service activators is done in showErrorPage
                            if (disableErrorPageNavigation) {
                                throw error;
                            }
                        }
                    }
                    /** @todo DEPRECATED, will be remove after the implementation of PFE Actions */
                    if (firstPageConfig.onEnterServiceActivators) {
                        try {
                            yield this.pfeServiceActivatorService.spreadWithGlobalServiceActivatorConfig(firstPageConfig.onEnterServiceActivators);
                            let firstPageServiceActivators;
                            if (disableErrorPageNavigation) {
                                // 1. Clone the config, we don't want to pollute it...
                                firstPageServiceActivators = clone(firstPageConfig.onEnterServiceActivators);
                                // 2. Set the _firstPageServiceActivator flag to disable the standard error handling
                                // Error navigation has to be done by the caller
                                firstPageServiceActivators.forEach((serviceActivator) => {
                                    serviceActivator._disableErrorPageNavigation = true;
                                });
                            }
                            else {
                                firstPageServiceActivators = firstPageConfig.onEnterServiceActivators;
                            }
                            yield this.pfeServiceActivatorService.handleServiceActivators(firstPageServiceActivators);
                        }
                        catch (error) {
                            // Error handling of the service activators is done in showErrorPage
                            if (disableErrorPageNavigation) {
                                throw error;
                            }
                        }
                    }
                    firstPage = yield this.getNextPageOption(firstPageConfig);
                }
                else {
                    firstPage = { nextPageId: firstPageConfig };
                }
            }
            if (!firstPage) {
                firstPage = yield this.pfeNavigationUtilService.getFirstPageOption();
            }
            // register the firstPage
            if (firstPage === null || firstPage === void 0 ? void 0 : firstPage.nextPageId) {
                this.pfeStateService.analyzePageForStale({
                    pageId: firstPage === null || firstPage === void 0 ? void 0 : firstPage.nextPageId,
                });
            }
            return firstPage;
        });
    }
    getErrorPage(errorResponse) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.pfeNavigationUtilService.getErrorPage(errorResponse);
        });
    }
    navigateRelative(pageID, extras) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.pfeNavigationUtilService.navigateRelative(pageID, extras);
        });
    }
    isFirstPageConfig(pageConfig) {
        if (pageConfig && pageConfig.nextOptionList !== undefined) {
            return true;
        }
        else {
            return false;
        }
    }
    isHttpErrorResponse(error) {
        return error instanceof HttpErrorResponse;
    }
    /**
     * This method determine which is the navigation config of a give key
     * @param key page to be search
     * @param pageNavConfig list with all the possible configs
     */
    determineUpdateStateConfig(key, pageNavConfig) {
        if (!pageNavConfig || pageNavConfig.length === 0) {
            return undefined;
        }
        return pageNavConfig.find((nav) => nav.nextPageId === key);
    }
}
PfeNavigationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeNavigationService, deps: [{ token: i1$1.Router }, { token: PfeStateService }, { token: PfeConfigurationService }, { token: PfeServiceActivatorService }, { token: PfeNavigationUtilService }, { token: PfeActionsService }, { token: i3.NgxLoggerService }], target: i0.ɵɵFactoryTarget.Injectable });
PfeNavigationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeNavigationService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeNavigationService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.Router }, { type: PfeStateService }, { type: PfeConfigurationService }, { type: PfeServiceActivatorService }, { type: PfeNavigationUtilService }, { type: PfeActionsService }, { type: i3.NgxLoggerService }]; } });

/**
 * Adapter for different translate tools/libraries.
 * An implementation of a translate tool/library adapter has to implement this service "interface".
 */
class PFETranslateService {
    /**
     * Returns the translation of a key.
     */
    get(key) {
        return of(key);
    }
    /**
     * Returns an instance of the translate pipe instance.
     * If a translation tool/library already provides a translate pipe, this can simply be returned.
     * Otherwise a "proxy" pipe can be used.
     * If undefined is returned, no translation will be applied.
     */
    getTranslatePipeInstance(_ref) {
        return undefined;
    }
}
PFETranslateService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PFETranslateService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
PFETranslateService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PFETranslateService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PFETranslateService, decorators: [{
            type: Injectable
        }] });

const PAGE_CONFIG_ROUTE_ATTRIBUTE_NAME = 'pageConfig';
/**
 * The PfeBusinessService provides functionality, that can be used in the pages to retrieve and store data
 * in the state and to access other parts of the application.
 *
 * @export
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
class PfeBusinessService {
    constructor(pfeConfigService, pfeStateService, pfeNavService, pfeServiceActivatorService, pfeTranslateService, logger) {
        this.pfeConfigService = pfeConfigService;
        this.pfeStateService = pfeStateService;
        this.pfeNavService = pfeNavService;
        this.pfeServiceActivatorService = pfeServiceActivatorService;
        this.pfeTranslateService = pfeTranslateService;
        this.logger = logger;
        this.componentDestroyed$ = new Subject();
        this._errorMessageSubject$ = new BehaviorSubject(undefined);
        this._errorMessage$ = this._errorMessageSubject$.asObservable();
        this.errorMessageDisplayInitialized = false;
        this.currentPageId$ = this.pfeNavService.currentPageId$;
        this.pageStatus$ = this.pfeNavService.pageStatus$;
        /**
         * Signals if the pfe is busy with:
         *
         * - A service activator call
         * - An action that is tied to a navigation
         * - A navigation itself
         *
         * For example, this can be used to show a loading animation in an app for those events.
         */
        this.busy$ = combineLatest([
            this.pfeServiceActivatorService.serviceActivatorCallInProgress$,
            this.pfeNavService.navigationInProgress$,
        ]).pipe(map(([serviceActivator, navigation]) => serviceActivator || navigation));
        this.serviceActivatorCallInProgressDetails$ = this.pfeServiceActivatorService.serviceActivatorCallInProgressDetails$;
    }
    /**
     * @deprecated This will be moved to a separate optional service
     */
    get errorMessage$() {
        if (!this.errorMessageDisplayInitialized) {
            this.initializeValidationDisplay();
            this.errorMessageDisplayInitialized = true;
        }
        return this._errorMessage$;
    }
    ngOnDestroy() {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }
    initializeValidationDisplay() {
        return __awaiter(this, void 0, void 0, function* () {
            const errorMessageKey = yield this.pfeConfigService.getErrorMessageKey();
            // set initial state of the error key to undefined
            this.storeValue(errorMessageKey, undefined);
            this.getObservableForKey(errorMessageKey, true)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((message) => {
                if (message) {
                    this.pfeTranslateService
                        .get(message)
                        .pipe(first())
                        .subscribe((translatedMessage) => {
                        this._errorMessageSubject$.next(translatedMessage);
                    });
                }
                else {
                    this._errorMessageSubject$.next(undefined);
                }
            });
        });
    }
    /**
     * Set to true, when a navigation or network call (service activator call) is currently running.
     */
    get serviceActivatorCallInProgress$() {
        return this.pfeServiceActivatorService.serviceActivatorCallInProgress$;
    }
    hasConfig() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.pfeConfigService.getConfig()) !== undefined;
        });
    }
    getErrorPage(errorResponse) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.pfeNavService.getErrorPage(errorResponse);
        });
    }
    getFirstPage() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.pfeNavService.getFirstPage();
        });
    }
    /**
     * @deprecated true
     * @deprecationMessage Use getFirstPage() instead.
     */
    goBackToStart() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.pfeNavService.getFirstPage();
        });
    }
    navigateNext(ignorePageStatus) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!ignorePageStatus && !this.pfeNavService.pageStatus$.value) {
                return;
            }
            return this.pfeNavService.navigateNext();
        });
    }
    navigateBack() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.pfeNavService.navigateBack();
        });
    }
    navigateToPage(pageId, triggerLeaveActions) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.pfeNavService.navigateToPageId(pageId, triggerLeaveActions);
        });
    }
    navigateToErrorPage(errorResponse) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.pfeNavService.navigate(yield this.pfeNavService.getErrorPage(errorResponse));
        });
    }
    getNextPage(pageNavConfig) {
        return __awaiter(this, void 0, void 0, function* () {
            const pageID = yield this.pfeNavService.getNextPage(pageNavConfig);
            return pageID;
        });
    }
    getPreviousPage(pageNavConfig) {
        return __awaiter(this, void 0, void 0, function* () {
            const pageID = yield this.pfeNavService.getPreviousPage(pageNavConfig);
            return pageID;
        });
    }
    getPageConfiguration(pageID) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.pfeConfigService.getPageConfiguration(pageID);
        });
    }
    /**
     * Sets the validity status of the current page.
     * When a page is opened it is considered invalid.
     * This will prevent the navigation.
     * Once the page is valid, navigation will be allowed.
     */
    setPageStatus(pageStatus) {
        this.pfeNavService.pageStatus$.next(pageStatus);
    }
    /**
     * {@link PfeStateService.storeUserInput}
     *
     * @deprecated true
     * @deprecationMessage Use storeValue instead
     */
    storeUserInput(key, value) {
        this.pfeStateService.storeValue(key, value);
    }
    /**
     * {@link PfeStateService.storeUserInput}
     *
     * @param key
     * @param value
     */
    storeValue(key, value) {
        this.pfeStateService.storeValue(key, value);
    }
    /**
     * @deprecated true
     * @deprecationMessage Use storeValueByExpression instead
     */
    storeUserInputByExpression(expression, value) {
        this.pfeStateService.storeValueByExpression(expression, value);
    }
    /**
     * {@link PfeStateService.storeUserInputByExpression}
     *
     * @param key
     * @param value
     */
    storeValueByExpression(expression, value) {
        this.pfeStateService.storeValueByExpression(expression, value);
    }
    /**
     * @deprecated true
     * @deprecationMessage Use getValue instead
     */
    getUserInput(key) {
        return this.pfeStateService.getValue(key);
    }
    /**
     * Returns previously stored data by its key.
     *
     * @param key
     */
    getValue(key) {
        return this.pfeStateService.getValue(key);
    }
    /**
     * {@link PfeStateService.getObservableForKey}
     *
     * @param key
     */
    getObservableForKey(key, triggerForUndefined, disableDeepEqual) {
        return this.pfeStateService.getObservableForKey(key, triggerForUndefined, disableDeepEqual);
    }
    /**
     * {@link PfeStateService.getObservableForExpressionKey}
     *
     * @param key
     */
    getObservableForExpressionKey(expression, triggerForUndefined, disableDeepEqual) {
        return this.pfeStateService.getObservableForExpressionKey(expression, triggerForUndefined, disableDeepEqual);
    }
    /**
     * @deprecated true
     * @deprecationMessage Use getValueByExpression instead
     */
    getStateValueByExpression(jsonPathExpression) {
        return this.getValueByExpression(jsonPathExpression);
    }
    getValueByExpression(jsonPathExpression) {
        return this.pfeStateService.getStateValueByExpression(jsonPathExpression);
    }
    restoreState() {
        return this.pfeStateService.automaticallyRestoreState();
    }
    getFullState() {
        return this.pfeStateService.getFullState();
    }
    /**
     * Triggers the service activators for a given page and action.
     *
     * {string} pageId the pageId whose service activators will be triggered
     * {ServiceActivatorHook} hook triggers onEnter or onLeave service activators, default is onEnter.
     */
    triggerServiceActivators(pageId, hook) {
        return __awaiter(this, void 0, void 0, function* () {
            const pageConfig = yield this.pfeConfigService.getPageNavigationConfiguration(pageId);
            if (!pageConfig) {
                return;
            }
            try {
                const serviceActivators = hook === ServiceActivatorHook.LEAVE ? pageConfig.onPageLeaveServiceActivators : pageConfig.onPageEnterServiceActivators;
                if (serviceActivators) {
                    yield this.pfeServiceActivatorService.spreadWithGlobalServiceActivatorConfig(serviceActivators);
                    yield this.pfeServiceActivatorService.handleServiceActivators(serviceActivators, pageConfig.executeServiceActivatorsSync);
                    yield this.pfeServiceActivatorService.waitForAsyncServiceActivators(pageConfig.pageId);
                }
                else {
                    throw new Error('Could not determine service activators to be triggered.');
                }
            }
            catch (error) {
                // Error handling is done in showErrorPage
            }
        });
    }
    /**
     * Triggers a specific service activator, that can either be from the configuration of the
     * current page or the global config.
     *
     * It returns the HttpResponse in a Promise for service activators that are NOT marked as async (flow async)
     * Error responses are also returned by rejecting the Promise.
     *
     * It is advisable to use the disableErrorPageNavigation flag for service activators that are called via code.
     * Otherwise the currently active page will be left and a navigation to the error page is done.
     *
     * @param serviceCallId the name of the ServiceActivator to be triggered
     * @param httpErrorHandler an optional custom callback function to receive an information in case any error
     * @returns a Promise with the HttpResponse or HTTPError
     */
    triggerSingleServiceActivator(serviceCallId, httpErrorHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            const pageConfig = yield this.pfeConfigService.getPageNavigationConfiguration(this.pfeNavService.currentPageId$.value);
            const globalConfig = yield this.pfeConfigService.getConfig();
            let serviceActivator;
            if (pageConfig) {
                // So we are inside a page, fine!
                if (pageConfig.serviceActivators && pageConfig.serviceActivators[serviceCallId]) {
                    serviceActivator = pageConfig.serviceActivators[serviceCallId];
                }
                else if (globalConfig.navConfiguration.serviceActivators) {
                    // Ok, so the page has no service activator, lets check in global cfg
                    serviceActivator = globalConfig.navConfiguration.serviceActivators[serviceCallId];
                }
            }
            else {
                return;
            }
            if (!serviceActivator) {
                // eslint-disable-next-line max-len
                this.logger.error(`This serviceCallId "${serviceCallId}" does not exist. Available option that you have from the configuration: `, pageConfig.serviceActivators, globalConfig.navConfiguration.serviceActivators);
                return;
            }
            const serviceActivators = [serviceActivator];
            yield this.pfeServiceActivatorService.spreadWithGlobalServiceActivatorConfig(serviceActivators);
            const response = yield this.pfeServiceActivatorService.handleServiceActivators(serviceActivators, pageConfig.executeServiceActivatorsSync, httpErrorHandler);
            yield this.pfeServiceActivatorService.waitForAsyncServiceActivators(pageConfig.pageId);
            if (Array.isArray(response)) {
                // As only one service activator is triggered, there is only 1 response:
                return response[0];
            }
            else {
                // This will happen if a sequential execution is active
                return response;
            }
        });
    }
    getPageConfigFromRoute(activatedRoute) {
        return activatedRoute.snapshot.data[PAGE_CONFIG_ROUTE_ATTRIBUTE_NAME];
    }
    /**
     * setPageFormIds to stale state
     * @param formIds
     */
    setPageFormIdsToStaleState(formIds) {
        this.pfeStateService.setStateIds(formIds);
    }
}
PfeBusinessService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeBusinessService, deps: [{ token: PfeConfigurationService }, { token: PfeStateService }, { token: PfeNavigationService }, { token: PfeServiceActivatorService }, { token: PFETranslateService }, { token: i3.NgxLoggerService }], target: i0.ɵɵFactoryTarget.Injectable });
PfeBusinessService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeBusinessService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeBusinessService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: PfeConfigurationService }, { type: PfeStateService }, { type: PfeNavigationService }, { type: PfeServiceActivatorService }, { type: PFETranslateService }, { type: i3.NgxLoggerService }]; } });

/**
 * The master page is the central PFE component.
 * All pages are displayed within this component.
 *
 * @export
 */
class PfeMasterPageComponent {
    // TODO: add template (=could be xtra error page with hardcoded config) and default for generic error page, that works without config
    constructor(pfeBusinessService, pfeNavigationService, pfeStateService, pfeConfigService, pfeServiceActivatorService, pfeUtilService, cdr, elementRef, router) {
        this.pfeBusinessService = pfeBusinessService;
        this.pfeNavigationService = pfeNavigationService;
        this.pfeStateService = pfeStateService;
        this.pfeConfigService = pfeConfigService;
        this.pfeServiceActivatorService = pfeServiceActivatorService;
        this.pfeUtilService = pfeUtilService;
        this.cdr = cdr;
        this.elementRef = elementRef;
        this.router = router;
        // Unfortunately, the template context has to be provided in the template as separate objects.
        /**
         * References to templates passed as ng-content
         */
        this.navigationTemplate = null;
        this.errorMessageTemplate = null;
        this.noConfigMessageTemplate = null;
        this.pageOutlet = null;
        this.headerTemplate = null;
        this.footerTemplate = null;
        /**
         * The pageStatus determines if the page is valid.
         * It is used, to prevent navigation in certain cases.
         * For example, when a form is not filled out completely.
         */
        this.pageStatus = false;
        /**
         * Flag, that is set to true, while a navigation or service activator call is active.
         */
        this.navigationOrServiceActivatorInProgress = false;
        this.labelCondition = false;
        this.errorState = false;
        // TODO: Add proper no config error handling:
        this.noConfig = false;
        this.componentDestroyed$ = new Subject();
        this.unSubscribeSubject$ = new Subject();
    }
    ngOnInit() {
        this.navigateCallback = this.navigate.bind(this);
        this.navigateNextCallback = this.nextClicked.bind(this, this.nextClickedCallback);
        this.navigateBackCallback = this.backClicked.bind(this);
        this.pfeNavigationService.currentPageId$.subscribe((pageID) => {
            (() => __awaiter(this, void 0, void 0, function* () {
                this.pageConfig = yield this.pfeConfigService.getPageConfiguration(pageID);
                if (this.pageConfig) {
                    this.updatePageStatus(this.pageConfig);
                }
            }))();
        });
        // Keep the new pageStatus subject state in sync for the master page:
        this.pfeNavigationService.pageStatus$.subscribe((status) => {
            this.pageStatus = status;
            this.cdr.detectChanges();
        });
        this.pfeBusinessService.busy$.subscribe((busy) => {
            this.navigationOrServiceActivatorInProgress = busy;
        });
        this.pfeBusinessService.errorMessage$.subscribe((message) => {
            this.errorMessage = message;
            setTimeout(() => {
                var _a, _b, _c;
                (_c = (_b = (_a = this.elementRef) === null || _a === void 0 ? void 0 : _a.nativeElement) === null || _b === void 0 ? void 0 : _b.scrollIntoView) === null || _c === void 0 ? void 0 : _c.call(_b, { block: 'start', behavior: 'smooth' });
            });
        });
        (() => __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                if (!(yield this.pfeBusinessService.hasConfig())) {
                    this.noConfig = true;
                    this.errorState = true;
                    // TODO: Show generic error page, that works without configuration, should be done in config service.
                    // this.currentPageID = await this.pfeBusinessService.getErrorPage();
                }
                this.appConfiguration = yield this.pfeConfigService.getAppConfiguration();
                this.initializeMasterPageAppConfiguration((_a = this.appConfiguration) === null || _a === void 0 ? void 0 : _a.pfeConfig);
            }
            catch (error) {
                this.noConfig = true;
                this.errorState = true;
                throw error;
            }
        }))();
    }
    initializeMasterPageAppConfiguration(pfeConfig) {
        var _a, _b;
        if (pfeConfig) {
            this.nextButtonLabel = (_a = pfeConfig.nextButtonLabel) !== null && _a !== void 0 ? _a : 'next_button';
            this.backButtonLabel = (_b = pfeConfig.backButtonLabel) !== null && _b !== void 0 ? _b : 'back_button';
        }
    }
    /**
     * Triggered by changes in the navigation.
     */
    updatePageStatus(pageConfig) {
        this.unSubscribeSubject$.next();
        this.checkChangeTextButton(pageConfig);
    }
    nextClicked(nextClickedCallback) {
        return __awaiter(this, void 0, void 0, function* () {
            if (nextClickedCallback) {
                const canNavigate = yield nextClickedCallback();
                if (canNavigate) {
                    this.nextClickedNavigate();
                }
            }
            else {
                this.nextClickedNavigate();
            }
        });
    }
    nextClickedNavigate() {
        return __awaiter(this, void 0, void 0, function* () {
            this.pfeUtilService.trackingEvents$.next({ event: 'navigationButton', value: 'NEXT_BUTTON' });
            if (this.errorState) {
                return;
            }
            yield this.pfeNavigationService.navigateNext();
        });
    }
    backClicked() {
        return __awaiter(this, void 0, void 0, function* () {
            this.pfeUtilService.trackingEvents$.next({ event: 'navigationButton', value: 'BACK_BUTTON' });
            if (this.errorState) {
                return;
            }
            yield this.pfeNavigationService.navigateBack();
        });
    }
    navigate(nextPage) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.pfeNavigationService.navigate(nextPage);
        });
    }
    ngOnDestroy() {
        this.unSubscribeSubject$.next();
        this.unSubscribeSubject$.complete();
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }
    checkChangeTextButton(pageConfig) {
        this.labelCondition = false;
        const btnCondition = pageConfig.nextBtnOptionalLabelCondition;
        if (btnCondition) {
            const propertyKeys = PfeRulesEvaluator.getStatePropertyName(btnCondition);
            propertyKeys.forEach((key) => {
                this.pfeBusinessService
                    .getObservableForKey(key)
                    .pipe(takeUntil(this.unSubscribeSubject$))
                    .subscribe(() => {
                    const result = PfeRulesEvaluator.evaluateConditions(btnCondition, this.pfeStateService.getFullState());
                    if (result !== this.labelCondition) {
                        this.labelCondition = result;
                        this.cdr.detectChanges();
                    }
                });
            });
        }
    }
}
PfeMasterPageComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeMasterPageComponent, deps: [{ token: PfeBusinessService }, { token: PfeNavigationService }, { token: PfeStateService }, { token: PfeConfigurationService }, { token: PfeServiceActivatorService }, { token: PfeUtilService }, { token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i1$1.Router }], target: i0.ɵɵFactoryTarget.Component });
PfeMasterPageComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.1", type: PfeMasterPageComponent, selector: "pfe-master-page", inputs: { nextClickedCallback: "nextClickedCallback" }, queries: [{ propertyName: "navigationTemplate", first: true, predicate: ["navigationTemplate"], descendants: true, read: TemplateRef, static: true }, { propertyName: "errorMessageTemplate", first: true, predicate: ["errorMessageTemplate"], descendants: true, read: TemplateRef, static: true }, { propertyName: "noConfigMessageTemplate", first: true, predicate: ["noConfigMessageTemplate"], descendants: true, read: TemplateRef, static: true }, { propertyName: "pageOutlet", first: true, predicate: ["pageOutlet"], descendants: true, read: TemplateRef, static: true }, { propertyName: "headerTemplate", first: true, predicate: ["headerTemplate"], descendants: true, read: TemplateRef, static: true }, { propertyName: "footerTemplate", first: true, predicate: ["footerTemplate"], descendants: true, read: TemplateRef, static: true }], ngImport: i0, template: "<div *ngIf=\"headerTemplate\">\n  <ng-container\n    *ngTemplateOutlet=\"\n      headerTemplate;\n      context: {\n        appConfiguration: appConfiguration,\n        pageConfig: pageConfig,\n        nextButtonLabel: nextButtonLabel,\n        backButtonLabel: backButtonLabel,\n        navigate: navigateCallback,\n        navigateNext: navigateNextCallback,\n        navigateBack: navigateBackCallback,\n        labelCondition: labelCondition,\n        pageStatus: pageStatus,\n        errorMessage: errorMessage,\n        noConfig: noConfig,\n        navigationOrServiceActivatorInProgress: navigationOrServiceActivatorInProgress\n      }\n    \"\n  ></ng-container>\n</div>\n\n<div *ngIf=\"errorMessageTemplate\">\n  <ng-container\n    *ngTemplateOutlet=\"\n      errorMessageTemplate;\n      context: {\n        appConfiguration: appConfiguration,\n        pageConfig: pageConfig,\n        nextButtonLabel: nextButtonLabel,\n        backButtonLabel: backButtonLabel,\n        navigate: navigateCallback,\n        navigateNext: navigateNextCallback,\n        navigateBack: navigateBackCallback,\n        labelCondition: labelCondition,\n        pageStatus: pageStatus,\n        errorMessage: errorMessage,\n        noConfig: noConfig,\n        navigationOrServiceActivatorInProgress: navigationOrServiceActivatorInProgress\n      }\n    \"\n  ></ng-container>\n</div>\n\n<div *ngIf=\"noConfigMessageTemplate\">\n  <ng-container\n    *ngTemplateOutlet=\"\n      noConfigMessageTemplate;\n      context: {\n        appConfiguration: appConfiguration,\n        pageConfig: pageConfig,\n        nextButtonLabel: nextButtonLabel,\n        backButtonLabel: backButtonLabel,\n        navigate: navigateCallback,\n        navigateNext: navigateNextCallback,\n        navigateBack: navigateBackCallback,\n        labelCondition: labelCondition,\n        pageStatus: pageStatus,\n        errorMessage: errorMessage,\n        noConfig: noConfig,\n        navigationOrServiceActivatorInProgress: navigationOrServiceActivatorInProgress\n      }\n    \"\n  ></ng-container>\n</div>\n\n<div *ngIf=\"pageOutlet\">\n  <ng-container\n    *ngTemplateOutlet=\"\n      pageOutlet;\n      context: {\n        appConfiguration: appConfiguration,\n        pageConfig: pageConfig,\n        nextButtonLabel: nextButtonLabel,\n        backButtonLabel: backButtonLabel,\n        navigate: navigateCallback,\n        navigateNext: navigateNextCallback,\n        navigateBack: navigateBackCallback,\n        labelCondition: labelCondition,\n        pageStatus: pageStatus,\n        errorMessage: errorMessage,\n        noConfig: noConfig,\n        navigationOrServiceActivatorInProgress: navigationOrServiceActivatorInProgress\n      }\n    \"\n  ></ng-container>\n</div>\n\n<div *ngIf=\"navigationTemplate\" [@.disabled]=\"!!pageConfig?.disableAnimations\" [@routeAnimations]=\"pageConfig?.pageId\">\n  <ng-container\n    *ngTemplateOutlet=\"\n      navigationTemplate;\n      context: {\n        appConfiguration: appConfiguration,\n        pageConfig: pageConfig,\n        nextButtonLabel: nextButtonLabel,\n        backButtonLabel: backButtonLabel,\n        navigate: navigateCallback,\n        navigateNext: navigateNextCallback,\n        navigateBack: navigateBackCallback,\n        labelCondition: labelCondition,\n        pageStatus: pageStatus,\n        errorMessage: errorMessage,\n        noConfig: noConfig,\n        navigationOrServiceActivatorInProgress: navigationOrServiceActivatorInProgress\n      }\n    \"\n  ></ng-container>\n</div>\n\n<div *ngIf=\"footerTemplate\">\n  <ng-container\n    *ngTemplateOutlet=\"\n      footerTemplate;\n      context: {\n        appConfiguration: appConfiguration,\n        pageConfig: pageConfig,\n        nextButtonLabel: nextButtonLabel,\n        backButtonLabel: backButtonLabel,\n        navigate: navigateCallback,\n        navigateNext: navigateNextCallback,\n        navigateBack: navigateBackCallback,\n        labelCondition: labelCondition,\n        pageStatus: pageStatus,\n        errorMessage: errorMessage,\n        noConfig: noConfig,\n        navigationOrServiceActivatorInProgress: navigationOrServiceActivatorInProgress\n      }\n    \"\n  ></ng-container>\n</div>\n", styles: [".master-page{padding-bottom:20px}\n"], directives: [{ type: i8.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i8.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }], animations: [fadeIn] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeMasterPageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'pfe-master-page', animations: [fadeIn], template: "<div *ngIf=\"headerTemplate\">\n  <ng-container\n    *ngTemplateOutlet=\"\n      headerTemplate;\n      context: {\n        appConfiguration: appConfiguration,\n        pageConfig: pageConfig,\n        nextButtonLabel: nextButtonLabel,\n        backButtonLabel: backButtonLabel,\n        navigate: navigateCallback,\n        navigateNext: navigateNextCallback,\n        navigateBack: navigateBackCallback,\n        labelCondition: labelCondition,\n        pageStatus: pageStatus,\n        errorMessage: errorMessage,\n        noConfig: noConfig,\n        navigationOrServiceActivatorInProgress: navigationOrServiceActivatorInProgress\n      }\n    \"\n  ></ng-container>\n</div>\n\n<div *ngIf=\"errorMessageTemplate\">\n  <ng-container\n    *ngTemplateOutlet=\"\n      errorMessageTemplate;\n      context: {\n        appConfiguration: appConfiguration,\n        pageConfig: pageConfig,\n        nextButtonLabel: nextButtonLabel,\n        backButtonLabel: backButtonLabel,\n        navigate: navigateCallback,\n        navigateNext: navigateNextCallback,\n        navigateBack: navigateBackCallback,\n        labelCondition: labelCondition,\n        pageStatus: pageStatus,\n        errorMessage: errorMessage,\n        noConfig: noConfig,\n        navigationOrServiceActivatorInProgress: navigationOrServiceActivatorInProgress\n      }\n    \"\n  ></ng-container>\n</div>\n\n<div *ngIf=\"noConfigMessageTemplate\">\n  <ng-container\n    *ngTemplateOutlet=\"\n      noConfigMessageTemplate;\n      context: {\n        appConfiguration: appConfiguration,\n        pageConfig: pageConfig,\n        nextButtonLabel: nextButtonLabel,\n        backButtonLabel: backButtonLabel,\n        navigate: navigateCallback,\n        navigateNext: navigateNextCallback,\n        navigateBack: navigateBackCallback,\n        labelCondition: labelCondition,\n        pageStatus: pageStatus,\n        errorMessage: errorMessage,\n        noConfig: noConfig,\n        navigationOrServiceActivatorInProgress: navigationOrServiceActivatorInProgress\n      }\n    \"\n  ></ng-container>\n</div>\n\n<div *ngIf=\"pageOutlet\">\n  <ng-container\n    *ngTemplateOutlet=\"\n      pageOutlet;\n      context: {\n        appConfiguration: appConfiguration,\n        pageConfig: pageConfig,\n        nextButtonLabel: nextButtonLabel,\n        backButtonLabel: backButtonLabel,\n        navigate: navigateCallback,\n        navigateNext: navigateNextCallback,\n        navigateBack: navigateBackCallback,\n        labelCondition: labelCondition,\n        pageStatus: pageStatus,\n        errorMessage: errorMessage,\n        noConfig: noConfig,\n        navigationOrServiceActivatorInProgress: navigationOrServiceActivatorInProgress\n      }\n    \"\n  ></ng-container>\n</div>\n\n<div *ngIf=\"navigationTemplate\" [@.disabled]=\"!!pageConfig?.disableAnimations\" [@routeAnimations]=\"pageConfig?.pageId\">\n  <ng-container\n    *ngTemplateOutlet=\"\n      navigationTemplate;\n      context: {\n        appConfiguration: appConfiguration,\n        pageConfig: pageConfig,\n        nextButtonLabel: nextButtonLabel,\n        backButtonLabel: backButtonLabel,\n        navigate: navigateCallback,\n        navigateNext: navigateNextCallback,\n        navigateBack: navigateBackCallback,\n        labelCondition: labelCondition,\n        pageStatus: pageStatus,\n        errorMessage: errorMessage,\n        noConfig: noConfig,\n        navigationOrServiceActivatorInProgress: navigationOrServiceActivatorInProgress\n      }\n    \"\n  ></ng-container>\n</div>\n\n<div *ngIf=\"footerTemplate\">\n  <ng-container\n    *ngTemplateOutlet=\"\n      footerTemplate;\n      context: {\n        appConfiguration: appConfiguration,\n        pageConfig: pageConfig,\n        nextButtonLabel: nextButtonLabel,\n        backButtonLabel: backButtonLabel,\n        navigate: navigateCallback,\n        navigateNext: navigateNextCallback,\n        navigateBack: navigateBackCallback,\n        labelCondition: labelCondition,\n        pageStatus: pageStatus,\n        errorMessage: errorMessage,\n        noConfig: noConfig,\n        navigationOrServiceActivatorInProgress: navigationOrServiceActivatorInProgress\n      }\n    \"\n  ></ng-container>\n</div>\n", styles: [".master-page{padding-bottom:20px}\n"] }]
        }], ctorParameters: function () { return [{ type: PfeBusinessService }, { type: PfeNavigationService }, { type: PfeStateService }, { type: PfeConfigurationService }, { type: PfeServiceActivatorService }, { type: PfeUtilService }, { type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i1$1.Router }]; }, propDecorators: { navigationTemplate: [{
                type: ContentChild,
                args: ['navigationTemplate', { read: TemplateRef, static: true }]
            }], errorMessageTemplate: [{
                type: ContentChild,
                args: ['errorMessageTemplate', { read: TemplateRef, static: true }]
            }], noConfigMessageTemplate: [{
                type: ContentChild,
                args: ['noConfigMessageTemplate', { read: TemplateRef, static: true }]
            }], pageOutlet: [{
                type: ContentChild,
                args: ['pageOutlet', { read: TemplateRef, static: true }]
            }], headerTemplate: [{
                type: ContentChild,
                args: ['headerTemplate', { read: TemplateRef, static: true }]
            }], footerTemplate: [{
                type: ContentChild,
                args: ['footerTemplate', { read: TemplateRef, static: true }]
            }], nextClickedCallback: [{
                type: Input
            }] } });

class PfeMasterPageModule {
}
PfeMasterPageModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeMasterPageModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
PfeMasterPageModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeMasterPageModule, declarations: [PfeMasterPageComponent], imports: [CommonModule], exports: [PfeMasterPageComponent] });
PfeMasterPageModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeMasterPageModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeMasterPageModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [PfeMasterPageComponent],
                    exports: [PfeMasterPageComponent],
                }]
        }] });

const APP_BASE_HREF_HASH_ONLY = new InjectionToken('appBaseHrefHashOnly');
/**
 *
 * Provides the possibility to keep search url parameters and to only change the hash.
 *
 * Alternative, to set the base href to a specific value:
 * {provide: APP_BASE_HREF_HASH_ONLY, useValue: '/' + window.location.pathname.split('/')[1] + window.location.search},
 *
 */
class HashOnlyLocationStrategy extends HashLocationStrategy {
    constructor(_platformLocation, _baseHref, _baseHashHref) {
        super(_platformLocation, _baseHref);
        this._baseHashHref = '';
        if (_baseHashHref != null) {
            this._baseHashHref = _baseHashHref;
        }
        else if (_platformLocation) {
            this.__platformLocation = _platformLocation;
        }
    }
    prepareExternalUrl(internal) {
        return this._baseHashHref + '#' + internal;
    }
    getBaseHref() {
        if (this.__platformLocation) {
            this._baseHashHref = '/' + this.__platformLocation.pathname + this.__platformLocation.search;
            if (this._baseHashHref.startsWith('//')) {
                this._baseHashHref = this._baseHashHref.slice(1);
            }
            return this._baseHashHref;
        }
        else {
            return this._baseHashHref;
        }
    }
}
HashOnlyLocationStrategy.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: HashOnlyLocationStrategy, deps: [{ token: i8.PlatformLocation }, { token: APP_BASE_HREF, optional: true }, { token: APP_BASE_HREF_HASH_ONLY, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
HashOnlyLocationStrategy.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: HashOnlyLocationStrategy });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: HashOnlyLocationStrategy, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: i8.PlatformLocation }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [APP_BASE_HREF]
                    }] }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [APP_BASE_HREF_HASH_ONLY]
                    }] }];
    } });

class PfeHeaderInterceptor {
    constructor(pfeUtilService) {
        this.pfeUtilService = pfeUtilService;
    }
    intercept(req, next) {
        req = req.clone({
            setHeaders: Object.assign(Object.assign({}, (this.pfeUtilService.tenant && { tenant: this.pfeUtilService.tenant })), (this.pfeUtilService.applicationId && { 'config-id': this.pfeUtilService.applicationId })),
        });
        return next.handle(req);
    }
}
PfeHeaderInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeHeaderInterceptor, deps: [{ token: PfeUtilService }], target: i0.ɵɵFactoryTarget.Injectable });
PfeHeaderInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeHeaderInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeHeaderInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: PfeUtilService }]; } });

class PfeTranslatePipe {
    constructor(pfeTranslateService, _ref) {
        this.pfeTranslateService = pfeTranslateService;
        this._ref = _ref;
        this.translatePipeInstance = this.pfeTranslateService.getTranslatePipeInstance(this._ref);
    }
    transform(value, args) {
        if (this.translatePipeInstance) {
            return this.translatePipeInstance.transform(value, args);
        }
        else {
            return value;
        }
    }
}
PfeTranslatePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeTranslatePipe, deps: [{ token: PFETranslateService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Pipe });
PfeTranslatePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeTranslatePipe, name: "pfeTranslate", pure: false });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeTranslatePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'pfeTranslate',
                    pure: false,
                }]
        }], ctorParameters: function () { return [{ type: PFETranslateService }, { type: i0.ChangeDetectorRef }]; } });

class PfeTranslateModule {
}
PfeTranslateModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeTranslateModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
PfeTranslateModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeTranslateModule, declarations: [PfeTranslatePipe], exports: [PfeTranslatePipe] });
PfeTranslateModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeTranslateModule, providers: [PFETranslateService] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeTranslateModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [PFETranslateService],
                    declarations: [PfeTranslatePipe],
                    exports: [PfeTranslatePipe],
                }]
        }] });

/**
 * The pfe translate*, are exported to make it possible to implement custom adapters:
 */

class PfeNestedActionsService {
    constructor(pfeActionsService) {
        this.pfeActionsService = pfeActionsService;
        this.executeNestedActions = (actionConfig) => __awaiter(this, void 0, void 0, function* () { return yield this.pfeActionsService.executeActions(actionConfig.actions); });
    }
}
PfeNestedActionsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeNestedActionsService, deps: [{ token: PfeActionsService }], target: i0.ɵɵFactoryTarget.Injectable });
PfeNestedActionsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeNestedActionsService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeNestedActionsService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: PfeActionsService }]; } });

class PfeActionsModule {
}
PfeActionsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeActionsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
PfeActionsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeActionsModule });
PfeActionsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeActionsModule, providers: [
        PfeActionsService,
        PfeUpdateStateValuesService,
        PfeUpdateStateOnBackendService,
        PfeTriggerServiceActivatorsService,
        PfeNestedActionsService,
        PfeRewindHistoryService,
        PfeResetStateService,
    ] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeActionsModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [
                        PfeActionsService,
                        PfeUpdateStateValuesService,
                        PfeUpdateStateOnBackendService,
                        PfeTriggerServiceActivatorsService,
                        PfeNestedActionsService,
                        PfeRewindHistoryService,
                        PfeResetStateService,
                    ],
                }]
        }] });

class PfeEmptyRoutingComponent {
}
PfeEmptyRoutingComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeEmptyRoutingComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
PfeEmptyRoutingComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.1", type: PfeEmptyRoutingComponent, selector: "pfe-empty-routing", ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeEmptyRoutingComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'pfe-empty-routing',
                    template: '',
                }]
        }] });

class PFEPageConfigResolver {
    constructor(router, pfeConfigService) {
        this.router = router;
        this.pfeConfigService = pfeConfigService;
    }
    resolve(activatedRoute, state) {
        var _a;
        if ((_a = activatedRoute === null || activatedRoute === void 0 ? void 0 : activatedRoute.routeConfig) === null || _a === void 0 ? void 0 : _a.path) {
            return this.pfeConfigService.getPageConfiguration(activatedRoute.routeConfig.path);
        }
        else {
            return Promise.resolve(undefined);
        }
    }
}
PFEPageConfigResolver.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PFEPageConfigResolver, deps: [{ token: i1$1.Router }, { token: PfeConfigurationService }], target: i0.ɵɵFactoryTarget.Injectable });
PFEPageConfigResolver.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PFEPageConfigResolver });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PFEPageConfigResolver, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.Router }, { type: PfeConfigurationService }]; } });

class PFEPageRoutingEventsHandlerService {
    constructor(ngxPfeModuleConfiguration, router, pfeServiceActivatorService, pfeConfigService, pfeNavigationService, pfeNavigationUtilService, location, pfeStateService, pfeActionsService, logger) {
        this.ngxPfeModuleConfiguration = ngxPfeModuleConfiguration;
        this.router = router;
        this.pfeServiceActivatorService = pfeServiceActivatorService;
        this.pfeConfigService = pfeConfigService;
        this.pfeNavigationService = pfeNavigationService;
        this.pfeNavigationUtilService = pfeNavigationUtilService;
        this.location = location;
        this.pfeStateService = pfeStateService;
        this.pfeActionsService = pfeActionsService;
        this.logger = logger;
        this.browserBackButtonPressed$ = new BehaviorSubject(undefined);
        this.location.subscribe((event) => {
            if (event.url) {
                const nextPage = event.url.split('/').pop();
                if (nextPage && event.type === 'popstate') {
                    this.browserBackButtonPressed$.next(nextPage);
                }
            }
        });
    }
    canActivate(activatedRoute, state) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const nextPageNavConfig = yield this.pfeConfigService.getPageNavigationConfiguration((_a = activatedRoute === null || activatedRoute === void 0 ? void 0 : activatedRoute.routeConfig) === null || _a === void 0 ? void 0 : _a.path);
            let success = true;
            if (nextPageNavConfig) {
                try {
                    if (nextPageNavConfig.onPageEnterActions) {
                        const actionsResult = yield this.pfeActionsService.executeActions(nextPageNavConfig.onPageEnterActions);
                        if (!actionsResult) {
                            yield this.handleNavigationEnd(false);
                            return false;
                        }
                    }
                }
                catch (error) {
                    // One of the actions failed
                    success = false;
                }
                try {
                    if (nextPageNavConfig.onPageEnterServiceActivators) {
                        yield this.pfeServiceActivatorService.spreadWithGlobalServiceActivatorConfig(nextPageNavConfig.onPageEnterServiceActivators);
                        yield this.pfeServiceActivatorService.handleServiceActivators(nextPageNavConfig.onPageEnterServiceActivators, nextPageNavConfig.executeServiceActivatorsSync);
                    }
                }
                catch (error) {
                    // Synchronous service activator call failed:
                    success = false;
                }
                try {
                    yield this.pfeServiceActivatorService.waitForAsyncServiceActivators(nextPageNavConfig.pageId);
                }
                catch (error) {
                    success = false;
                }
            }
            // Update state values from the page config
            if (nextPageNavConfig === null || nextPageNavConfig === void 0 ? void 0 : nextPageNavConfig.pageId) {
                const config = yield this.pfeConfigService.getPageConfiguration(nextPageNavConfig.pageId);
                if (config === null || config === void 0 ? void 0 : config.updateStateValues) {
                    yield this.pfeStateService.updateStateValues(config.updateStateValues);
                }
            }
            if (nextPageNavConfig) {
                if (!nextPageNavConfig.omitFromHistory) {
                    this.pfeStateService.storeValue(LAST_VISITED_PAGE_STATE_KEY, nextPageNavConfig.pageId);
                }
                if (this.ngxPfeModuleConfiguration.enableAutomaticStateStorage) {
                    this.pfeNavigationService.updateStoredState(nextPageNavConfig);
                }
            }
            // These events were handed with Angular router events in the past
            // But that causes issues with secondary router outlets
            // Handling them here is safe in all situations
            yield this.handleNavigationEnd(success);
            return success;
        });
    }
    /**
     * Determines if a back or forward navigation was triggered by the browser buttons.
     * If this is the case a pfe navigation is triggered.
     */
    canDeactivate(component, currentRoute, currentState, nextState) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            this.handleNavigationStartBegin();
            let allowNavigation = true;
            if (this.browserBackButtonPressed$.value) {
                const nextPageFromBrowserButton = this.browserBackButtonPressed$.value;
                this.browserBackButtonPressed$.next(undefined);
                allowNavigation = yield this.handleUserPressedBrowserButtonNavigation(nextPageFromBrowserButton, currentState);
            }
            if (allowNavigation && nextState) {
                allowNavigation = yield this.runOnNavigationStartActions(nextState);
            }
            if (allowNavigation && nextState) {
                const currentPageNavConfig = yield this.pfeConfigService.getPageNavigationConfiguration(this.pfeNavigationService.currentPageId$.value);
                const nextPreviousPage = this.pfeNavigationService.getPreviousPageOnHistory();
                const shouldPopPageFromHistory = nextPreviousPage === getPageIDFromRouteSnapshot(nextState.root);
                if (shouldPopPageFromHistory) {
                    this.pfeNavigationService.popPageFromHistory(currentPageNavConfig === null || currentPageNavConfig === void 0 ? void 0 : currentPageNavConfig.omitFromHistory);
                }
            }
            const currentNavigationPFEExtras = (_a = this.router.getCurrentNavigation()) === null || _a === void 0 ? void 0 : _a.extras;
            if (!allowNavigation) {
                this.handleNavigationStartCancel();
                return this.preventNavigation(currentState);
                // If a navigation has the error page as target, the navigation itself is successful,
                // but things like cleaning up the error message from the state should still not happen.
            }
            else if (!((_c = (_b = currentNavigationPFEExtras === null || currentNavigationPFEExtras === void 0 ? void 0 : currentNavigationPFEExtras.state) === null || _b === void 0 ? void 0 : _b.pfe) === null || _c === void 0 ? void 0 : _c.navigatingToErrorPage)) {
                yield this.handleNavigationStartSuccess();
            }
            return allowNavigation;
        });
    }
    handleUserPressedBrowserButtonNavigation(nextPageFromBrowserButton, currentState) {
        return __awaiter(this, void 0, void 0, function* () {
            const currentPageID = this.pfeNavigationService.currentPageId$.value;
            const currentPageNavConfig = yield this.pfeConfigService.getPageNavigationConfiguration(currentPageID);
            if (!currentPageNavConfig) {
                // There is no navigation config, we don't know what to do...
                this.logger.warnToServer(`No navigation config for page: ${currentPageID}`);
            }
            let nextPage;
            if (currentPageNavConfig && currentPageNavConfig.nextOptionList) {
                const nextPageOption = yield this.pfeNavigationService.getNextPageOption(currentPageNavConfig);
                // It might be that the next page is actually external. In this case navigate directly:
                if (this.pfeNavigationUtilService.navigateExternal(nextPageOption)) {
                    return false;
                }
                else {
                    nextPage = nextPageOption === null || nextPageOption === void 0 ? void 0 : nextPageOption.nextPageId;
                }
            }
            if (currentPageNavConfig && currentPageNavConfig.backOptionList) {
                const nextPreviousPageOption = yield this.pfeNavigationService.getPreviousPageOption(currentPageNavConfig);
                if (this.pfeNavigationUtilService.navigateExternal(nextPreviousPageOption)) {
                    return false;
                }
            }
            const nextPreviousPage = yield this.getNextPreviousPage(currentPageNavConfig);
            if (nextPage === nextPageFromBrowserButton) {
                // User was navigating forward in the flow by either the browser button or a url change
                // Prevent the navigation forward if the page status is false:
                if (!this.pfeNavigationService.pageStatus$.getValue()) {
                    return false;
                }
                else {
                    const navigationResult = yield this.pfeNavigationService.navigateNext(true);
                    if (!navigationResult) {
                        return false;
                    }
                    return true;
                }
            }
            if (nextPreviousPage === nextPageFromBrowserButton) {
                // User was navigating backward in the flow by either the browser button or a url change
                const navigationResult = yield this.pfeNavigationService.navigateBack(true);
                if (!navigationResult) {
                    return false;
                }
                return true;
            }
            // We were unable to determine, if the user navigated back or forward, prevent the navigation
            return false;
        });
    }
    getNextPreviousPage(currentPageNavConfig) {
        return __awaiter(this, void 0, void 0, function* () {
            let nextPreviousPage;
            if (currentPageNavConfig && currentPageNavConfig.backOptionList) {
                const nextPreviousPageOption = yield this.pfeNavigationService.getPreviousPageOption(currentPageNavConfig);
                nextPreviousPage = nextPreviousPageOption === null || nextPreviousPageOption === void 0 ? void 0 : nextPreviousPageOption.nextPageId;
            }
            else {
                nextPreviousPage = this.pfeNavigationService.getPreviousPageOnHistory(currentPageNavConfig === null || currentPageNavConfig === void 0 ? void 0 : currentPageNavConfig.omitFromHistory);
            }
            return nextPreviousPage;
        });
    }
    preventNavigation(currentState) {
        // There is an angular bug that requires the workaround to push the current url into the browser history in that case
        // References:
        // https://github.com/angular/angular/issues/13586
        // https://github.com/angular/angular/pull/18135
        // https://github.com/angular/angular/issues/15664
        // Push the current page into the history:
        this.location.go(currentState.url);
        return false;
    }
    runOnNavigationStartActions(nextState) {
        return __awaiter(this, void 0, void 0, function* () {
            this.setNavigationState(nextState);
            const currentPageID = this.pfeNavigationService.currentPageId$.value;
            const currentPageNavConfig = yield this.pfeConfigService.getPageNavigationConfiguration(currentPageID);
            if (currentPageNavConfig) {
                try {
                    if (currentPageNavConfig.onNavigationStartActions) {
                        const actionsResult = yield this.pfeActionsService.executeActions(currentPageNavConfig.onNavigationStartActions);
                        if (!actionsResult) {
                            return false;
                        }
                    }
                }
                catch (error) {
                    // One of the actions failed
                    return false;
                }
            }
            return true;
        });
    }
    /**
     * Set the navigationState within the state.
     * This contains the page that is being navigated to and the
     * one where the navigation is coming from.
     */
    setNavigationState(nextState) {
        this.pfeStateService.storeValueByExpression(PFE_STATE_NAVIGATING_FROM, this.pfeNavigationService.currentPageId$.value);
        // The RouterStateSnapshot contains the router state starting from the root/top level
        // The top level gives us the reference to the active children and with that the whole tree.
        // We need to search the children, until we find the pfe routing page
        const nextPageID = getPageIDFromRouteSnapshot(nextState.root);
        if (nextPageID) {
            this.pfeStateService.storeValueByExpression(PFE_STATE_NAVIGATING_TO, nextPageID);
        }
    }
    /**
     * If needed, it would be possible to expose these "events" in a similar fashion like the Angular Router
     */
    /**
     * Handles everything related to the ending of a navigation.
     * If the navigation was not successful (canceled in the last step), the
     * pageStatus$ and error message are not reset, as it is likely tha the user tries it again.
     */
    handleNavigationEnd(success) {
        return __awaiter(this, void 0, void 0, function* () {
            if (success) {
                // Reset the page status after the navigation, but before the page itself becomes active:
                this.pfeNavigationService.pageStatus$.next(false);
            }
            this.pfeNavigationService.navigationInProgress$.next(false);
        });
    }
    /**
     * Handles a cancellation of the navigation at any point.
     */
    handleNavigationStartCancel() {
        this.pfeNavigationService.navigationInProgress$.next(false);
    }
    /**
     * Handles the start of a new navigation
     */
    handleNavigationStartBegin() {
        this.pfeNavigationService.navigationInProgress$.next(true);
    }
    /**
     * Runs after the handleNavigationStartBegin and the navigation handling in the guard
     */
    handleNavigationStartSuccess() {
        return __awaiter(this, void 0, void 0, function* () {
            this.pfeStateService.storeValue(yield this.pfeConfigService.getErrorMessageKey(), undefined);
        });
    }
}
PFEPageRoutingEventsHandlerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PFEPageRoutingEventsHandlerService, deps: [{ token: NGX_PFE_CONFIGURATION }, { token: i1$1.Router }, { token: PfeServiceActivatorService }, { token: PfeConfigurationService }, { token: PfeNavigationService }, { token: PfeNavigationUtilService }, { token: i8.Location }, { token: PfeStateService }, { token: PfeActionsService }, { token: i3.NgxLoggerService }], target: i0.ɵɵFactoryTarget.Injectable });
PFEPageRoutingEventsHandlerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PFEPageRoutingEventsHandlerService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PFEPageRoutingEventsHandlerService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [NGX_PFE_CONFIGURATION]
                    }] }, { type: i1$1.Router }, { type: PfeServiceActivatorService }, { type: PfeConfigurationService }, { type: PfeNavigationService }, { type: PfeNavigationUtilService }, { type: i8.Location }, { type: PfeStateService }, { type: PfeActionsService }, { type: i3.NgxLoggerService }];
    } });

/**
 * Takes a path segment and extracts the query parameters from it.
 * Returns the stripped path as well as the extracted query parameters.
 * Paths that don't contain any query parameters will be left unchanged.
 *
 * @param path the path to extract the query parameters from
 */
function extractQueryParams(path) {
    const searchStart = path.indexOf('?');
    if (searchStart < 0) {
        return { path, queryParams: {} };
    }
    const strippedPath = path.substr(0, searchStart);
    const params = {};
    const queryParams = new URLSearchParams(path.substr(searchStart));
    queryParams.forEach((value, key) => (params[key] = value));
    return { path: strippedPath, queryParams: params };
}

const PFE_GUARDS_CONFIG = new InjectionToken('PFE_GUARDS_CONFIG');

/**
 * Guards manager class to handle the management of guards
 */
class PfeRouteGuardsService {
    constructor(injector, logger) {
        this.injector = injector;
        this.logger = logger;
        this.FUNCTION_KEY = 'function';
        /**
         * Methods names that belong to theses interfaces:
         * https://angular.io/api/router/CanActivate
         * https://angular.io/api/router/CanActivateChild
         * https://angular.io/api/router/CanDeactivate
         * https://angular.io/api/router/CanLoad
         */
        this.ANGULAR_GUARDS_METHODS_KEYS = ['canActivate', 'canActivateChild', 'canDeactivate', 'canLoad'];
    }
    /**
     * Generate valid guards list to be added
     * @param guards list of guards define in the pfe config page
     */
    generateGuards(guards) {
        const noGuardsConfig = !guards || Object.values(guards).every((option) => !(option === null || option === void 0 ? void 0 : option.length));
        if (noGuardsConfig) {
            return undefined;
        }
        let guardsObject;
        try {
            guardsObject = this.injector.get(PFE_GUARDS_CONFIG);
        }
        catch (err) {
            this.logger.error(`There are no guards defined in the PFE_GUARDS_CONFIG provider`);
            return undefined;
        }
        // Filter valid guards to be added
        return this.mapGuards(guards, guardsObject);
    }
    /**
     * For each item (arrays - canLoad, canActivate, etc) retrieve the ones that implement
     * the interfaces and are define in the providers of the app.module
     * @param guards
     * @param guardsObject
     */
    mapGuards(guards, guardsObject) {
        if (!guards || !guardsObject) {
            return undefined;
        }
        const guardOptions = {};
        Object.keys(guards).forEach((guardKey) => {
            // Iterate through the define elements in GuardOptions for this page
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const elements = guards[guardKey].map((key) => guardsObject[key])
                // Get the class define in provider for an specific key
                .filter((value) => this.validateInjectable(value) && this.validateImplementation(guardKey, value));
            guardOptions[guardKey] = elements && elements.length > 0 && elements; // Add valid guards
        });
        return guardOptions;
    }
    /**
     * Check if the injectable that defines a guard is add to the module as a provider
     * @param injectable
     */
    validateInjectable(injectable) {
        try {
            this.injector.get(injectable);
        }
        catch (err) {
            return false;
        }
        return true;
    }
    /**
     * Validate if the class implement some of the guards interfaces
     * @param key - canLoad, canActivate...
     * @param injectable
     */
    validateImplementation(key, injectable) {
        if (!key || !injectable) {
            return false;
        }
        let findFunction = false;
        const value = this.injector.get(injectable);
        for (let i = 0; i < this.ANGULAR_GUARDS_METHODS_KEYS.length; i++) {
            if (typeof value[this.ANGULAR_GUARDS_METHODS_KEYS[i]] === this.FUNCTION_KEY) {
                // check if method is implemented
                findFunction = true;
                // check if the list key is the same that the method implemented
                if (key.toUpperCase() === this.ANGULAR_GUARDS_METHODS_KEYS[i].toUpperCase()) {
                    return true;
                }
            }
        }
        const error = !findFunction
            ? `${injectable.name} is not implementing one of the next methods ${this.ANGULAR_GUARDS_METHODS_KEYS.toString()}`
            : `${injectable.name} is part of the list: ${key}, but this is not the right one`;
        this.logger.error(error);
        return false;
    }
}
PfeRouteGuardsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeRouteGuardsService, deps: [{ token: i0.Injector }, { token: i3.NgxLoggerService }], target: i0.ɵɵFactoryTarget.Injectable });
PfeRouteGuardsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeRouteGuardsService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeRouteGuardsService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i0.Injector }, { type: i3.NgxLoggerService }]; } });

/**
 * A PageDefinition contains the component for a page.
 *
 * @export
 */
class PageDefinition {
    constructor(pageComponent) {
        if (pageComponent) {
            this.pageComponent = pageComponent;
        }
    }
}

/**
 * The PfePageMappingService is responsible to map the configuration to a page component.
 *
 * @export
 */
class PfePageMappingService {
    constructor(ngxPfeModuleConfiguration) {
        this.ngxPfeModuleConfiguration = ngxPfeModuleConfiguration;
        this.pages = this.ngxPfeModuleConfiguration.pageMapping;
    }
    /**
     * Helper to add a page component to the mapping.
     *
     * @param  pageType The attribute used in the configuration.
     * @param pageComponent The page component to use.
     */
    static addPageToMap(pages, pageType, pageComponent) {
        pages.set(pageType, new PageDefinition(pageComponent));
    }
    /**
     * Helper to add a lazy loaded page component to the mapping.
     * Check the documentation, on how to create such a page.
     *
     * @param  pageType The attribute used in the configuration.
     * @param lazyPageRoute The lazy loaded module.
     */
    static addLazyLoadedPageToMap(pages, pageType, lazyPageRoute) {
        const pageDef = new PageDefinition();
        pageDef.lazyPageRoute = lazyPageRoute;
        pages.set(pageType, pageDef);
    }
    /**
     * Determines the page type of a page configuration by its page configuration attribute.
     *
     * @param config The page config.
     * @returns The determined page type.
     */
    getPageDefinition(config) {
        for (const key in config) {
            if (Object.prototype.hasOwnProperty.call(config, key)) {
                const pageDef = this.pages.get(key);
                if (pageDef) {
                    return pageDef;
                }
            }
        }
    }
}
PfePageMappingService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfePageMappingService, deps: [{ token: NGX_PFE_CONFIGURATION }], target: i0.ɵɵFactoryTarget.Injectable });
PfePageMappingService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfePageMappingService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfePageMappingService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [NGX_PFE_CONFIGURATION]
                    }] }];
    } });

class PFERouteGeneratorService {
    constructor(router, pfeRouteGuardsService, pfePageMappingService, pfeConfigService, pfeNavigationService, pfeNavigationUtilService, pfeBusinessService, logger) {
        this.router = router;
        this.pfeRouteGuardsService = pfeRouteGuardsService;
        this.pfePageMappingService = pfePageMappingService;
        this.pfeConfigService = pfeConfigService;
        this.pfeNavigationService = pfeNavigationService;
        this.pfeNavigationUtilService = pfeNavigationUtilService;
        this.pfeBusinessService = pfeBusinessService;
        this.logger = logger;
    }
    canActivate(activatedRoute, state) {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            if ((_a = activatedRoute === null || activatedRoute === void 0 ? void 0 : activatedRoute.routeConfig) === null || _a === void 0 ? void 0 : _a.children) {
                // Navigate to first child:
                // We can be sure that the structure below the activatedRoute looks like this, as we created it ourselves in the generatePFERoutingConfig()
                if (((_d = (_c = (_b = activatedRoute === null || activatedRoute === void 0 ? void 0 : activatedRoute.children) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.children) === null || _d === void 0 ? void 0 : _d.length) === 0) {
                    const firstPageOption = yield this.pfeNavigationService.getFirstPageOrErrorPageOption();
                    if (this.pfeNavigationUtilService.navigateExternal(firstPageOption)) {
                    }
                    else if (firstPageOption === null || firstPageOption === void 0 ? void 0 : firstPageOption.nextPageId) {
                        this.router.navigate([state.url, firstPageOption.nextPageId], { replaceUrl: true });
                    }
                    return false;
                }
                return true;
            }
            let config;
            try {
                config = yield this.pfeConfigService.getConfig();
            }
            catch (error) {
                // Loading the config failed. Child routes cannot be generated. Master Page will trigger an error state to show an error message
                return true;
            }
            const pfeChildRoutes = this.generatePFERoutingConfig(config);
            if ((_e = activatedRoute === null || activatedRoute === void 0 ? void 0 : activatedRoute.parent) === null || _e === void 0 ? void 0 : _e.routeConfig) {
                activatedRoute.parent.routeConfig.children = pfeChildRoutes;
                activatedRoute.parent.routeConfig.canActivate = [PFERouteGeneratorService];
            }
            // Try to restore the state from server:
            let lastVisitedPage;
            try {
                yield this.pfeBusinessService.restoreState();
                lastVisitedPage = this.pfeBusinessService.getFullState()[LAST_VISITED_PAGE_STATE_KEY];
            }
            catch (error) {
                const errorPageID = yield this.pfeNavigationUtilService.getErrorPage(this.isHttpErrorResponse(error) ? error : undefined);
                if (errorPageID) {
                    this.navigateToPageID(state, activatedRoute, errorPageID);
                }
                else {
                    throw error;
                }
                return false;
            }
            // default is to navigate to the last page.
            let navigateToLastPage = true;
            if (activatedRoute.url[0] && activatedRoute.url[0].path) {
                const navConfig = yield this.pfeConfigService.getPageNavigationConfiguration(activatedRoute.url[0].path);
                navigateToLastPage =
                    navConfig !== undefined && navConfig.navigateToLastVisitedPage !== undefined ? navConfig.navigateToLastVisitedPage : true;
            }
            if (lastVisitedPage) {
                // We have a restored state:
                if (navigateToLastPage) {
                    this.navigateToPageID(state, activatedRoute, lastVisitedPage);
                }
                else {
                    // Retrigger navigation with current child:
                    this.navigateWithURL(state.url);
                }
            }
            else if ((activatedRoute.url.length > 0 && (yield this.getAllowGlobalDirectNavigation())) ||
                (yield this.getAllowDirectNavigationForPage(activatedRoute))) {
                // Directly navigated to child
                const pageID = activatedRoute.url[0].path;
                const pageConfig = yield this.pfeConfigService.getPageConfiguration(pageID);
                if (!pageConfig) {
                    // We don't have a config for that pageID, redirect to first page:
                    const firstPageOption = yield this.pfeNavigationService.getFirstPageOrErrorPageOption();
                    if (this.pfeNavigationUtilService.navigateExternal(firstPageOption)) {
                        return false;
                    }
                    else if (firstPageOption === null || firstPageOption === void 0 ? void 0 : firstPageOption.nextPageId) {
                        this.navigateToChildOnSameLevel(state, activatedRoute, firstPageOption.nextPageId);
                    }
                }
                else {
                    // Retrigger navigation with current child:
                    this.navigateWithURL(state.url);
                }
            }
            else {
                // Navigate to first child:
                const firstPageOption = yield this.pfeNavigationService.getFirstPageOrErrorPageOption();
                if (this.pfeNavigationUtilService.navigateExternal(firstPageOption)) {
                    return false;
                }
                else if (firstPageOption === null || firstPageOption === void 0 ? void 0 : firstPageOption.nextPageId) {
                    this.navigateToPageID(state, activatedRoute, firstPageOption.nextPageId);
                }
            }
            return false;
        });
    }
    navigateToPageID(state, activatedRoute, pageID) {
        if (activatedRoute.url.length > 0) {
            this.navigateToChildOnSameLevel(state, activatedRoute, pageID);
        }
        else {
            const { path, queryParams } = extractQueryParams(state.url);
            this.router.navigate([path, pageID], { replaceUrl: true, queryParams });
        }
    }
    navigateToChildOnSameLevel(state, activatedRoute, pageID) {
        const parentUrl = state.url.slice(0, state.url.indexOf(activatedRoute.url[activatedRoute.url.length - 1].path));
        this.router.navigate([parentUrl, pageID], { replaceUrl: true });
    }
    /**
     * Triggers a navigation, with a URL. Can handle query parameters correctly.
     *
     * We need to pay special attention to query parameters so we can deal with deep-linked
     * entry URLs. We can't simply use these URLs to navigate further because they might lead
     * to unidentifiable routes.
     * E.g. the router won't know how to deal with `my-page?key=value`. It's not a known route
     */
    navigateWithURL(url) {
        const { path, queryParams } = extractQueryParams(url);
        this.router.navigate([path], { replaceUrl: true, queryParams });
    }
    getAllowGlobalDirectNavigation() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const appConfig = yield this.pfeConfigService.getAppConfiguration();
            return (_a = appConfig === null || appConfig === void 0 ? void 0 : appConfig.pfeConfig) === null || _a === void 0 ? void 0 : _a.allowDirectNavigationWithoutState;
        });
    }
    /**
     * Determines if it is allowed to directly navigate to this specific navigation config.
     */
    getAllowDirectNavigationForPage(activatedRoute) {
        return __awaiter(this, void 0, void 0, function* () {
            if (activatedRoute.url.length > 0) {
                // Directly navigated to child
                const pageID = activatedRoute.url[0].path;
                if (pageID) {
                    const pageNavConfig = yield this.pfeConfigService.getPageNavigationConfiguration(pageID);
                    if (pageNavConfig && pageNavConfig.allowDirectNavigationWithoutState) {
                        return true;
                    }
                }
            }
            return false;
        });
    }
    generatePFERoutingConfig(pfeConfig) {
        const routerConfig = [];
        if (pfeConfig.pagesConfiguration) {
            pfeConfig.pagesConfiguration.forEach((page) => {
                var _a, _b;
                const pageDefinition = this.pfePageMappingService.getPageDefinition(page);
                if (pageDefinition) {
                    /**
                     * The actual page route is added as a child to an empty parent route.
                     * The background for this is, that Angular runs route guards of every "layer" in parallel.
                     * That means, a configured route guard and the pfe route guard (PFEPageRoutingEventsHandlerService)
                     * would run in parallel.
                     *
                     * This is not desired, as the pfe route guard could trigger service activators, etc...
                     *
                     * The solution is, to setup the configured route guards in a sequence that they run first.
                     * This ensures, that they finish before the pfe guard. The pfe guard
                     * will also not be executed when the parent returns a false result.
                     *
                     * See also: https://angular.io/guide/router-tutorial-toh#milestone-5-route-guards
                     *
                     * Execution order:
                     * - From the deepest child towards the top: CanDeactivate, CanActivateChild
                     * - From the top towards the children: CanActivate
                     * - On module layer: CanLoad
                     */
                    const guards = this.pfeRouteGuardsService.generateGuards(page.guards);
                    const route = {
                        path: '',
                        canActivate: [...((guards === null || guards === void 0 ? void 0 : guards.canActivate) || [])],
                        canDeactivate: [PFEPageRoutingEventsHandlerService],
                        children: [],
                    };
                    const childRoute = {
                        path: page.pageId,
                        canActivate: [PFEPageRoutingEventsHandlerService],
                        canActivateChild: [...((guards === null || guards === void 0 ? void 0 : guards.canActivateChild) || [])],
                        canDeactivate: [...((guards === null || guards === void 0 ? void 0 : guards.canDeactivate) || [])],
                        canLoad: [...((guards === null || guards === void 0 ? void 0 : guards.canLoad) || [])],
                        data: {
                            pfePage: true,
                        },
                        resolve: {
                            pageConfig: PFEPageConfigResolver,
                        },
                    };
                    if (pageDefinition.pageComponent) {
                        childRoute.component = pageDefinition.pageComponent;
                    }
                    else {
                        childRoute.loadChildren = (_a = pageDefinition === null || pageDefinition === void 0 ? void 0 : pageDefinition.lazyPageRoute) === null || _a === void 0 ? void 0 : _a.loadChildren;
                    }
                    (_b = route.children) === null || _b === void 0 ? void 0 : _b.push(childRoute);
                    routerConfig.push(route);
                }
                else {
                    this.logger.errorToServer(`generatePFERoutingConfig: PageType used in configuration not found: `, page);
                }
            });
        }
        return routerConfig;
    }
    isHttpErrorResponse(error) {
        return error instanceof HttpErrorResponse;
    }
}
PFERouteGeneratorService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PFERouteGeneratorService, deps: [{ token: i1$1.Router }, { token: PfeRouteGuardsService }, { token: PfePageMappingService }, { token: PfeConfigurationService }, { token: PfeNavigationService }, { token: PfeNavigationUtilService }, { token: PfeBusinessService }, { token: i3.NgxLoggerService }], target: i0.ɵɵFactoryTarget.Injectable });
PFERouteGeneratorService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PFERouteGeneratorService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PFERouteGeneratorService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.Router }, { type: PfeRouteGuardsService }, { type: PfePageMappingService }, { type: PfeConfigurationService }, { type: PfeNavigationService }, { type: PfeNavigationUtilService }, { type: PfeBusinessService }, { type: i3.NgxLoggerService }]; } });

const routes = [
    {
        path: '',
        data: {
            pfeParent: true,
        },
        children: [
            {
                // Needed to make the angular compiler happy
                component: PfeEmptyRoutingComponent,
                path: '**',
                canActivate: [PFERouteGeneratorService],
            },
        ],
    },
];
class NgxPfeRoutingModule {
}
NgxPfeRoutingModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: NgxPfeRoutingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
NgxPfeRoutingModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: NgxPfeRoutingModule, declarations: [PfeEmptyRoutingComponent], imports: [i1$1.RouterModule], exports: [RouterModule] });
NgxPfeRoutingModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: NgxPfeRoutingModule, imports: [[RouterModule.forChild(routes)], RouterModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: NgxPfeRoutingModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [PfeEmptyRoutingComponent],
                    imports: [RouterModule.forChild(routes)],
                    exports: [RouterModule],
                }]
        }] });

class NgxPfeUtilModule {
}
NgxPfeUtilModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: NgxPfeUtilModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
NgxPfeUtilModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: NgxPfeUtilModule, imports: [CommonModule] });
NgxPfeUtilModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: NgxPfeUtilModule, providers: [PfeUtilService], imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: NgxPfeUtilModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    providers: [PfeUtilService],
                }]
        }] });

class PfeServiceActivatorModule {
}
PfeServiceActivatorModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeServiceActivatorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
PfeServiceActivatorModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeServiceActivatorModule, imports: [CommonModule, NgxPfeUtilModule] });
PfeServiceActivatorModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeServiceActivatorModule, providers: [PfeServiceActivatorService], imports: [[CommonModule, NgxPfeUtilModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeServiceActivatorModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, NgxPfeUtilModule],
                    declarations: [],
                    providers: [PfeServiceActivatorService],
                }]
        }] });

// TODO: Create wrapper module/component that encapsulates everything (routing/template):
const defaultProviders = [
    PFERouteGeneratorService,
    PfeRouteGuardsService,
    PFEPageConfigResolver,
    PFEPageRoutingEventsHandlerService,
    PfeBusinessService,
    PfeNavigationService,
    PfeStateService,
    PfePageMappingService,
    PfeRestService,
    [{ provide: PfeConfigurationService, useClass: PfeConfigurationService }],
    PfeNavigationUtilService,
];
/**
 * @deprecated: Shocked to read that the NgxPfeModule is deprecated? Worry not!
 * This is only about changes to the routing integration of the PFE. Please replace the NgxPfeModule import
 * in the routing setup with the NgxPfeRoutingModule. This will turn into a breaking change with the next major release.
 * See https://github.developer.allianz.io/gcj/ngx-pfe/issues/729 for more information.
 */
class NgxPfeModule {
    /**
     * @param ngxPfeModuleConfigurationProvider Provider, that provides the NGX_PFE_CONFIGURATION injection token.
     */
    static forRoot(ngxPfeModuleConfigurationProvider) {
        return {
            ngModule: NgxPfeModule,
            providers: [...defaultProviders, ngxPfeModuleConfigurationProvider],
        };
    }
}
NgxPfeModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: NgxPfeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
NgxPfeModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: NgxPfeModule, imports: [CommonModule,
        FormsModule,
        ReactiveFormsModule,
        // TODO: Deprecated: This will be removed with the next major release.
        NgxPfeRoutingModule,
        NgxPfeUtilModule,
        PfeServiceActivatorModule,
        PfeActionsModule,
        PfeTranslateModule] });
NgxPfeModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: NgxPfeModule, imports: [[
            CommonModule,
            FormsModule,
            ReactiveFormsModule,
            // TODO: Deprecated: This will be removed with the next major release.
            NgxPfeRoutingModule,
            NgxPfeUtilModule,
            PfeServiceActivatorModule,
            PfeActionsModule,
            PfeTranslateModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: NgxPfeModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        // TODO: Deprecated: This will be removed with the next major release.
                        NgxPfeRoutingModule,
                        NgxPfeUtilModule,
                        PfeServiceActivatorModule,
                        PfeActionsModule,
                        PfeTranslateModule,
                    ],
                }]
        }] });

class PfeConfigurationServiceMock extends PfeConfigurationService {
    constructor(mockNgxPfeModuleConfiguration, mockHttp, mockPfeUtilService, mockLogger) {
        super(mockNgxPfeModuleConfiguration, mockHttp, mockPfeUtilService, mockLogger, {
            pagesConfiguration: [],
            navConfiguration: {
                errorPageNavigation: [],
                pages: [],
            },
            appConfiguration: {
                pfeConfig: {
                    autoCleanStaleState: true,
                    enableStateCleaning: true,
                },
            },
        });
        this.mockNgxPfeModuleConfiguration = mockNgxPfeModuleConfiguration;
        this.mockHttp = mockHttp;
        this.mockPfeUtilService = mockPfeUtilService;
        this.mockLogger = mockLogger;
        this.pfeApplicationConfiguration = {
            pfeConfig: {
                autoCleanStaleState: true,
                enableStateCleaning: true,
            },
        };
    }
}
PfeConfigurationServiceMock.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeConfigurationServiceMock, deps: [{ token: NGX_PFE_CONFIGURATION }, { token: i1.HttpClient }, { token: PfeUtilService }, { token: i3.NgxLoggerService }], target: i0.ɵɵFactoryTarget.Injectable });
PfeConfigurationServiceMock.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeConfigurationServiceMock });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeConfigurationServiceMock, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [NGX_PFE_CONFIGURATION]
                    }] }, { type: i1.HttpClient }, { type: PfeUtilService }, { type: i3.NgxLoggerService }];
    } });
const ngxPfeModuleMockingConfiguration = {
    pageMapping: new Map(),
    applicationID: 'testing mock',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    config_api_endpoint: '/config/',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    service_activator_endpoint: '/service-activator',
    production: false,
};
class NgxPfeTestingModule {
}
NgxPfeTestingModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: NgxPfeTestingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
NgxPfeTestingModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: NgxPfeTestingModule, imports: [HttpClientTestingModule, NgxPfeModule], exports: [NgxPfeModule] });
NgxPfeTestingModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: NgxPfeTestingModule, providers: [
        {
            provide: PfeConfigurationService,
            useClass: PfeConfigurationServiceMock,
        },
        NgxLoggerService,
    ], imports: [[
            HttpClientTestingModule,
            NgxPfeModule.forRoot({
                provide: NGX_PFE_CONFIGURATION,
                useValue: ngxPfeModuleMockingConfiguration,
            }),
        ], NgxPfeModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: NgxPfeTestingModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        HttpClientTestingModule,
                        NgxPfeModule.forRoot({
                            provide: NGX_PFE_CONFIGURATION,
                            useValue: ngxPfeModuleMockingConfiguration,
                        }),
                    ],
                    declarations: [],
                    exports: [NgxPfeModule],
                    providers: [
                        {
                            provide: PfeConfigurationService,
                            useClass: PfeConfigurationServiceMock,
                        },
                        NgxLoggerService,
                    ],
                }]
        }] });

class MockActivatedRoute {
    constructor(pageConfig) {
        this.snapshot = {
            data: {
                pageConfig: {},
            },
        };
        this.snapshot.data.pageConfig = pageConfig;
        this.data = of(pageConfig);
    }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const PfeNavigateActionType = 'PFE_NAVIGATE';

class PfeNavigateActionService {
    constructor(router, navigationService, pfeStateService) {
        this.router = router;
        this.navigationService = navigationService;
        this.pfeStateService = pfeStateService;
        this.navigate = (actionConfig) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            const currentNavigationPFEExtras = (_a = this.router.getCurrentNavigation()) === null || _a === void 0 ? void 0 : _a.extras;
            if ((_c = (_b = currentNavigationPFEExtras === null || currentNavigationPFEExtras === void 0 ? void 0 : currentNavigationPFEExtras.state) === null || _b === void 0 ? void 0 : _b.pfe) === null || _c === void 0 ? void 0 : _c.navigatingToErrorPage) {
                return true;
            }
            if (!actionConfig.ignoreDestination) {
                const nextPage = this.pfeStateService.getStateValueByExpression(PFE_STATE_NAVIGATING_TO);
                if (nextPage !== actionConfig.pageId) {
                    this.navigationService.navigateToPage(actionConfig.pageId);
                    return false;
                }
                return true;
            }
            else {
                this.navigationService.navigateToPage(actionConfig.pageId);
                return false;
            }
        });
    }
}
PfeNavigateActionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeNavigateActionService, deps: [{ token: i1$1.Router }, { token: PfeBusinessService }, { token: PfeStateService }], target: i0.ɵɵFactoryTarget.Injectable });
PfeNavigateActionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeNavigateActionService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeNavigateActionService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.Router }, { type: PfeBusinessService }, { type: PfeStateService }]; } });

class PfeNavigateActionModule {
    constructor(pfeActionsService, pfeNavigateActionService) {
        this.pfeActionsService = pfeActionsService;
        this.pfeActionsService.registerAction(PfeNavigateActionType, pfeNavigateActionService.navigate);
    }
}
PfeNavigateActionModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeNavigateActionModule, deps: [{ token: PfeActionsService }, { token: PfeNavigateActionService }], target: i0.ɵɵFactoryTarget.NgModule });
PfeNavigateActionModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeNavigateActionModule });
PfeNavigateActionModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeNavigateActionModule, providers: [PfeNavigateActionService] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: PfeNavigateActionModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [PfeNavigateActionService],
                }]
        }], ctorParameters: function () { return [{ type: PfeActionsService }, { type: PfeNavigateActionService }]; } });

// eslint-disable-next-line @typescript-eslint/naming-convention
const PfeNestedActionsType = 'PFE_NESTED_ACTIONS';

var ServiceActivatorHTTPMethods;
(function (ServiceActivatorHTTPMethods) {
    ServiceActivatorHTTPMethods["GET"] = "GET";
    ServiceActivatorHTTPMethods["POST"] = "POST";
    ServiceActivatorHTTPMethods["PUT"] = "PUT";
    ServiceActivatorHTTPMethods["DELETE"] = "DELETE";
})(ServiceActivatorHTTPMethods || (ServiceActivatorHTTPMethods = {}));

/**
 * Generated bundle index. Do not edit.
 */

export { APP_BASE_HREF_HASH_ONLY, ARRAY_STATE_EXPRESSION, HashOnlyLocationStrategy, LAST_VISITED_PAGE_STATE_KEY, META_KEYS, MockActivatedRoute, NGX_PFE_CONFIGURATION, NGX_PFE_CONFIG_API_FORMAT, NGX_PFE_FLOW_CONFIGURATION, NGX_PFE_INITIAL_STATE, NGX_PFE_STATE_SESSION_STORAGE_KEY_CONFIG, NgxPfeModule, NgxPfeRoutingModule, NgxPfeTestingModule, NgxPfeUtilModule, OPERATORS, PAGE_CONFIG_ROUTE_ATTRIBUTE_NAME, PFETranslateService, PFE_DEFAULT_REQUEST_BODY, PFE_GUARDS_CONFIG, PageDefinition, PfeActionsService, PfeBusinessService, PfeConfigurationService, PfeConfigurationServiceMock, PfeHeaderInterceptor, PfeMasterPageComponent, PfeMasterPageModule, PfeNavigateActionModule, PfeNavigateActionService, PfeNavigateActionType, PfeNavigationService, PfeNavigationUtilService, PfeNestedActionsService, PfeNestedActionsType, PfePageMappingService, PfeRewindHistory, PfeRulesEvaluator, PfeServiceActivatorService, PfeStateService, PfeTranslateModule, PfeTranslatePipe, PfeTriggerServiceActivatorsService, PfeTriggerServiceActivatorsType, PfeUpdateStateOnBackend, PfeUpdateStateValuesService, PfeUpdateStateValuesType, PfeUrlParameters, PfeUtilService, STATE_EXPRESSION, ServiceActivatorHTTPMethods, ServiceActivatorHook, StateOperations, URL_STATE_REGEX, VALIDATION_ERROR_STATE_KEY_FALLBACK, fadeIn, ngxPfeModuleMockingConfiguration };
